import * as THREE from 'three';
import { Box3, Ray, Plane, MathUtils, Vector3, Controls, MOUSE, TOUCH, Quaternion, Spherical, Vector2, PerspectiveCamera, TextureLoader, Color, Texture, PMREMGenerator, HalfFloatType, Mesh, WebGLRenderer, SRGBColorSpace, PCFSoftShadowMap, Box2, BufferGeometry, Object3D, LinearToneMapping, Scene, AmbientLight, DirectionalLight, Sphere, Matrix4, Clock, AxesHelper, MeshStandardMaterial, Box3Helper, ArrowHelper, SphereGeometry, MeshBasicMaterial, Euler, ShaderMaterial, MeshPhysicalMaterial, TorusKnotGeometry, RepeatWrapping, FrontSide, BackSide, DoubleSide, ACESFilmicToneMapping, Group, BoxGeometry, Float32BufferAttribute, LineSegments, LineBasicMaterial, LineDashedMaterial, Material, Raycaster, GridHelper, PlaneGeometry } from 'three';
import { USDZExporter } from 'three/examples/jsm/exporters/USDZExporter';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { assertUnreachable } from '@canvas-logic/clp-utils';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { GLTFLoader as GLTFLoader$1 } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader as DRACOLoader$1 } from 'three/examples/jsm/loaders/DRACOLoader';
import { CSS2DRenderer, CSS2DObject as CSS2DObject$1 } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { formatDimension } from '@canvas-logic/clp-shared-ui';
import { jointPointToMatrix, calculateJointTransform, MaterialMaps, isConditionActive, VariantProperties, getAttributeValue, defaultPosition, defaultRotation, defaultJP, ralToHex, PartsRegistry, traverseForest, findPartNodes, defaultTransform, StandardAttributes, createNodes } from '@canvas-logic/clp-domain';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls';
import { RGBELoader as RGBELoader$1 } from 'three/examples/jsm/loaders/RGBELoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import RAPIER from '@dimforge/rapier3d-compat';
import { ViewHelper as ViewHelper$1 } from 'three/examples/jsm/helpers/ViewHelper';
import { CSG } from 'three-csg-ts';

const DEFAULT_NODE_SETTINGS = {
    takePlace: true,
    webAr: { visible: true },
    slotItem: null,
};
function deepMerge(target, source) {
    const result = {};
    const src = source;
    for (const key of Object.keys(target)) {
        const targetValue = target[key];
        if (typeof targetValue === 'object' && targetValue !== null && !Array.isArray(targetValue)) {
            result[key] = deepMerge(targetValue, {});
        }
        else {
            result[key] = targetValue;
        }
    }
    for (const key of Object.keys(src)) {
        const sourceValue = src[key];
        const targetValue = target[key];
        if (sourceValue === undefined) {
            continue;
        }
        if (typeof sourceValue === 'object' &&
            sourceValue !== null &&
            !Array.isArray(sourceValue) &&
            typeof targetValue === 'object' &&
            targetValue !== null) {
            result[key] = deepMerge(targetValue, sourceValue);
        }
        else {
            result[key] = sourceValue;
        }
    }
    return result;
}
function resolveNodeSettings(settings) {
    return deepMerge(DEFAULT_NODE_SETTINGS, settings !== null && settings !== void 0 ? settings : {});
}

function hasUserData(node) {
    return Object.keys(node.userData).length > 0;
}
function getUserData(node) {
    return resolveNodeSettings(node.userData);
}
function setUserData(node, data) {
    node.userData = resolveNodeSettings(data);
}
function changeUserData(node, data) {
    node.userData = resolveNodeSettings(Object.assign(Object.assign({}, node.userData), data));
}

function removeNonMeasurableObjects(root) {
    const measurableObject = root;
    const toDelete = [];
    measurableObject.traverse((node) => {
        const takePlace = getUserData(node).takePlace;
        if (hasUserData(node) && !takePlace) {
            toDelete.push(node);
        }
    });
    for (const node of toDelete) {
        node.removeFromParent();
    }
    return measurableObject;
}
function getMeasurableObjects(root) {
    const measurableObject = root.clone(true);
    return removeNonMeasurableObjects(measurableObject);
}

function computeAABB(root) {
    const box = new Box3().setFromObject(getMeasurableObjects(root));
    return {
        min: { x: box.min.x, y: box.min.y, z: box.min.z },
        max: { x: box.max.x, y: box.max.y, z: box.max.z },
    };
}
function computeModelBounds(root) {
    return { whole: computeAABB(root) };
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class UsdzExporter {
    constructor() {
        this.exporter = new USDZExporter();
    }
    export(scene) {
        return __awaiter(this, void 0, void 0, function* () {
            const exporter = new USDZExporter();
            const arraybuffer = yield exporter.parseAsync(scene);
            return new Blob([arraybuffer], { type: 'application/octet-stream' });
        });
    }
}

class GltfExporter {
    constructor() {
        this.exporter = new GLTFExporter();
    }
    export(scene) {
        return new Promise((resolve, reject) => {
            this.exporter.parse(scene, (gltf) => {
                resolve(gltf);
            }, reject, { forceIndices: true, binary: true });
        });
    }
}

function deviceDependentConfigurationTypeGuard(configuration) {
    return (typeof configuration === 'object' && !!configuration && 'mobile' in configuration && 'desktop' in configuration);
}
function getAdaptiveConfigurationValue(configuration) {
    if (deviceDependentConfigurationTypeGuard(configuration)) {
        return configuration[getDeviceType()];
    }
    else {
        return configuration;
    }
}
function getDeviceType() {
    const query = window.matchMedia('only screen and (max-width: 600px)');
    const isMobile = query && query.matches;
    return isMobile ? 'mobile' : 'desktop';
}

/**
 * Fires when the camera has been transformed by the controls.
 *
 * @event OrbitControls#change
 * @type {Object}
 */
const _changeEvent = { type: 'change' };

/**
 * Fires when an interaction was initiated.
 *
 * @event OrbitControls#start
 * @type {Object}
 */
const _startEvent = { type: 'start' };

/**
 * Fires when an interaction has finished.
 *
 * @event OrbitControls#end
 * @type {Object}
 */
const _endEvent = { type: 'end' };

const _ray = new Ray();
const _plane = new Plane();
const _TILT_LIMIT = Math.cos(70 * MathUtils.DEG2RAD);

const _v = new Vector3();
const _twoPI = 2 * Math.PI;

const _STATE = {
  NONE: -1,
  ROTATE: 0,
  DOLLY: 1,
  PAN: 2,
  TOUCH_ROTATE: 3,
  TOUCH_PAN: 4,
  TOUCH_DOLLY_PAN: 5,
  TOUCH_DOLLY_ROTATE: 6,
};
const _EPS = 0.000001;

/**
 * Orbit controls allow the camera to orbit around a target.
 *
 * OrbitControls performs orbiting, dollying (zooming), and panning. Unlike {@link TrackballControls},
 * it maintains the "up" direction `object.up` (+Y by default).
 *
 * - Orbit: Left mouse / touch: one-finger move.
 * - Zoom: Middle mouse, or mousewheel / touch: two-finger spread or squish.
 * - Pan: Right mouse, or left mouse + ctrl/meta/shiftKey, or arrow keys / touch: two-finger move.
 *
 * ```js
 * const controls = new OrbitControls( camera, renderer.domElement );
 *
 * // controls.update() must be called after any manual changes to the camera's transform
 * camera.position.set( 0, 20, 100 );
 * controls.update();
 *
 * function animate() {
 *
 * 	// required if controls.enableDamping or controls.autoRotate are set to true
 * 	controls.update();
 *
 * 	renderer.render( scene, camera );
 *
 * }
 * ```
 *
 * @augments Controls
 * @three_import import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
 */
class OrbitControls extends Controls {
  /**
   * Constructs a new controls instance.
   *
   * @param {Object3D} object - The object that is managed by the controls.
   * @param {?HTMLDOMElement} domElement - The HTML element used for event listeners.
   */
  constructor(object, domElement = null) {
    super(object, domElement);

    this.state = _STATE.NONE;

    this.autoZoomDisable = false;

    /**
     * The focus point of the controls, the `object` orbits around this.
     * It can be updated manually at any point to change the focus of the controls.
     *
     * @type {Vector3}
     */
    this.target = new Vector3();

    /**
     * The focus point of the `minTargetRadius` and `maxTargetRadius` limits.
     * It can be updated manually at any point to change the center of interest
     * for the `target`.
     *
     * @type {Vector3}
     */
    this.cursor = new Vector3();

    /**
     * How far you can dolly in (perspective camera only).
     *
     * @type {number}
     * @default 0
     */
    this.minDistance = 0;

    /**
     * How far you can dolly out (perspective camera only).
     *
     * @type {number}
     * @default Infinity
     */
    this.maxDistance = Infinity;

    /**
     * How far you can zoom in (orthographic camera only).
     *
     * @type {number}
     * @default 0
     */
    this.minZoom = 0;

    /**
     * How far you can zoom out (orthographic camera only).
     *
     * @type {number}
     * @default Infinity
     */
    this.maxZoom = Infinity;

    /**
     * How close you can get the target to the 3D `cursor`.
     *
     * @type {number}
     * @default 0
     */
    this.minTargetRadius = 0;

    /**
     * How far you can move the target from the 3D `cursor`.
     *
     * @type {number}
     * @default Infinity
     */
    this.maxTargetRadius = Infinity;

    /**
     * How far you can orbit vertically, lower limit. Range is `[0, Math.PI]` radians.
     *
     * @type {number}
     * @default 0
     */
    this.minPolarAngle = 0;

    /**
     * How far you can orbit vertically, upper limit. Range is `[0, Math.PI]` radians.
     *
     * @type {number}
     * @default Math.PI
     */
    this.maxPolarAngle = Math.PI;

    /**
     * How far you can orbit horizontally, lower limit. If set, the interval `[ min, max ]`
     * must be a sub-interval of `[ - 2 PI, 2 PI ]`, with `( max - min < 2 PI )`.
     *
     * @type {number}
     * @default -Infinity
     */
    this.minAzimuthAngle = -Infinity;

    /**
     * How far you can orbit horizontally, upper limit. If set, the interval `[ min, max ]`
     * must be a sub-interval of `[ - 2 PI, 2 PI ]`, with `( max - min < 2 PI )`.
     *
     * @type {number}
     * @default -Infinity
     */
    this.maxAzimuthAngle = Infinity;

    /**
     * Set to `true` to enable damping (inertia), which can be used to give a sense of weight
     * to the controls. Note that if this is enabled, you must call `update()` in your animation
     * loop.
     *
     * @type {boolean}
     * @default false
     */
    this.enableDamping = false;

    /**
     * The damping inertia used if `enableDamping` is set to `true`.
     *
     * Note that for this to work, you must call `update()` in your animation loop.
     *
     * @type {number}
     * @default 0.05
     */
    this.dampingFactor = 0.05;

    /**
     * Enable or disable zooming (dollying) of the camera.
     *
     * @type {boolean}
     * @default true
     */
    this.enableZoom = true;

    /**
     * Speed of zooming / dollying.
     *
     * @type {number}
     * @default 1
     */
    this.zoomSpeed = 1.0;

    /**
     * Enable or disable horizontal and vertical rotation of the camera.
     *
     * Note that it is possible to disable a single axis by setting the min and max of the
     * `minPolarAngle` or `minAzimuthAngle` to the same value, which will cause the vertical
     * or horizontal rotation to be fixed at that value.
     *
     * @type {boolean}
     * @default true
     */
    this.enableRotate = true;

    /**
     * Speed of rotation.
     *
     * @type {number}
     * @default 1
     */
    this.rotateSpeed = 1.0;

    /**
     * How fast to rotate the camera when the keyboard is used.
     *
     * @type {number}
     * @default 1
     */
    this.keyRotateSpeed = 1.0;

    /**
     * Enable or disable camera panning.
     *
     * @type {boolean}
     * @default true
     */
    this.enablePan = true;

    /**
     * Speed of panning.
     *
     * @type {number}
     * @default 1
     */
    this.panSpeed = 1.0;

    /**
     * Defines how the camera's position is translated when panning. If `true`, the camera pans
     * in screen space. Otherwise, the camera pans in the plane orthogonal to the camera's up
     * direction.
     *
     * @type {boolean}
     * @default true
     */
    this.screenSpacePanning = true;

    /**
     * How fast to pan the camera when the keyboard is used in
     * pixels per keypress.
     *
     * @type {number}
     * @default 7
     */
    this.keyPanSpeed = 7.0;

    /**
     * Setting this property to `true` allows to zoom to the cursor's position.
     *
     * @type {boolean}
     * @default false
     */
    this.zoomToCursor = false;

    /**
     * Set to true to automatically rotate around the target
     *
     * Note that if this is enabled, you must call `update()` in your animation loop.
     * If you want the auto-rotate speed to be independent of the frame rate (the refresh
     * rate of the display), you must pass the time `deltaTime`, in seconds, to `update()`.
     *
     * @type {boolean}
     * @default false
     */
    this.autoRotate = false;

    /**
     * How fast to rotate around the target if `autoRotate` is `true`. The default  equates to 30 seconds
     * per orbit at 60fps.
     *
     * Note that if `autoRotate` is enabled, you must call `update()` in your animation loop.
     *
     * @type {number}
     * @default 2
     */
    this.autoRotateSpeed = 2.0;

    /**
     * This object contains references to the keycodes for controlling camera panning.
     *
     * ```js
     * controls.keys = {
     * 	LEFT: 'ArrowLeft', //left arrow
     * 	UP: 'ArrowUp', // up arrow
     * 	RIGHT: 'ArrowRight', // right arrow
     * 	BOTTOM: 'ArrowDown' // down arrow
     * }
     * ```
     * @type {Object}
     */
    this.keys = { LEFT: 'ArrowLeft', UP: 'ArrowUp', RIGHT: 'ArrowRight', BOTTOM: 'ArrowDown' };

    /**
     * This object contains references to the mouse actions used by the controls.
     *
     * ```js
     * controls.mouseButtons = {
     * 	LEFT: THREE.MOUSE.ROTATE,
     * 	MIDDLE: THREE.MOUSE.DOLLY,
     * 	RIGHT: THREE.MOUSE.PAN
     * }
     * ```
     * @type {Object}
     */
    this.mouseButtons = { LEFT: MOUSE.ROTATE, MIDDLE: MOUSE.DOLLY, RIGHT: MOUSE.PAN };

    /**
     * This object contains references to the touch actions used by the controls.
     *
     * ```js
     * controls.mouseButtons = {
     * 	ONE: THREE.TOUCH.ROTATE,
     * 	TWO: THREE.TOUCH.DOLLY_PAN
     * }
     * ```
     * @type {Object}
     */
    this.touches = { ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_PAN };

    /**
     * Used internally by `saveState()` and `reset()`.
     *
     * @type {Vector3}
     */
    this.target0 = this.target.clone();

    /**
     * Used internally by `saveState()` and `reset()`.
     *
     * @type {Vector3}
     */
    this.position0 = this.object.position.clone();

    /**
     * Used internally by `saveState()` and `reset()`.
     *
     * @type {number}
     */
    this.zoom0 = this.object.zoom;

    // the target DOM element for key events
    this._domElementKeyEvents = null;

    // internals

    this._lastPosition = new Vector3();
    this._lastQuaternion = new Quaternion();
    this._lastTargetPosition = new Vector3();

    // so camera.up is the orbit axis
    this._quat = new Quaternion().setFromUnitVectors(object.up, new Vector3(0, 1, 0));
    this._quatInverse = this._quat.clone().invert();

    // current position in spherical coordinates
    this._spherical = new Spherical();
    this._sphericalDelta = new Spherical();

    this._scale = 1;
    this._panOffset = new Vector3();

    this._rotateStart = new Vector2();
    this._rotateEnd = new Vector2();
    this._rotateDelta = new Vector2();

    this._panStart = new Vector2();
    this._panEnd = new Vector2();
    this._panDelta = new Vector2();

    this._dollyStart = new Vector2();
    this._dollyEnd = new Vector2();
    this._dollyDelta = new Vector2();

    this._dollyDirection = new Vector3();
    this._mouse = new Vector2();
    this._performCursorZoom = false;

    this._pointers = [];
    this._pointerPositions = {};

    this._controlActive = false;

    // event listeners

    this._onPointerMove = onPointerMove.bind(this);
    this._onPointerDown = onPointerDown.bind(this);
    this._onPointerUp = onPointerUp.bind(this);
    this._onContextMenu = onContextMenu.bind(this);
    this._onMouseWheel = onMouseWheel.bind(this);
    this._onKeyDown = onKeyDown.bind(this);

    this._onTouchStart = onTouchStart.bind(this);
    this._onTouchMove = onTouchMove.bind(this);

    this._onMouseDown = onMouseDown.bind(this);
    this._onMouseMove = onMouseMove.bind(this);

    this._interceptControlDown = interceptControlDown.bind(this);
    this._interceptControlUp = interceptControlUp.bind(this);

    //

    if (this.domElement !== null) {
      this.connect(this.domElement);
    }

    this.update();
  }

  connect(element) {
    super.connect(element);

    this.domElement.addEventListener('pointerdown', this._onPointerDown);
    this.domElement.addEventListener('pointercancel', this._onPointerUp);

    this.domElement.addEventListener('contextmenu', this._onContextMenu);
    this.domElement.addEventListener('wheel', this._onMouseWheel, { passive: false });

    const document = this.domElement.getRootNode(); // offscreen canvas compatibility
    document.addEventListener('keydown', this._interceptControlDown, { passive: true, capture: true });

    this.domElement.style.touchAction = 'none'; // disable touch scroll
  }

  disconnect() {
    this.domElement.removeEventListener('pointerdown', this._onPointerDown);
    this.domElement.removeEventListener('pointermove', this._onPointerMove);
    this.domElement.removeEventListener('pointerup', this._onPointerUp);
    this.domElement.removeEventListener('pointercancel', this._onPointerUp);

    this.domElement.removeEventListener('wheel', this._onMouseWheel);
    this.domElement.removeEventListener('contextmenu', this._onContextMenu);

    this.stopListenToKeyEvents();

    const document = this.domElement.getRootNode(); // offscreen canvas compatibility
    document.removeEventListener('keydown', this._interceptControlDown, { capture: true });

    this.domElement.style.touchAction = 'auto';
  }

  dispose() {
    this.disconnect();
  }

  /**
   * Get the current vertical rotation, in radians.
   *
   * @return {number} The current vertical rotation, in radians.
   */
  getPolarAngle() {
    return this._spherical.phi;
  }

  /**
   * Get the current horizontal rotation, in radians.
   *
   * @return {number} The current horizontal rotation, in radians.
   */
  getAzimuthalAngle() {
    return this._spherical.theta;
  }

  /**
   * Returns the distance from the camera to the target.
   *
   * @return {number} The distance from the camera to the target.
   */
  getDistance() {
    return this.object.position.distanceTo(this.target);
  }

  /**
   * Adds key event listeners to the given DOM element.
   * `window` is a recommended argument for using this method.
   *
   * @param {HTMLDOMElement} domElement - The DOM element
   */
  listenToKeyEvents(domElement) {
    domElement.addEventListener('keydown', this._onKeyDown);
    this._domElementKeyEvents = domElement;
  }

  /**
   * Removes the key event listener previously defined with `listenToKeyEvents()`.
   */
  stopListenToKeyEvents() {
    if (this._domElementKeyEvents !== null) {
      this._domElementKeyEvents.removeEventListener('keydown', this._onKeyDown);
      this._domElementKeyEvents = null;
    }
  }

  /**
   * Save the current state of the controls. This can later be recovered with `reset()`.
   */
  saveState() {
    this.target0.copy(this.target);
    this.position0.copy(this.object.position);
    this.zoom0 = this.object.zoom;
  }

  /**
   * Reset the controls to their state from either the last time the `saveState()`
   * was called, or the initial state.
   */
  reset() {
    this.target.copy(this.target0);
    this.object.position.copy(this.position0);
    this.object.zoom = this.zoom0;

    this.object.updateProjectionMatrix();
    this.dispatchEvent(_changeEvent);

    this.update();

    this.state = _STATE.NONE;
  }

  update(deltaTime = null) {
    const position = this.object.position;

    _v.copy(position).sub(this.target);

    // rotate offset to "y-axis-is-up" space
    _v.applyQuaternion(this._quat);

    // angle from z-axis around y-axis
    this._spherical.setFromVector3(_v);

    if (this.autoRotate && this.state === _STATE.NONE) {
      this._rotateLeft(this._getAutoRotationAngle(deltaTime));
    }

    if (this.enableDamping) {
      this._spherical.theta += this._sphericalDelta.theta * this.dampingFactor;
      this._spherical.phi += this._sphericalDelta.phi * this.dampingFactor;
    } else {
      this._spherical.theta += this._sphericalDelta.theta;
      this._spherical.phi += this._sphericalDelta.phi;
    }

    // restrict theta to be between desired limits

    let min = this.minAzimuthAngle;
    let max = this.maxAzimuthAngle;

    if (isFinite(min) && isFinite(max)) {
      if (min < -Math.PI) min += _twoPI;
      else if (min > Math.PI) min -= _twoPI;

      if (max < -Math.PI) max += _twoPI;
      else if (max > Math.PI) max -= _twoPI;

      if (min <= max) {
        this._spherical.theta = Math.max(min, Math.min(max, this._spherical.theta));
      } else {
        this._spherical.theta =
          this._spherical.theta > (min + max) / 2
            ? Math.max(min, this._spherical.theta)
            : Math.min(max, this._spherical.theta);
      }
    }

    // restrict phi to be between desired limits
    this._spherical.phi = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, this._spherical.phi));

    this._spherical.makeSafe();

    // move target to panned location

    if (this.enableDamping === true) {
      this.target.addScaledVector(this._panOffset, this.dampingFactor);
    } else {
      this.target.add(this._panOffset);
    }

    // Limit the target distance from the cursor to create a sphere around the center of interest
    this.target.sub(this.cursor);
    this.target.clampLength(this.minTargetRadius, this.maxTargetRadius);
    this.target.add(this.cursor);

    let zoomChanged = false;
    // adjust the camera position based on zoom only if we're not zooming to the cursor or if it's an ortho camera
    // we adjust zoom later in these cases
    if ((this.zoomToCursor && this._performCursorZoom) || this.object.isOrthographicCamera) {
      this._spherical.radius = this._clampDistance(this._spherical.radius);
    } else {
      const prevRadius = this._spherical.radius;
      this._spherical.radius = this._clampDistance(this._spherical.radius * this._scale);
      zoomChanged = prevRadius != this._spherical.radius;
    }

    _v.setFromSpherical(this._spherical);

    // rotate offset back to "camera-up-vector-is-up" space
    _v.applyQuaternion(this._quatInverse);

    position.copy(this.target).add(_v);

    this.object.lookAt(this.target);

    if (this.enableDamping === true) {
      this._sphericalDelta.theta *= 1 - this.dampingFactor;
      this._sphericalDelta.phi *= 1 - this.dampingFactor;

      this._panOffset.multiplyScalar(1 - this.dampingFactor);
    } else {
      this._sphericalDelta.set(0, 0, 0);

      this._panOffset.set(0, 0, 0);
    }

    // adjust camera position
    if (this.zoomToCursor && this._performCursorZoom) {
      let newRadius = null;
      if (this.object.isPerspectiveCamera) {
        // move the camera down the pointer ray
        // this method avoids floating point error
        const prevRadius = _v.length();
        newRadius = this._clampDistance(prevRadius * this._scale);

        const radiusDelta = prevRadius - newRadius;
        this.object.position.addScaledVector(this._dollyDirection, radiusDelta);
        this.object.updateMatrixWorld();

        zoomChanged = !!radiusDelta;
      } else if (this.object.isOrthographicCamera) {
        // adjust the ortho camera position based on zoom changes
        const mouseBefore = new Vector3(this._mouse.x, this._mouse.y, 0);
        mouseBefore.unproject(this.object);

        const prevZoom = this.object.zoom;
        this.object.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.object.zoom / this._scale));
        this.object.updateProjectionMatrix();

        zoomChanged = prevZoom !== this.object.zoom;

        const mouseAfter = new Vector3(this._mouse.x, this._mouse.y, 0);
        mouseAfter.unproject(this.object);

        this.object.position.sub(mouseAfter).add(mouseBefore);
        this.object.updateMatrixWorld();

        newRadius = _v.length();
      } else {
        console.warn('WARNING: OrbitControls.js encountered an unknown camera type - zoom to cursor disabled.');
        this.zoomToCursor = false;
      }

      // handle the placement of the target
      if (newRadius !== null) {
        if (this.screenSpacePanning) {
          // position the orbit target in front of the new camera position
          this.target
            .set(0, 0, -1)
            .transformDirection(this.object.matrix)
            .multiplyScalar(newRadius)
            .add(this.object.position);
        } else {
          // get the ray and translation plane to compute target
          _ray.origin.copy(this.object.position);
          _ray.direction.set(0, 0, -1).transformDirection(this.object.matrix);

          // if the camera is 20 degrees above the horizon then don't adjust the focus target to avoid
          // extremely large values
          if (Math.abs(this.object.up.dot(_ray.direction)) < _TILT_LIMIT) {
            this.object.lookAt(this.target);
          } else {
            _plane.setFromNormalAndCoplanarPoint(this.object.up, this.target);
            _ray.intersectPlane(_plane, this.target);
          }
        }
      }
    } else if (this.object.isOrthographicCamera) {
      const prevZoom = this.object.zoom;
      this.object.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.object.zoom / this._scale));

      if (prevZoom !== this.object.zoom) {
        this.object.updateProjectionMatrix();
        zoomChanged = true;
      }
    }

    this._scale = 1;
    this._performCursorZoom = false;

    // update condition is:
    // min(camera displacement, camera rotation in radians)^2 > EPS
    // using small-angle approximation cos(x/2) = 1 - x^2 / 8

    if (
      zoomChanged ||
      this._lastPosition.distanceToSquared(this.object.position) > _EPS ||
      8 * (1 - this._lastQuaternion.dot(this.object.quaternion)) > _EPS ||
      this._lastTargetPosition.distanceToSquared(this.target) > _EPS
    ) {
      this.dispatchEvent(_changeEvent);

      this._lastPosition.copy(this.object.position);
      this._lastQuaternion.copy(this.object.quaternion);
      this._lastTargetPosition.copy(this.target);

      return true;
    }

    return false;
  }

  _getAutoRotationAngle(deltaTime) {
    if (deltaTime !== null) {
      return (_twoPI / 60) * this.autoRotateSpeed * deltaTime;
    } else {
      return (_twoPI / 60 / 60) * this.autoRotateSpeed;
    }
  }

  _getZoomScale(delta) {
    const normalizedDelta = Math.abs(delta * 0.01);
    return Math.pow(0.95, this.zoomSpeed * normalizedDelta);
  }

  _rotateLeft(angle) {
    this._sphericalDelta.theta -= angle;
  }

  _rotateUp(angle) {
    this._sphericalDelta.phi -= angle;
  }

  _panLeft(distance, objectMatrix) {
    _v.setFromMatrixColumn(objectMatrix, 0); // get X column of objectMatrix
    _v.multiplyScalar(-distance);

    this._panOffset.add(_v);
  }

  _panUp(distance, objectMatrix) {
    if (this.screenSpacePanning === true) {
      _v.setFromMatrixColumn(objectMatrix, 1);
    } else {
      _v.setFromMatrixColumn(objectMatrix, 0);
      _v.crossVectors(this.object.up, _v);
    }

    _v.multiplyScalar(distance);

    this._panOffset.add(_v);
  }

  // deltaX and deltaY are in pixels; right and down are positive
  _pan(deltaX, deltaY) {
    const element = this.domElement;

    if (this.object.isPerspectiveCamera) {
      // perspective
      const position = this.object.position;
      _v.copy(position).sub(this.target);
      let targetDistance = _v.length();

      // half of the fov is center to top of screen
      targetDistance *= Math.tan(((this.object.fov / 2) * Math.PI) / 180.0);

      // we use only clientHeight here so aspect ratio does not distort speed
      this._panLeft((2 * deltaX * targetDistance) / element.clientHeight, this.object.matrix);
      this._panUp((2 * deltaY * targetDistance) / element.clientHeight, this.object.matrix);
    } else if (this.object.isOrthographicCamera) {
      // orthographic
      this._panLeft(
        (deltaX * (this.object.right - this.object.left)) / this.object.zoom / element.clientWidth,
        this.object.matrix,
      );
      this._panUp(
        (deltaY * (this.object.top - this.object.bottom)) / this.object.zoom / element.clientHeight,
        this.object.matrix,
      );
    } else {
      // camera neither orthographic nor perspective
      console.warn('WARNING: OrbitControls.js encountered an unknown camera type - pan disabled.');
      this.enablePan = false;
    }
  }

  _dollyOut(dollyScale) {
    if (this.object.isPerspectiveCamera || this.object.isOrthographicCamera) {
      this._scale /= dollyScale;
    } else {
      console.warn('WARNING: OrbitControls.js encountered an unknown camera type - dolly/zoom disabled.');
      this.enableZoom = false;
    }
  }

  _dollyIn(dollyScale) {
    if (this.object.isPerspectiveCamera || this.object.isOrthographicCamera) {
      this._scale *= dollyScale;
    } else {
      console.warn('WARNING: OrbitControls.js encountered an unknown camera type - dolly/zoom disabled.');
      this.enableZoom = false;
    }
  }

  _updateZoomParameters(x, y) {
    if (!this.zoomToCursor) {
      return;
    }

    this._performCursorZoom = true;

    const rect = this.domElement.getBoundingClientRect();
    const dx = x - rect.left;
    const dy = y - rect.top;
    const w = rect.width;
    const h = rect.height;

    this._mouse.x = (dx / w) * 2 - 1;
    this._mouse.y = -(dy / h) * 2 + 1;

    this._dollyDirection
      .set(this._mouse.x, this._mouse.y, 1)
      .unproject(this.object)
      .sub(this.object.position)
      .normalize();
  }

  _clampDistance(dist) {
    return Math.max(this.minDistance, Math.min(this.maxDistance, dist));
  }

  //
  // event callbacks - update the object state
  //

  _handleMouseDownRotate(event) {
    this._rotateStart.set(event.clientX, event.clientY);
  }

  _handleMouseDownDolly(event) {
    this._updateZoomParameters(event.clientX, event.clientX);
    this._dollyStart.set(event.clientX, event.clientY);
  }

  _handleMouseDownPan(event) {
    this._panStart.set(event.clientX, event.clientY);
  }

  _handleMouseMoveRotate(event) {
    this._rotateEnd.set(event.clientX, event.clientY);

    this._rotateDelta.subVectors(this._rotateEnd, this._rotateStart).multiplyScalar(this.rotateSpeed);

    const element = this.domElement;

    this._rotateLeft((_twoPI * this._rotateDelta.x) / element.clientHeight); // yes, height

    this._rotateUp((_twoPI * this._rotateDelta.y) / element.clientHeight);

    this._rotateStart.copy(this._rotateEnd);

    this.update();
  }

  _handleMouseMoveDolly(event) {
    this._dollyEnd.set(event.clientX, event.clientY);

    this._dollyDelta.subVectors(this._dollyEnd, this._dollyStart);

    if (this._dollyDelta.y > 0) {
      this._dollyOut(this._getZoomScale(this._dollyDelta.y));
    } else if (this._dollyDelta.y < 0) {
      this._dollyIn(this._getZoomScale(this._dollyDelta.y));
    }

    this._dollyStart.copy(this._dollyEnd);

    this.update();
  }

  _handleMouseMovePan(event) {
    this._panEnd.set(event.clientX, event.clientY);

    this._panDelta.subVectors(this._panEnd, this._panStart).multiplyScalar(this.panSpeed);

    this._pan(this._panDelta.x, this._panDelta.y);

    this._panStart.copy(this._panEnd);

    this.update();
  }

  _handleMouseWheel(event) {
    this._updateZoomParameters(event.clientX, event.clientY);

    if (event.deltaY < 0) {
      this._dollyIn(this._getZoomScale(event.deltaY));
    } else if (event.deltaY > 0) {
      this._dollyOut(this._getZoomScale(event.deltaY));
    }

    this.update();

    if (this.autoZoomDisable) {
      if (this.getDistance() < this.maxDistance && this.getDistance() > this.minDistance) {
        event.preventDefault();
      }
    } else {
      event.preventDefault();
    }
  }

  _handleKeyDown(event) {
    let needsUpdate = false;

    switch (event.code) {
      case this.keys.UP:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          if (this.enableRotate) {
            this._rotateUp((_twoPI * this.keyRotateSpeed) / this.domElement.clientHeight);
          }
        } else {
          if (this.enablePan) {
            this._pan(0, this.keyPanSpeed);
          }
        }

        needsUpdate = true;
        break;

      case this.keys.BOTTOM:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          if (this.enableRotate) {
            this._rotateUp((-_twoPI * this.keyRotateSpeed) / this.domElement.clientHeight);
          }
        } else {
          if (this.enablePan) {
            this._pan(0, -this.keyPanSpeed);
          }
        }

        needsUpdate = true;
        break;

      case this.keys.LEFT:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          if (this.enableRotate) {
            this._rotateLeft((_twoPI * this.keyRotateSpeed) / this.domElement.clientHeight);
          }
        } else {
          if (this.enablePan) {
            this._pan(this.keyPanSpeed, 0);
          }
        }

        needsUpdate = true;
        break;

      case this.keys.RIGHT:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          if (this.enableRotate) {
            this._rotateLeft((-_twoPI * this.keyRotateSpeed) / this.domElement.clientHeight);
          }
        } else {
          if (this.enablePan) {
            this._pan(-this.keyPanSpeed, 0);
          }
        }

        needsUpdate = true;
        break;
    }

    if (needsUpdate) {
      // prevent the browser from scrolling on cursor keys
      event.preventDefault();

      this.update();
    }
  }

  _handleTouchStartRotate(event) {
    if (this._pointers.length === 1) {
      this._rotateStart.set(event.pageX, event.pageY);
    } else {
      const position = this._getSecondPointerPosition(event);

      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);

      this._rotateStart.set(x, y);
    }
  }

  _handleTouchStartPan(event) {
    if (this._pointers.length === 1) {
      this._panStart.set(event.pageX, event.pageY);
    } else {
      const position = this._getSecondPointerPosition(event);

      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);

      this._panStart.set(x, y);
    }
  }

  _handleTouchStartDolly(event) {
    const position = this._getSecondPointerPosition(event);

    const dx = event.pageX - position.x;
    const dy = event.pageY - position.y;

    const distance = Math.sqrt(dx * dx + dy * dy);

    this._dollyStart.set(0, distance);
  }

  _handleTouchStartDollyPan(event) {
    if (this.enableZoom) this._handleTouchStartDolly(event);

    if (this.enablePan) this._handleTouchStartPan(event);
  }

  _handleTouchStartDollyRotate(event) {
    if (this.enableZoom) this._handleTouchStartDolly(event);

    if (this.enableRotate) this._handleTouchStartRotate(event);
  }

  _handleTouchMoveRotate(event) {
    if (this._pointers.length == 1) {
      this._rotateEnd.set(event.pageX, event.pageY);
    } else {
      const position = this._getSecondPointerPosition(event);

      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);

      this._rotateEnd.set(x, y);
    }

    this._rotateDelta.subVectors(this._rotateEnd, this._rotateStart).multiplyScalar(this.rotateSpeed);

    const element = this.domElement;

    this._rotateLeft((_twoPI * this._rotateDelta.x) / element.clientHeight); // yes, height

    this._rotateUp((_twoPI * this._rotateDelta.y) / element.clientHeight);

    this._rotateStart.copy(this._rotateEnd);
  }

  _handleTouchMovePan(event) {
    if (this._pointers.length === 1) {
      this._panEnd.set(event.pageX, event.pageY);
    } else {
      const position = this._getSecondPointerPosition(event);

      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);

      this._panEnd.set(x, y);
    }

    this._panDelta.subVectors(this._panEnd, this._panStart).multiplyScalar(this.panSpeed);

    this._pan(this._panDelta.x, this._panDelta.y);

    this._panStart.copy(this._panEnd);
  }

  _handleTouchMoveDolly(event) {
    const position = this._getSecondPointerPosition(event);

    const dx = event.pageX - position.x;
    const dy = event.pageY - position.y;

    const distance = Math.sqrt(dx * dx + dy * dy);

    this._dollyEnd.set(0, distance);

    this._dollyDelta.set(0, Math.pow(this._dollyEnd.y / this._dollyStart.y, this.zoomSpeed));

    this._dollyOut(this._dollyDelta.y);

    this._dollyStart.copy(this._dollyEnd);

    const centerX = (event.pageX + position.x) * 0.5;
    const centerY = (event.pageY + position.y) * 0.5;

    this._updateZoomParameters(centerX, centerY);
  }

  _handleTouchMoveDollyPan(event) {
    if (this.enableZoom) this._handleTouchMoveDolly(event);

    if (this.enablePan) this._handleTouchMovePan(event);
  }

  _handleTouchMoveDollyRotate(event) {
    if (this.enableZoom) this._handleTouchMoveDolly(event);

    if (this.enableRotate) this._handleTouchMoveRotate(event);
  }

  // pointers

  _addPointer(event) {
    this._pointers.push(event.pointerId);
  }

  _removePointer(event) {
    delete this._pointerPositions[event.pointerId];

    for (let i = 0; i < this._pointers.length; i++) {
      if (this._pointers[i] == event.pointerId) {
        this._pointers.splice(i, 1);
        return;
      }
    }
  }

  _isTrackingPointer(event) {
    for (let i = 0; i < this._pointers.length; i++) {
      if (this._pointers[i] == event.pointerId) return true;
    }

    return false;
  }

  _trackPointer(event) {
    let position = this._pointerPositions[event.pointerId];

    if (position === undefined) {
      position = new Vector2();
      this._pointerPositions[event.pointerId] = position;
    }

    position.set(event.pageX, event.pageY);
  }

  _getSecondPointerPosition(event) {
    const pointerId = event.pointerId === this._pointers[0] ? this._pointers[1] : this._pointers[0];

    return this._pointerPositions[pointerId];
  }

  //

  _customWheelEvent(event) {
    const mode = event.deltaMode;

    // minimal wheel event altered to meet delta-zoom demand
    const newEvent = {
      clientX: event.clientX,
      clientY: event.clientY,
      deltaY: event.deltaY,
      preventDefault: () => event.preventDefault(),
    };

    switch (mode) {
      case 1: // LINE_MODE
        newEvent.deltaY *= 16;
        break;

      case 2: // PAGE_MODE
        newEvent.deltaY *= 100;
        break;
    }

    // detect if event was triggered by pinching
    if (event.ctrlKey && !this._controlActive) {
      newEvent.deltaY *= 10;
    }

    return newEvent;
  }
}

function onPointerDown(event) {
  if (this.enabled === false) return;

  if (this._pointers.length === 0) {
    this.domElement.setPointerCapture(event.pointerId);

    this.domElement.addEventListener('pointermove', this._onPointerMove);
    this.domElement.addEventListener('pointerup', this._onPointerUp);
  }

  //

  if (this._isTrackingPointer(event)) return;

  //

  this._addPointer(event);

  if (event.pointerType === 'touch') {
    this._onTouchStart(event);
  } else {
    this._onMouseDown(event);
  }
}

function onPointerMove(event) {
  if (this.enabled === false) return;

  if (event.pointerType === 'touch') {
    this._onTouchMove(event);
  } else {
    this._onMouseMove(event);
  }
}

function onPointerUp(event) {
  this._removePointer(event);

  switch (this._pointers.length) {
    case 0:
      this.domElement.releasePointerCapture(event.pointerId);

      this.domElement.removeEventListener('pointermove', this._onPointerMove);
      this.domElement.removeEventListener('pointerup', this._onPointerUp);

      this.dispatchEvent(_endEvent);

      this.state = _STATE.NONE;

      break;

    case 1:
      const pointerId = this._pointers[0];
      const position = this._pointerPositions[pointerId];

      // minimal placeholder event - allows state correction on pointer-up
      this._onTouchStart({ pointerId: pointerId, pageX: position.x, pageY: position.y });

      break;
  }
}

function onMouseDown(event) {
  let mouseAction;

  switch (event.button) {
    case 0:
      mouseAction = this.mouseButtons.LEFT;
      break;

    case 1:
      mouseAction = this.mouseButtons.MIDDLE;
      break;

    case 2:
      mouseAction = this.mouseButtons.RIGHT;
      break;

    default:
      mouseAction = -1;
  }

  switch (mouseAction) {
    case MOUSE.DOLLY:
      if (this.enableZoom === false) return;

      this._handleMouseDownDolly(event);

      this.state = _STATE.DOLLY;

      break;

    case MOUSE.ROTATE:
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        if (this.enablePan === false) return;

        this._handleMouseDownPan(event);

        this.state = _STATE.PAN;
      } else {
        if (this.enableRotate === false) return;

        this._handleMouseDownRotate(event);

        this.state = _STATE.ROTATE;
      }

      break;

    case MOUSE.PAN:
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        if (this.enableRotate === false) return;

        this._handleMouseDownRotate(event);

        this.state = _STATE.ROTATE;
      } else {
        if (this.enablePan === false) return;

        this._handleMouseDownPan(event);

        this.state = _STATE.PAN;
      }

      break;

    default:
      this.state = _STATE.NONE;
  }

  if (this.state !== _STATE.NONE) {
    this.dispatchEvent(_startEvent);
  }
}

function onMouseMove(event) {
  switch (this.state) {
    case _STATE.ROTATE:
      if (this.enableRotate === false) return;

      this._handleMouseMoveRotate(event);

      break;

    case _STATE.DOLLY:
      if (this.enableZoom === false) return;

      this._handleMouseMoveDolly(event);

      break;

    case _STATE.PAN:
      if (this.enablePan === false) return;

      this._handleMouseMovePan(event);

      break;
  }
}

function onMouseWheel(event) {
  if (this.enabled === false || this.enableZoom === false || this.state !== _STATE.NONE) return;

  // event.preventDefault();

  this.dispatchEvent(_startEvent);

  this._handleMouseWheel(this._customWheelEvent(event));

  this.dispatchEvent(_endEvent);
}

function onKeyDown(event) {
  if (this.enabled === false) return;

  this._handleKeyDown(event);
}

function onTouchStart(event) {
  this._trackPointer(event);

  switch (this._pointers.length) {
    case 1:
      switch (this.touches.ONE) {
        case TOUCH.ROTATE:
          if (this.enableRotate === false) return;

          this._handleTouchStartRotate(event);

          this.state = _STATE.TOUCH_ROTATE;

          break;

        case TOUCH.PAN:
          if (this.enablePan === false) return;

          this._handleTouchStartPan(event);

          this.state = _STATE.TOUCH_PAN;

          break;

        default:
          this.state = _STATE.NONE;
      }

      break;

    case 2:
      switch (this.touches.TWO) {
        case TOUCH.DOLLY_PAN:
          if (this.enableZoom === false && this.enablePan === false) return;

          this._handleTouchStartDollyPan(event);

          this.state = _STATE.TOUCH_DOLLY_PAN;

          break;

        case TOUCH.DOLLY_ROTATE:
          if (this.enableZoom === false && this.enableRotate === false) return;

          this._handleTouchStartDollyRotate(event);

          this.state = _STATE.TOUCH_DOLLY_ROTATE;

          break;

        default:
          this.state = _STATE.NONE;
      }

      break;

    default:
      this.state = _STATE.NONE;
  }

  if (this.state !== _STATE.NONE) {
    this.dispatchEvent(_startEvent);
  }
}

function onTouchMove(event) {
  this._trackPointer(event);

  switch (this.state) {
    case _STATE.TOUCH_ROTATE:
      if (this.enableRotate === false) return;

      this._handleTouchMoveRotate(event);

      this.update();

      break;

    case _STATE.TOUCH_PAN:
      if (this.enablePan === false) return;

      this._handleTouchMovePan(event);

      this.update();

      break;

    case _STATE.TOUCH_DOLLY_PAN:
      if (this.enableZoom === false && this.enablePan === false) return;

      this._handleTouchMoveDollyPan(event);

      this.update();

      break;

    case _STATE.TOUCH_DOLLY_ROTATE:
      if (this.enableZoom === false && this.enableRotate === false) return;

      this._handleTouchMoveDollyRotate(event);

      this.update();

      break;

    default:
      this.state = _STATE.NONE;
  }
}

function onContextMenu(event) {
  if (this.enabled === false) return;

  event.preventDefault();
}

function interceptControlDown(event) {
  if (event.key === 'Control') {
    this._controlActive = true;

    const document = this.domElement.getRootNode(); // offscreen canvas compatibility

    document.addEventListener('keyup', this._interceptControlUp, { passive: true, capture: true });
  }
}

function interceptControlUp(event) {
  if (event.key === 'Control') {
    this._controlActive = false;

    const document = this.domElement.getRootNode(); // offscreen canvas compatibility

    document.removeEventListener('keyup', this._interceptControlUp, { passive: true, capture: true });
  }
}

class OrbitControlsCameraManager {
    constructor() {
        this.width = 0;
        this.height = 0;
        this.debug = false;
        this.defaultMinDistance = 0;
        this.defaultMaxDistance = 0;
    }
    set enabled(value) {
        this.orbitControls.enabled = value;
    }
    getControlsAzimuthalAngle() {
        return this.orbitControls.getAzimuthalAngle();
    }
    init(configuration, canvas) {
        var _a, _b, _c, _d, _e, _f;
        const params = new URLSearchParams(location.search);
        this.debug = params.get('debug') !== null;
        const parent = canvas.parentElement;
        if (parent) {
            this.height = parent.clientHeight;
            this.width = parent.clientWidth;
        }
        const near = 0.05;
        const farFactor = (_b = (_a = configuration.camera) === null || _a === void 0 ? void 0 : _a.farFactor) !== null && _b !== void 0 ? _b : 2;
        const far = configuration.camera.maxDistance * farFactor;
        this.camera = new PerspectiveCamera(30, this.width / this.height, near, far);
        this.orbitControls = new OrbitControls(this.camera, canvas);
        this.orbitControls.autoZoomDisable = (_d = (_c = configuration.camera) === null || _c === void 0 ? void 0 : _c.autoZoomDisable) !== null && _d !== void 0 ? _d : false;
        if (configuration.camera.position) {
            const position = getAdaptiveConfigurationValue(configuration.camera.position);
            this.camera.position.x = position.x;
            this.camera.position.y = position.y;
            this.camera.position.z = position.z;
        }
        this.orbitControls.enabled = true;
        this.orbitControls.rotateSpeed = 1;
        this.orbitControls.zoomSpeed = 1.5;
        this.orbitControls.minDistance = configuration.camera.minDistance;
        this.orbitControls.maxDistance = configuration.camera.maxDistance;
        this.defaultMinDistance = configuration.camera.minDistance;
        this.defaultMaxDistance = configuration.camera.maxDistance;
        this.orbitControls.maxPolarAngle = configuration.camera.maxPolarAngle
            ? (Math.PI / 180) * configuration.camera.maxPolarAngle
            : Math.PI;
        this.orbitControls.minAzimuthAngle = (_e = configuration.camera.minAzimuthAngle) !== null && _e !== void 0 ? _e : Infinity;
        this.orbitControls.maxAzimuthAngle = (_f = configuration.camera.maxAzimuthAngle) !== null && _f !== void 0 ? _f : Infinity;
        if (configuration.camera.enablePan) {
            this.orbitControls.enablePan = true;
        }
        else {
            this.orbitControls.enablePan = false;
        }
        if (configuration.camera.lookAt) {
            const lookAt = getAdaptiveConfigurationValue(configuration.camera.lookAt);
            this.orbitControls.target = new Vector3(lookAt.x, lookAt.y, lookAt.z);
        }
        this.orbitControls.enableDamping = true;
        this.orbitControls.dampingFactor = 0.1;
        this.orbitControls.screenSpacePanning = true;
    }
    updateControlsTarget(target) {
        this.orbitControls.target = target;
    }
    setDistanceLimits(min, max) {
        this.orbitControls.minDistance = min;
        this.orbitControls.maxDistance = max;
    }
    resetDistanceLimits() {
        this.orbitControls.minDistance = this.defaultMinDistance;
        this.orbitControls.maxDistance = this.defaultMaxDistance;
    }
    update() {
        if (this.debug) {
            console.log('Camera position', this.camera.position.toArray());
            console.log('Camera lookAt', this.orbitControls.target.toArray());
            console.log('azimuth', this.orbitControls.getAzimuthalAngle());
            console.log('polar', this.orbitControls.getPolarAngle());
        }
        this.orbitControls.update();
    }
    resize(width, height) {
        this.width = width;
        this.height = height;
        this.camera.aspect = this.width / this.height;
        this.camera.updateProjectionMatrix();
    }
}

class ModelLoader {
    constructor() {
        this.cache = new Map();
        this.loader = new GLTFLoader();
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('./three/draco/gltf/');
        this.loader.setDRACOLoader(dracoLoader);
    }
    load(path_1) {
        return __awaiter(this, arguments, void 0, function* (path, onProgress = () => { }) {
            if (this.cache.has(path)) {
                return Promise.resolve(this.cache.get(path));
            }
            return new Promise((resolve, reject) => {
                this.loader.load(path, (gltf) => {
                    this.cache.set(path, gltf.scene);
                    resolve(gltf.scene);
                }, onProgress, reject);
            });
        });
    }
}

class ClTextureLoader {
    constructor() {
        this.cache = new Map();
        this.loader = new TextureLoader();
    }
    load(path, onProgress) {
        if (this.cache.has(path)) {
            return Promise.resolve(this.cache.get(path));
        }
        return new Promise((resolve, reject) => {
            this.loader.load(path, (texture) => {
                this.cache.set(path, texture);
                resolve(texture);
            }, onProgress, reject);
        });
    }
}

const TEXTURE_SCHEMA = {
    offset: { kind: 'vector2', requiresNeedsUpdateOnChange: false },
    repeat: { kind: 'vector2', requiresNeedsUpdateOnChange: false },
    center: { kind: 'vector2', requiresNeedsUpdateOnChange: false },
    rotation: { kind: 'number', requiresNeedsUpdateOnChange: false },
    mapping: { kind: 'enum', requiresNeedsUpdateOnChange: true },
    wrapS: { kind: 'enum', requiresNeedsUpdateOnChange: true },
    wrapT: { kind: 'enum', requiresNeedsUpdateOnChange: true },
    magFilter: { kind: 'enum', requiresNeedsUpdateOnChange: true },
    minFilter: { kind: 'enum', requiresNeedsUpdateOnChange: true },
    anisotropy: { kind: 'number', requiresNeedsUpdateOnChange: true },
    format: { kind: 'enum', requiresNeedsUpdateOnChange: true },
    type: { kind: 'enum', requiresNeedsUpdateOnChange: true },
    colorSpace: { kind: 'enum', requiresNeedsUpdateOnChange: true },
    generateMipmaps: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    matrixAutoUpdate: { kind: 'boolean', requiresNeedsUpdateOnChange: false },
    flipY: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    premultiplyAlpha: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    unpackAlignment: { kind: 'number', requiresNeedsUpdateOnChange: true },
};
function convertTextureValue(kind, value) {
    if (value == null)
        return value;
    switch (kind) {
        case 'vector2': {
            if (value instanceof Vector2)
                return value;
            if (Array.isArray(value))
                return new Vector2(value[0], value[1]);
            return new Vector2(value.x, value.y);
        }
        case 'vector3': {
            if (value instanceof Vector3)
                return value;
            if (Array.isArray(value))
                return new Vector3(value[0], value[1], value[2]);
            return new Vector3(value.x, value.y, value.z);
        }
        case 'color':
            return new Color(value);
        default:
            return value;
    }
}

class ClFontLoader {
    constructor() {
        this.cache = new Map();
        this.loader = new FontLoader();
    }
    load(path, onProgress) {
        if (this.cache.has(path)) {
            return Promise.resolve(this.cache.get(path));
        }
        return new Promise((resolve, reject) => {
            this.loader.load(path, (font) => {
                this.cache.set(path, font);
                resolve(font);
            }, onProgress, reject);
        });
    }
}

const SCENE_ENVIRONMENT_KEY = '__scene_environment__';
class AssetsManager {
    constructor() {
        this.loadedAssets = new Map();
        this.missingAssets = [];
        this.modelLoader = new ModelLoader();
        this.textureLoader = new ClTextureLoader();
        this.fontLoader = new ClFontLoader();
        this.defaultTexture = new Texture();
    }
    wantsAssets(assets) {
        for (const asset of assets) {
            if (this.loadedAssets.has(asset.key)) {
                continue;
            }
            this.missingAssets.push(asset);
        }
    }
    loadMissingAssets() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(this.missingAssets.map((asset) => this.loadAsset(asset)));
            this.missingAssets = [];
        });
    }
    getAsset(key) {
        var _a;
        return (_a = this.loadedAssets.get(key)) === null || _a === void 0 ? void 0 : _a.payload;
    }
    getAssetWithType(key, type) {
        const asset = this.loadedAssets.get(key);
        if (!asset) {
            return;
        }
        if (asset.prefetchAsset.type !== type) {
            throw new Error(`Invalid asset type. Expected ${type} but got ${asset.prefetchAsset.type}`);
        }
        return asset.payload;
    }
    getModelAsset(key) {
        return this.getAssetWithType(key, 'Model3d');
    }
    getFontAsset(key) {
        return this.getAssetWithType(key, 'Font');
    }
    getTextureAsset(key) {
        return this.getAssetWithType(key, 'Texture');
    }
    registerAsset(key, asset, type = 'Texture') {
        this.loadedAssets.set(key, {
            prefetchAsset: { key, type, url: '' },
            payload: asset,
        });
    }
    loadAsset(asset) {
        return __awaiter(this, void 0, void 0, function* () {
            const type = asset.type;
            switch (type) {
                case 'Model3d': {
                    return this.loadModel3d(asset);
                }
                case 'Texture': {
                    return this.loadTexture(asset);
                }
                case 'Font': {
                    return this.loadFont(asset);
                }
                default:
                    assertUnreachable(type, `Unhandled asset type "${type}"`);
            }
        });
    }
    loadModel3d(asset) {
        return __awaiter(this, void 0, void 0, function* () {
            const model = yield this.modelLoader.load(asset.url);
            this.loadedAssets.set(asset.key, { prefetchAsset: asset, payload: model });
        });
    }
    loadTexture(asset) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const prev = (_a = this.loadedAssets.get(asset.key)) === null || _a === void 0 ? void 0 : _a.prefetchAsset;
            const oldParams = (_b = prev === null || prev === void 0 ? void 0 : prev.params) !== null && _b !== void 0 ? _b : {};
            const texture = yield this.textureLoader.load(asset.url);
            if (!texture) {
                throw new Error(`Failed to load texture ${asset.url}`);
            }
            const params = (_c = asset.params) !== null && _c !== void 0 ? _c : {};
            const schema = TEXTURE_SCHEMA;
            for (const key of Object.keys(params)) {
                const spec = schema[key];
                const nextValue = params[key];
                if (nextValue === undefined) {
                    console.warn(`Skipped texture param "${key}" of ${asset.key}: it resolved to undefined`);
                    continue;
                }
                if (!spec) {
                    texture[key] = nextValue;
                    texture.needsUpdate = true;
                    continue;
                }
                texture[key] = convertTextureValue(spec.kind, nextValue);
                if (spec.requiresNeedsUpdateOnChange)
                    texture.needsUpdate = true;
            }
            for (const key of Object.keys(oldParams)) {
                if (!(key in params)) {
                    this.resetTextureProperty(texture, key);
                    const spec = schema[key];
                    if (!spec || spec.requiresNeedsUpdateOnChange) {
                        texture.needsUpdate = true;
                    }
                }
            }
            this.loadedAssets.set(asset.key, { prefetchAsset: asset, payload: texture });
        });
    }
    resetTextureProperty(texture, key) {
        const defaultTex = this.defaultTexture;
        if (key in defaultTex) {
            texture[key] = defaultTex[key];
        }
        else {
            console.warn(`You are trying to reset unknown texture property ${key}. Setting it to undefined`);
            texture[key] = undefined;
        }
    }
    loadFont(asset) {
        return __awaiter(this, void 0, void 0, function* () {
            const font = yield this.fontLoader.load(asset.url);
            this.loadedAssets.set(asset.key, { prefetchAsset: asset, payload: font });
        });
    }
}

class HdrEnvironment {
    constructor(settings, assetsManager) {
        this.settings = settings;
        this.assetsManager = assetsManager;
    }
    init(renderer, scene) {
        const pMREMGenerator = new PMREMGenerator(renderer);
        pMREMGenerator.compileEquirectangularShader();
        return new Promise((resolve, reject) => {
            if (!this.settings.hdr) {
                resolve();
                return;
            }
            new RGBELoader().setDataType(HalfFloatType).load(getAdaptiveConfigurationValue(this.settings.hdr), (texture) => {
                const envTexture = pMREMGenerator.fromEquirectangular(texture).texture;
                scene.environment = envTexture;
                if (this.assetsManager) {
                    this.assetsManager.registerAsset(SCENE_ENVIRONMENT_KEY, envTexture);
                }
                texture.dispose();
                pMREMGenerator.dispose();
                resolve();
            }, () => { }, reject);
        });
    }
}

class MaterialLoader {
    constructor() {
        this.cache = new Map();
        this.loader = new GLTFLoader$1();
        const dracoLoader = new DRACOLoader$1();
        dracoLoader.setDecoderPath('./three/draco/gltf/');
        this.loader.setDRACOLoader(dracoLoader);
    }
    get(materialSpecification_1) {
        return __awaiter(this, arguments, void 0, function* (materialSpecification, onProgress = () => { }) {
            const path = materialSpecification.fileName;
            if (this.cache.has(path)) {
                const file = this.cache.get(path);
                const mesh = getMeshByName(file, materialSpecification.meshName);
                return Promise.resolve(mesh ? mesh.material : undefined);
            }
            return new Promise((resolve, reject) => {
                this.loader.load(path, (gltf) => {
                    this.cache.set(path, gltf.scene);
                    const mesh = getMeshByName(gltf.scene, materialSpecification.meshName);
                    resolve(mesh ? mesh.material : undefined);
                }, onProgress, reject);
            });
        });
    }
}
function getMeshByName(model, name) {
    const mesh = model.getObjectByName(name);
    if (!(mesh instanceof Mesh)) {
        return undefined;
    }
    return mesh;
}

class ThreeJSViewRenderer extends WebGLRenderer {
    constructor(canvas, config) {
        var _a, _b;
        super({
            canvas: canvas,
            antialias: true,
            preserveDrawingBuffer: true,
            alpha: (_b = (_a = config.renderer) === null || _a === void 0 ? void 0 : _a.transparent) !== null && _b !== void 0 ? _b : false,
        });
        this.config = config;
        this.init(canvas);
    }
    init(canvas) {
        this.domElement = canvas;
        this.setClearColor(0xeeeeee);
        this.outputColorSpace = SRGBColorSpace;
        this.setPixelRatio(window.devicePixelRatio);
        this.shadowMap.enabled = true;
        this.shadowMap.type = PCFSoftShadowMap;
        this.applyConfig();
    }
    applyConfig() {
        const rendererConfig = this.config.renderer;
        if (rendererConfig) {
            if (rendererConfig.toneMapping) {
                this.toneMapping = rendererConfig.toneMapping;
            }
            if (rendererConfig.transparent) {
                this.setClearAlpha(0);
            }
        }
    }
}

const isBoxIntersectsBoundary = (boundary) => (box) => {
    const boundaryBox = new Box2(new Vector2(boundary.minX, boundary.minY), new Vector2(boundary.maxX, boundary.maxY));
    const screenBox = new Box2(new Vector2(box.x, box.y), new Vector2(box.x + box.width, box.y + box.height));
    return boundaryBox.intersectsBox(screenBox);
};
const emptyPadding$1 = {
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
};
class IntersectionService {
    constructor(screenSize, positionService, boundingService, settings = {}) {
        var _a, _b;
        this.screenSize = screenSize;
        this.positionService = positionService;
        this.boundingService = boundingService;
        const padding2D = Object.assign({}, emptyPadding$1, (_a = settings.padding2D) !== null && _a !== void 0 ? _a : {});
        const padding3D = Object.assign({}, emptyPadding$1, (_b = settings.padding3D) !== null && _b !== void 0 ? _b : {});
        this.settings = {
            padding3D,
            padding2D,
        };
    }
    init(screenBoxes, root) {
        this.screenBoxes = screenBoxes;
        this.root = root;
        this.bbox = this.getBoundingBox();
    }
    getBoundingBox() {
        const { top, left, right, bottom } = this.settings.padding3D;
        const bbox = this.boundingService.getBoundingBox(this.root).clone();
        bbox.max.add(new Vector3(right, top, 0));
        bbox.min.sub(new Vector3(left, bottom, 0));
        return bbox;
    }
    getBoundary(distance) {
        const { top, left, right, bottom } = this.settings.padding2D;
        const boundary = this.getBoundingBoxProjectionBox(distance, this.bbox);
        boundary.maxX += right;
        boundary.maxY += top;
        boundary.minX -= left;
        boundary.minY -= bottom;
        return boundary;
    }
    intersects(distance) {
        const boundary = this.getBoundary(distance);
        const result = this.screenBoxes.find(isBoxIntersectsBoundary(boundary));
        return !!result;
    }
    getBoundingBoxProjectionBox(distance, bbox) {
        const camera = this.positionService.getCameraAtDistance(distance);
        return this.boundingService.getBoundingBoxProjection(bbox, camera, this.screenSize);
    }
}

class PositionService {
    init(camera, [x, y, z, lookX, lookY, lookZ]) {
        this.camera = camera;
        this.lookAt = new Vector3(lookX, lookY, lookZ);
        this.position = new Vector3(x, y, z);
        this.lookAt.clone().sub(this.position).length();
        this.dir = this.lookAt.clone().sub(this.position).negate().normalize();
    }
    stateFromDistance(distance) {
        const dir = this.dir.clone().multiplyScalar(distance);
        const position = this.lookAt.clone().add(dir);
        return position.toArray().concat(this.lookAt.toArray());
    }
    getCameraAtDistance(distance) {
        const state = this.stateFromDistance(distance);
        return this.cameraFromState(state);
    }
    cameraFromState([x, y, z, lookX, lookY, lookZ]) {
        const t = this.camera;
        const camera = new PerspectiveCamera(t.fov, t.aspect, t.near, t.far);
        camera.position.copy(new Vector3(x, y, z));
        camera.lookAt(new Vector3(lookX, lookY, lookZ));
        camera.updateMatrixWorld();
        return camera;
    }
}

class ThreeUtils {
    getNodeBoundingBox(node) {
        const bbox = new Box3();
        bbox.setFromObject(node);
        return bbox;
    }
    get2DPosition(vector, camera, screenWidth, screenHeight) {
        const v = vector.project(camera);
        return {
            x: ((v.x + 1) * screenWidth) / 2,
            y: ((1 - v.y) * screenHeight) / 2,
        };
    }
    getMorphedBoundingBox(mesh) {
        if (!((mesh === null || mesh === void 0 ? void 0 : mesh.geometry) instanceof BufferGeometry)) {
            throw new Error('BufferGeometry expected');
        }
        const morphCount = mesh.morphTargetInfluences ? mesh.morphTargetInfluences.length : 0;
        const positions = mesh.geometry.getAttribute('position');
        const min = new Vector3(Infinity, Infinity, Infinity), max = new Vector3(-Infinity, -Infinity, -Infinity);
        for (let i = 0; i < positions.count; i++) {
            let x = positions.getX(i);
            for (let j = 0; j < morphCount; j++) {
                x += mesh.morphTargetInfluences[j] * mesh.geometry.morphAttributes.position[j].getX(i);
            }
            if (x < min.x) {
                min.x = x;
            }
            if (x > max.x) {
                max.x = x;
            }
            let y = positions.getY(i);
            for (let j = 0; j < morphCount; j++) {
                y += mesh.morphTargetInfluences[j] * mesh.geometry.morphAttributes.position[j].getY(i);
            }
            if (y < min.y) {
                min.y = y;
            }
            if (y > max.y) {
                max.y = y;
            }
            let z = positions.getZ(i);
            for (let j = 0; j < morphCount; j++) {
                z += mesh.morphTargetInfluences[j] * mesh.geometry.morphAttributes.position[j].getZ(i);
            }
            if (z < min.z) {
                min.z = z;
            }
            if (z > max.z) {
                max.z = z;
            }
        }
        return new Box3(min, max);
    }
    loadTexture(name) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                new TextureLoader().load(name, (texture) => {
                    resolve(texture);
                }, () => { }, () => {
                    reject(new Error(`Cannot load texture ${name}`));
                });
            });
        });
    }
    vectorToArray(vector) {
        return [vector.x, vector.y, vector.z];
    }
    arrayToVector(xs) {
        return new Vector3(xs[0], xs[1], xs[2]);
    }
}
const threeUtils = new ThreeUtils();

class BoundingBoxService {
    getBoundingBox(root) {
        const bbox = new Box3();
        bbox.setFromObject(root);
        const size = new Vector3();
        bbox.getSize(size);
        return bbox;
    }
    getBoundingBoxProjection(bbox, camera, screenSize) {
        const center = new Vector3();
        bbox.getCenter(center);
        const size = new Vector3();
        bbox.getSize(size);
        const vertexes = [
            new Vector3(-0.5 * size.x + center.x, -0.5 * size.y + center.y, -0.5 * size.z + center.z),
            new Vector3(-0.5 * size.x + center.x, -0.5 * size.y + center.y, 0.5 * size.z + center.z),
            new Vector3(-0.5 * size.x + center.x, 0.5 * size.y + center.y, -0.5 * size.z + center.z),
            new Vector3(-0.5 * size.x + center.x, 0.5 * size.y + center.y, 0.5 * size.z + center.z),
            new Vector3(0.5 * size.x + center.x, -0.5 * size.y + center.y, -0.5 * size.z + center.z),
            new Vector3(0.5 * size.x + center.x, -0.5 * size.y + center.y, 0.5 * size.z + center.z),
            new Vector3(0.5 * size.x + center.x, 0.5 * size.y + center.y, -0.5 * size.z + center.z),
            new Vector3(0.5 * size.x + center.x, 0.5 * size.y + center.y, 0.5 * size.z + center.z),
        ].map((v) => threeUtils.get2DPosition(v, camera, screenSize.width, screenSize.height));
        return vertexes.reduce((acc, next) => ({
            minX: Math.min(acc.minX, next.x),
            minY: Math.min(acc.minY, next.y),
            maxX: Math.max(acc.maxX, next.x),
            maxY: Math.max(acc.maxY, next.y),
        }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
    }
}

class BinarySearchCameraOptimizer {
    static create(size, minDistance, maxDistance, precision = 0.001, maxIteration = 1000) {
        const positionService = new PositionService();
        const intersectionService = new IntersectionService(size, positionService, new BoundingBoxService(), {
            padding3D: { top: 0.0, left: 0.0, right: 0.0 },
        });
        return new BinarySearchCameraOptimizer(minDistance, maxDistance, intersectionService, positionService, precision, maxIteration);
    }
    constructor(minDistance, maxDistance, intersectionService, positionService, precision = 1, maxIteration = 10000) {
        this.minDistance = minDistance;
        this.maxDistance = maxDistance;
        this.intersectionService = intersectionService;
        this.positionService = positionService;
        this.precision = precision;
        this.maxIteration = maxIteration;
        this.min = this.minDistance;
        this.max = this.maxDistance;
    }
    optimizeCameraPosition(camera, state, root, screenBoxes) {
        this.intersectionService.init(screenBoxes, root);
        this.positionService.init(camera, state);
        this.min = this.minDistance;
        this.max = this.maxDistance;
        if (this.intersectionService.intersects(this.max)) {
            return { state: this.positionService.stateFromDistance(this.max), distance: this.max };
        }
        if (!this.intersectionService.intersects(this.min)) {
            return { state: this.positionService.stateFromDistance(this.min), distance: this.min };
        }
        let iterations = 0;
        let error = Infinity;
        let middle = this.max;
        while (iterations < this.maxIteration && error > this.precision) {
            middle = 0.5 * (this.max + this.min);
            if (this.intersectionService.intersects(middle)) {
                this.min = middle;
            }
            else {
                this.max = middle;
            }
            error = Math.abs(this.max - this.min);
            iterations++;
        }
        const distance = this.max;
        return { state: this.positionService.stateFromDistance(distance), distance };
    }
}

const emptyPadding = {
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
};
class GlbConverter {
    constructor(width, height, config) {
        this.width = width;
        this.height = height;
        this.config = config;
        this.padding2D = emptyPadding;
    }
    convertGltfToBase64(url, hdr, onProgress) {
        return __awaiter(this, void 0, void 0, function* () {
            const gltfModel = yield this.loadModel(url, onProgress);
            return this.convertObject3DToBase64(gltfModel.scene, hdr);
        });
    }
    convertObject3DToBase64(node_1, hdr_1) {
        return __awaiter(this, arguments, void 0, function* (node, hdr, options = {}) {
            var _a;
            const container = new Object3D();
            container.add(node);
            const canvas = document.createElement('canvas');
            canvas.width = this.width;
            canvas.height = this.height;
            const renderer = new WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
            renderer.outputColorSpace = SRGBColorSpace;
            renderer.setPixelRatio(window.devicePixelRatio);
            renderer.shadowMap.enabled = true;
            renderer.shadowMap.type = PCFSoftShadowMap;
            renderer.toneMapping = LinearToneMapping;
            renderer.toneMappingExposure = 0.65;
            renderer.setSize(this.width, this.height);
            renderer.setClearColor(0xffffff, 1);
            const scene = new Scene();
            const pMREMGenerator = new PMREMGenerator(renderer);
            pMREMGenerator.compileEquirectangularShader();
            const envTexture = yield this.loadEnvTexture(hdr);
            scene.environment = pMREMGenerator.fromEquirectangular(envTexture).texture;
            const ambientLight = new AmbientLight(0xffffff, 0.6);
            scene.add(ambientLight);
            const directionalLight = new DirectionalLight(0xffffff, 0.8);
            directionalLight.position.set(10, 10, 10);
            scene.add(directionalLight);
            scene.add(container);
            const camera = new PerspectiveCamera(30, this.width / this.height, 0.01, 180);
            const cameraPosition = (_a = options.cameraPosition) !== null && _a !== void 0 ? _a : 'perspectiveLeft';
            this.fitCameraToObject3D(camera, container, cameraPosition);
            renderer.render(scene, camera);
            const dataURL = canvas.toDataURL('image/png');
            return dataURL;
        });
    }
    convertBufferToBase64(data, hdr) {
        return __awaiter(this, void 0, void 0, function* () {
            const scene = yield this.parseBuffer(data);
            return this.convertObject3DToBase64(scene, hdr);
        });
    }
    parseBuffer(data) {
        return new Promise((resolve, reject) => {
            const loader = this.createGltfLoader();
            loader.parse(data, '', (gltf) => {
                resolve(gltf.scene);
            }, (error) => {
                console.error('Parsing error:', error);
                reject(error);
            });
        });
    }
    fitCameraToObject3D(camera, node, cameraPosition) {
        const { position, lookAt, far } = this.getCameraParams(camera, node, cameraPosition);
        camera.far = far;
        camera.position.copy(position);
        camera.lookAt(lookAt);
        camera.updateProjectionMatrix();
        return lookAt;
    }
    getCameraParams(camera, node, cameraPosition) {
        const nodeClone = getMeasurableObjects(node);
        const box = new Box3().setFromObject(nodeClone);
        const size = box.getSize(new Vector3());
        const center = box.getCenter(new Vector3());
        const position = center.clone().add(this.getCameraPosition(cameraPosition));
        camera.position.set(position.x, position.y, position.z);
        camera.lookAt(center);
        const maxSize = Math.max(size.x, size.y, size.z, 100);
        const minSize = 0.01;
        const cameraOptimizer = BinarySearchCameraOptimizer.create({ width: this.width, height: this.height }, minSize, maxSize * maxSize);
        const desiredState = [...camera.position.toArray(), ...center.toArray()];
        const infinity = 1e100;
        const width = this.width;
        const height = this.height;
        const { state: targetState, distance } = cameraOptimizer.optimizeCameraPosition(camera, desiredState, nodeClone, [
            { x: -infinity, width: infinity * 2 + width, y: -infinity, height: infinity },
            { x: -infinity, width: infinity * 2 + width, y: height - this.padding2D.bottom, height: height + infinity },
            { x: -infinity, width: infinity, y: 0, height: height },
            { x: width, width: width + infinity, y: 0, height: height },
        ]);
        return {
            position: new Vector3(targetState[0], targetState[1], targetState[2]),
            lookAt: new Vector3(targetState[3], targetState[4], targetState[5]),
            far: Math.max(distance * distance, 100),
        };
    }
    loadModel(url, onProgress) {
        const loader = this.createGltfLoader();
        return new Promise((resolve, reject) => {
            loader.load(url, (gltf) => {
                resolve(gltf);
            }, onProgress, (error) => {
                reject(error);
            });
        });
    }
    loadEnvTexture(url, onProgress) {
        const loader = new RGBELoader();
        return new Promise((resolve, reject) => {
            loader.setDataType(HalfFloatType).load(url, (texture) => {
                resolve(texture);
            }, onProgress, (error) => {
                reject(error);
            });
        });
    }
    getCameraPosition(cameraPosition) {
        switch (cameraPosition) {
            case 'perspectiveRight':
                return sphericalToVector3$1({ polarDegrees: 72.5, azimuthDegrees: 36.5 });
            case 'perspectiveLeft': {
                return sphericalToVector3$1({ polarDegrees: 72.5, azimuthDegrees: -36.5 });
            }
            default:
                return sphericalToVector3$1(cameraPosition);
        }
    }
    createGltfLoader() {
        var _a, _b, _c;
        const loader = new GLTFLoader$1();
        if ((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.dracoLoader) === null || _b === void 0 ? void 0 : _b.enabled) {
            const dracoLoader = new DRACOLoader$1();
            if ((_c = this.config) === null || _c === void 0 ? void 0 : _c.dracoLoader.decoderPath) {
                dracoLoader.setDecoderPath(this.config.dracoLoader.decoderPath);
            }
            loader.setDRACOLoader(dracoLoader);
        }
        return loader;
    }
}
function sphericalToVector3$1(position) {
    const { azimuthDegrees, polarDegrees } = position;
    const globalAzimuth = Math.PI / 2 - (azimuthDegrees * Math.PI) / 180;
    const globalPolar = (polarDegrees * Math.PI) / 180;
    const x = Math.sin(globalPolar) * Math.cos(globalAzimuth);
    const z = Math.sin(globalPolar) * Math.sin(globalAzimuth);
    const y = Math.cos(globalPolar);
    return new Vector3(x, y, z);
}

const PERSPECTIVE_RIGHT = {
    polarDegrees: 72.5,
    azimuthDegrees: 36.5,
};
const PERSPECTIVE_LEFT = {
    polarDegrees: 72.5,
    azimuthDegrees: -36.5,
};
const NEAR_ZERO_DISTANCE = 1e-6;
class CameraFocusController {
    constructor(cameraManager, getSize) {
        this.cameraManager = cameraManager;
        this.getSize = getSize;
        this.savedNear = null;
        this.savedFar = null;
    }
    focusOn(target, options = {}) {
        var _a, _b, _c;
        const camera = this.cameraManager.camera;
        if (!(camera instanceof PerspectiveCamera)) {
            return;
        }
        if (target instanceof Vector3) {
            this.cameraManager.updateControlsTarget(target.clone());
            return;
        }
        target.updateWorldMatrix(true, true);
        const box = new Box3().setFromObject(target);
        if (box.isEmpty()) {
            this.cameraManager.updateControlsTarget(target.getWorldPosition(new Vector3()));
            return;
        }
        const sphere = box.getBoundingSphere(new Sphere());
        const center = sphere.center.clone();
        if (!((_a = options.fit) !== null && _a !== void 0 ? _a : true)) {
            this.cameraManager.updateControlsTarget(center);
            return;
        }
        this.rememberDefaults(camera);
        const fov = MathUtils.degToRad(camera.fov);
        const margin = (_b = options.margin) !== null && _b !== void 0 ? _b : 1.2;
        const fitHeightDistance = sphere.radius / Math.sin(fov / 2);
        const fitWidthDistance = fitHeightDistance / camera.aspect;
        const distance = margin * Math.max(fitHeightDistance, fitWidthDistance);
        const direction = directionVector((_c = options.cameraPosition) !== null && _c !== void 0 ? _c : 'perspectiveRight');
        camera.position.copy(center).addScaledVector(direction, distance);
        camera.near = clamp(distance / 100, 0.001, 0.05);
        camera.far = Math.max(distance * 4, 100);
        camera.updateProjectionMatrix();
        this.cameraManager.updateControlsTarget(center);
        this.cameraManager.setDistanceLimits(Math.max(0.001, sphere.radius * 0.5), distance * 4);
    }
    orient(target, options) {
        const camera = this.cameraManager.camera;
        if (!(camera instanceof PerspectiveCamera)) {
            return;
        }
        const { center, radius } = this.resolveFocusPoint(target);
        const direction = directionVector(options.cameraPosition);
        let distance = camera.position.distanceTo(center);
        if (distance <= NEAR_ZERO_DISTANCE) {
            distance = fitSphereDistance(camera, radius);
        }
        camera.position.copy(center).addScaledVector(direction, distance);
        this.cameraManager.updateControlsTarget(center);
        camera.lookAt(center);
    }
    fitDistance(target, options = {}) {
        var _a;
        const camera = this.cameraManager.camera;
        if (!(camera instanceof PerspectiveCamera)) {
            return;
        }
        target.updateWorldMatrix(true, true);
        const box = new Box3().setFromObject(target);
        if (box.isEmpty()) {
            return;
        }
        const center = box.getCenter(new Vector3());
        const size = box.getSize(new Vector3());
        const sphereRadius = box.getBoundingSphere(new Sphere()).radius;
        const dir = camera.getWorldDirection(new Vector3()).negate();
        const desiredState = [...center.clone().add(dir).toArray(), ...center.toArray()];
        const measurable = this.toWorldFrameMeasurable(target);
        const { width, height } = this.getSize();
        const maxSize = Math.max(size.x, size.y, size.z, 100);
        const optimizer = BinarySearchCameraOptimizer.create({ width, height }, 0.01, maxSize * maxSize);
        const padding = Object.assign(Object.assign({}, emptyPadding), options.padding2D);
        const { distance: fitted } = optimizer.optimizeCameraPosition(camera, desiredState, measurable, spillBoxes(width, height, padding));
        const distance = fitted * ((_a = options.margin) !== null && _a !== void 0 ? _a : 1);
        this.rememberDefaults(camera);
        camera.position.copy(center).addScaledVector(dir, distance);
        camera.near = clamp(distance / 100, 0.001, 0.05);
        camera.far = Math.max(distance * 4, 100);
        camera.updateProjectionMatrix();
        this.cameraManager.updateControlsTarget(center);
        camera.lookAt(center);
        this.cameraManager.setDistanceLimits(Math.max(0.001, sphereRadius * 0.5), distance * 4);
    }
    reset() {
        const camera = this.cameraManager.camera;
        if (camera instanceof PerspectiveCamera) {
            if (this.savedNear !== null) {
                camera.near = this.savedNear;
            }
            if (this.savedFar !== null) {
                camera.far = this.savedFar;
            }
            camera.updateProjectionMatrix();
        }
        this.savedNear = null;
        this.savedFar = null;
        this.cameraManager.resetDistanceLimits();
    }
    rememberDefaults(camera) {
        if (this.savedNear === null) {
            this.savedNear = camera.near;
        }
        if (this.savedFar === null) {
            this.savedFar = camera.far;
        }
    }
    resolveFocusPoint(target) {
        if (target instanceof Vector3) {
            return { center: target.clone(), radius: 0 };
        }
        target.updateWorldMatrix(true, true);
        const box = new Box3().setFromObject(target);
        if (box.isEmpty()) {
            return { center: target.getWorldPosition(new Vector3()), radius: 0 };
        }
        const sphere = box.getBoundingSphere(new Sphere());
        return { center: sphere.center.clone(), radius: sphere.radius };
    }
    toWorldFrameMeasurable(target) {
        const clone = getMeasurableObjects(target);
        clone.matrix.copy(target.matrixWorld);
        clone.matrixAutoUpdate = false;
        clone.updateMatrixWorld(true);
        return clone;
    }
}
function fitSphereDistance(camera, radius) {
    const fov = MathUtils.degToRad(camera.fov);
    const fitHeightDistance = radius / Math.sin(fov / 2);
    const fitWidthDistance = fitHeightDistance / camera.aspect;
    return Math.max(fitHeightDistance, fitWidthDistance);
}
function spillBoxes(width, height, padding) {
    const infinity = 1e100;
    return [
        { x: -infinity, width: infinity * 2 + width, y: -infinity, height: infinity + padding.top },
        { x: -infinity, width: infinity * 2 + width, y: height - padding.bottom, height: height + infinity },
        { x: -infinity, width: infinity + padding.left, y: 0, height },
        { x: width - padding.right, width: width + infinity, y: 0, height },
    ];
}
function directionVector(position) {
    switch (position) {
        case 'perspectiveRight':
            return sphericalToVector3(PERSPECTIVE_RIGHT);
        case 'perspectiveLeft':
            return sphericalToVector3(PERSPECTIVE_LEFT);
        default:
            return sphericalToVector3(position);
    }
}
function sphericalToVector3({ azimuthDegrees, polarDegrees }) {
    const globalAzimuth = Math.PI / 2 - MathUtils.degToRad(azimuthDegrees);
    const globalPolar = MathUtils.degToRad(polarDegrees);
    const x = Math.sin(globalPolar) * Math.cos(globalAzimuth);
    const z = Math.sin(globalPolar) * Math.sin(globalAzimuth);
    const y = Math.cos(globalPolar);
    return new Vector3(x, y, z);
}
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

const TEXT_SIZE = '14px';
const TEXT_COLOR = 'rgb(255,255,255)';
const BACKGROUND_COLOR = 'rgb(69,69,69)';
const BORDER_RADIUS = '15px';
const PADDING = '9px 13px 7px 13px';
const LABEL_OFFSET = 0.07;
const MAIN_LINE_COLOR = 0x454545;
const LINE_COLOR = 0x7b7c7c;
const LINE_OFFSET = 0.1;
const ARROW_OFFSET_1 = 0.02;
const ARROW_OFFSET_2 = 0.09;
const ARROW_OFFSET_3 = 0.11;
const WIDTH_ANGLE_RANGE = { start: 95, end: 265 };
const DEPTH_ANGLE_RANGE = { start: 10, end: 190 };
const HEIGHT_ANGLE_RANGE = { start: 175, end: 285 };
class DimensionsRenderer {
    constructor(width, height, dimensionOptions = { unitSystem: 'metric', region: 'DE' }) {
        this.dimensionOptions = dimensionOptions;
        this.width = true;
        this.depth = true;
        this.height = true;
        this.dimensionsRenderer2D = new CSS2DRenderer();
        this.dimensionsRenderer2D.setSize(width, height);
        this.dimensionsRenderer2D.domElement.style.position = 'absolute';
        this.dimensionsRenderer2D.domElement.style.top = '0px';
        this.dimensionsRenderer2D.domElement.style.transition = 'all .15s';
        this.dimensionsRenderer2D.domElement.style.pointerEvents = 'none';
        if (document.getElementById('viewer')) {
            document.getElementById('viewer').appendChild(this.dimensionsRenderer2D.domElement);
        }
    }
    setDimensionOptions(options) {
        this.dimensionOptions = options;
    }
    render(dimensionScene2D, dimensionLinesNode, measurableObject, camera, angle, showRuler) {
        if (showRuler) {
            const angleInDegrees = MathUtils.radToDeg(angle) < 0 ? 180 + (180 + MathUtils.radToDeg(angle)) : MathUtils.radToDeg(angle);
            this.width = this.rebuildRuler(angleInDegrees, WIDTH_ANGLE_RANGE.start, WIDTH_ANGLE_RANGE.end, dimensionScene2D, dimensionLinesNode, measurableObject, this.width);
            this.depth = this.rebuildRuler(angleInDegrees, DEPTH_ANGLE_RANGE.start, DEPTH_ANGLE_RANGE.end, dimensionScene2D, dimensionLinesNode, measurableObject, this.depth);
            this.height = this.rebuildRuler(angleInDegrees, HEIGHT_ANGLE_RANGE.start, HEIGHT_ANGLE_RANGE.end, dimensionScene2D, dimensionLinesNode, measurableObject, this.height);
            this.dimensionsRenderer2D.render(dimensionScene2D, camera);
        }
    }
    rebuildRuler(angle, startAngle, endAngle, dimensionScene2D, dimensionLinesNode, measurableObject, rebuildFlag) {
        if (rebuildFlag && angle > startAngle && angle < endAngle) {
            rebuildFlag = false;
            this.clearScene(dimensionScene2D, dimensionLinesNode);
            this.buildRuler(dimensionScene2D, dimensionLinesNode, measurableObject, angle);
        }
        else {
            if (!rebuildFlag && !(angle > startAngle && angle < endAngle)) {
                rebuildFlag = true;
                this.clearScene(dimensionScene2D, dimensionLinesNode);
                this.buildRuler(dimensionScene2D, dimensionLinesNode, measurableObject, angle);
            }
        }
        return rebuildFlag;
    }
    clearScene(dimensionScene2D, dimensionLinesNode) {
        dimensionScene2D.remove(...dimensionScene2D.children);
        dimensionLinesNode.remove(...dimensionLinesNode.children);
    }
    buildRuler(dimensionScene2D, dimensionLinesNode, root, angle = -1) {
        this.clearScene(dimensionScene2D, dimensionLinesNode);
        const sceneBox = new Box3();
        const sceneBoxSize = new Vector3();
        const sceneBoxCenter = new Vector3();
        const measurableObject = getMeasurableObjects(root);
        if (measurableObject.children.length > 0) {
            sceneBox.setFromObject(measurableObject);
            sceneBox.getSize(sceneBoxSize);
            sceneBox.getCenter(sceneBoxCenter);
            const widthFlip = angle > WIDTH_ANGLE_RANGE.start && angle < WIDTH_ANGLE_RANGE.end;
            const widthLabelPosition = new Vector3(sceneBoxCenter.x, sceneBox.min.y + LABEL_OFFSET, widthFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET);
            const widthLineStart = [
                sceneBox.min.x,
                sceneBox.min.y,
                widthFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET,
            ];
            const widthLineEnd = [
                sceneBox.max.x,
                sceneBox.min.y,
                widthFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET,
            ];
            const widthMinX = sceneBox.min.x + ARROW_OFFSET_1;
            const widthMaxX = sceneBox.max.x - ARROW_OFFSET_1;
            this.add2DLabel(widthLabelPosition, this.formatValue(sceneBoxSize.x), dimensionScene2D);
            this.add3DLine(widthLineStart, widthLineEnd, 1, dimensionLinesNode, MAIN_LINE_COLOR);
            this.add3DLine(widthLineStart, [widthMinX, sceneBox.min.y, widthFlip ? sceneBox.min.z - ARROW_OFFSET_2 : sceneBox.max.z + ARROW_OFFSET_2], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(widthLineStart, [widthMinX, sceneBox.min.y, widthFlip ? sceneBox.min.z - ARROW_OFFSET_3 : sceneBox.max.z + ARROW_OFFSET_3], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(widthLineEnd, [widthMaxX, sceneBox.min.y, widthFlip ? sceneBox.min.z - ARROW_OFFSET_2 : sceneBox.max.z + ARROW_OFFSET_2], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(widthLineEnd, [widthMaxX, sceneBox.min.y, widthFlip ? sceneBox.min.z - ARROW_OFFSET_3 : sceneBox.max.z + ARROW_OFFSET_3], 1.2, dimensionLinesNode, LINE_COLOR);
            const depthFlip = angle > DEPTH_ANGLE_RANGE.start && angle < DEPTH_ANGLE_RANGE.end;
            const depthLabelPosition = new Vector3(depthFlip ? sceneBox.max.x + LINE_OFFSET : sceneBox.min.x - LINE_OFFSET, sceneBox.min.y + LABEL_OFFSET, sceneBoxCenter.z);
            const depthLineStart = [
                depthFlip ? sceneBox.max.x + LINE_OFFSET : sceneBox.min.x - LINE_OFFSET,
                sceneBox.min.y,
                sceneBox.max.z,
            ];
            const depthLineEnd = [
                depthFlip ? sceneBox.max.x + LINE_OFFSET : sceneBox.min.x - LINE_OFFSET,
                sceneBox.min.y,
                sceneBox.min.z,
            ];
            const depthMinZ = sceneBox.min.z + ARROW_OFFSET_1;
            const depthMaxZ = sceneBox.max.z - ARROW_OFFSET_1;
            this.add2DLabel(depthLabelPosition, this.formatValue(sceneBoxSize.z), dimensionScene2D);
            this.add3DLine(depthLineStart, depthLineEnd, 1, dimensionLinesNode, MAIN_LINE_COLOR);
            this.add3DLine(depthLineEnd, [depthFlip ? sceneBox.max.x + ARROW_OFFSET_2 : sceneBox.min.x - ARROW_OFFSET_2, sceneBox.min.y, depthMinZ], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(depthLineEnd, [depthFlip ? sceneBox.max.x + ARROW_OFFSET_3 : sceneBox.min.x - ARROW_OFFSET_3, sceneBox.min.y, depthMinZ], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(depthLineStart, [depthFlip ? sceneBox.max.x + ARROW_OFFSET_2 : sceneBox.min.x - ARROW_OFFSET_2, sceneBox.min.y, depthMaxZ], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(depthLineStart, [depthFlip ? sceneBox.max.x + ARROW_OFFSET_3 : sceneBox.min.x - ARROW_OFFSET_3, sceneBox.min.y, depthMaxZ], 1.2, dimensionLinesNode, LINE_COLOR);
            const heightFlip = angle > HEIGHT_ANGLE_RANGE.start && angle < HEIGHT_ANGLE_RANGE.end;
            const heightLabelPosition = new Vector3(heightFlip ? sceneBox.min.x : sceneBox.max.x, sceneBoxCenter.y, heightFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET);
            const heightLineStart = [
                heightFlip ? sceneBox.min.x - LINE_OFFSET : sceneBox.max.x + LINE_OFFSET,
                sceneBox.min.y,
                heightFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET,
            ];
            const heightLineEnd = [
                heightFlip ? sceneBox.min.x - LINE_OFFSET : sceneBox.max.x + LINE_OFFSET,
                sceneBox.max.y,
                heightFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET,
            ];
            const heightX = heightFlip ? sceneBox.min.x - LINE_OFFSET : sceneBox.max.x + LINE_OFFSET;
            const heightMinY = sceneBox.min.y + ARROW_OFFSET_1;
            const heightMaxY = sceneBox.max.y - ARROW_OFFSET_1;
            this.add2DLabel(heightLabelPosition, this.formatValue(sceneBoxSize.y), dimensionScene2D, -90);
            this.add3DLine(heightLineStart, heightLineEnd, 1, dimensionLinesNode, MAIN_LINE_COLOR);
            this.add3DLine(heightLineStart, [heightX, heightMinY, heightFlip ? sceneBox.min.z - ARROW_OFFSET_2 : sceneBox.max.z + ARROW_OFFSET_2], 1, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(heightLineStart, [heightX, heightMinY, heightFlip ? sceneBox.min.z - ARROW_OFFSET_3 : sceneBox.max.z + ARROW_OFFSET_3], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(heightLineEnd, [heightX, heightMaxY, heightFlip ? sceneBox.min.z - ARROW_OFFSET_2 : sceneBox.max.z + ARROW_OFFSET_2], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(heightLineEnd, [heightX, heightMaxY, heightFlip ? sceneBox.min.z - ARROW_OFFSET_3 : sceneBox.max.z + ARROW_OFFSET_3], 1.2, dimensionLinesNode, LINE_COLOR);
        }
    }
    getMeasurableObject(root) {
        const measurableObject = root.clone(true);
        const toDelete = [];
        measurableObject.traverse((node) => {
            const takePlace = getUserData(node).takePlace;
            if (!takePlace) {
                toDelete.push(node);
            }
        });
        for (const node of toDelete) {
            node.removeFromParent();
        }
        return measurableObject;
    }
    add2DLabel(position, content, dimensionScene2D, rotation = 0) {
        const text = document.createElement('div');
        text.textContent = content;
        text.style.fontSize = TEXT_SIZE;
        text.style.color = TEXT_COLOR;
        text.style.transform = `rotate(${rotation}deg)`;
        text.style.backgroundColor = BACKGROUND_COLOR;
        text.style.borderRadius = BORDER_RADIUS;
        text.style.padding = PADDING;
        const wrap = document.createElement('div');
        wrap.appendChild(text);
        const label = new CSS2DObject(wrap);
        label.position.copy(position);
        dimensionScene2D.add(label);
    }
    add3DLine(from, to, width, dimensionLinesNode, color, opacity = 0.5) {
        const geometry = new LineGeometry().setPositions([...from, ...to]);
        const material = new LineMaterial({ color: color, linewidth: width, transparent: true });
        material.opacity = opacity;
        const line = new Line2(geometry, material);
        dimensionLinesNode.add(line);
    }
    formatValue(sizeMeters) {
        const millimetres = sizeMeters * 1000;
        return formatDimension(millimetres, this.dimensionOptions);
    }
}

function toThreeMatrix(mat) {
    return new Matrix4().fromArray(mat.elements);
}
function jointPointToThreeMatrix(jp) {
    return toThreeMatrix(jointPointToMatrix(jp));
}
function jointPointInverseMatrix(jp) {
    return toThreeMatrix(jointPointToMatrix(jp).invert());
}
function calculateJointTransformThree(parentJp, childJp) {
    return toThreeMatrix(calculateJointTransform(parentJp, childJp));
}

class LayoutManagerV3 {
    constructor(sceneSpecification) {
        this.sceneSpecification = sceneSpecification;
    }
    buildLayout() {
        return computeLayout(this.sceneSpecification);
    }
}
LayoutManagerV3.emptyLayout = {
    notConnectedParts: [],
    containers: [],
};
function computeLayout(spec) {
    if (spec.rootPartId === undefined) {
        return LayoutManagerV3.emptyLayout;
    }
    const transformMap = new Map();
    transformMap.set(spec.rootPartId, new Matrix4());
    const childrenMap = new Map();
    for (const conn of spec.connections) {
        const parentId = conn.parts[0].id;
        if (!childrenMap.has(parentId)) {
            childrenMap.set(parentId, []);
        }
        childrenMap.get(parentId).push(conn);
    }
    const visited = new Set();
    function traverse(parentId) {
        var _a;
        visited.add(parentId);
        const parentMatrix = transformMap.get(parentId);
        const connections = (_a = childrenMap.get(parentId)) !== null && _a !== void 0 ? _a : [];
        for (const conn of connections) {
            const [parentPart, childPart] = conn.parts;
            const jointMat = calculateJointTransformThree(parentPart.jointPoint, childPart.jointPoint);
            const childMatrix = new Matrix4().multiplyMatrices(parentMatrix, jointMat);
            transformMap.set(childPart.id, childMatrix);
            traverse(childPart.id);
        }
    }
    traverse(spec.rootPartId);
    const notConnectedParts = spec.parts.filter((part) => !visited.has(part.id));
    return {
        containers: Array.from(transformMap.entries()).map(([id, transform]) => {
            const part = spec.parts.find((it) => it.id === id);
            if (!part) {
                throw new Error(`Cannot find part ${id}`);
            }
            return { id, transform, fileName: part.fileName };
        }),
        notConnectedParts,
    };
}

function createPartContainerName(partKey) {
    return `$$$<part-container>$$$-${partKey}`;
}
class ConfiguratorView3D {
    constructor(canvas, sceneConfig, gltfExporter, usdzExporter, cameraManager, environment, modelLoader, materialsLibrary, renderer, plugin) {
        this.canvas = canvas;
        this.sceneConfig = sceneConfig;
        this.gltfExporter = gltfExporter;
        this.usdzExporter = usdzExporter;
        this.cameraManager = cameraManager;
        this.environment = environment;
        this.modelLoader = modelLoader;
        this.materialsLibrary = materialsLibrary;
        this.renderer = renderer;
        this.plugin = plugin;
        this.debug = false;
        this.dimensionOptions = { unitSystem: 'metric', region: 'DE' };
        this.disableRerender = false;
        this.showRulerFlag = false;
        this.clock = null;
        this.resizeHandler = () => {
            this.updateSizes();
            this.dimensionsRenderer.dimensionsRenderer2D.setSize(this.width, this.height);
            this.cameraManager.resize(this.width, this.height);
            this.renderer.setSize(this.width, this.height);
        };
        this.width = canvas.offsetWidth;
        this.height = canvas.offsetHeight;
        const params = new URLSearchParams(location.search);
        this.debug = params.get('debug') !== null;
    }
    dispose() {
        if (this.renderer instanceof WebGLRenderer) {
            this.renderer.dispose();
            this.renderer = null;
            this.destroy();
        }
    }
    init(view3dConfiguration) {
        return __awaiter(this, void 0, void 0, function* () {
            this.clock = new Clock();
            this.view3dConfiguration = view3dConfiguration;
            this.initScene();
            this.initLights();
            this.updateSizes();
            this.renderer.setSize(this.width, this.height);
            this.cameraManager.init(view3dConfiguration, this.canvas);
            yield this.environment.init(this.renderer, this.scene);
            this.dimensionsRenderer = new DimensionsRenderer(this.width, this.height, this.dimensionOptions);
            this.initHandlers();
            this.startRendering();
            const getWidth = () => this.width;
            const getHeight = () => this.height;
            const cameraFocus = new CameraFocusController(this.cameraManager, () => ({
                width: this.width,
                height: this.height,
            }));
            this.plugin.init({
                cameraManager: this.cameraManager,
                cameraFocus,
                renderer: this.renderer,
                scene: this.scene,
                rootNode: this.rootNode,
                getWidth,
                getHeight,
            });
        });
    }
    setDimensionOptions(options) {
        var _a;
        this.dimensionOptions = options;
        (_a = this.dimensionsRenderer) === null || _a === void 0 ? void 0 : _a.setDimensionOptions(options);
    }
    showScene(sceneSpecification_1) {
        return __awaiter(this, arguments, void 0, function* (sceneSpecification, _keepCameraPosition = false) {
            yield this.updateMaterials(sceneSpecification.materials);
            const layout = new LayoutManagerV3(sceneSpecification).buildLayout();
            const nodesFactories = yield Promise.all(layout.containers.map((part) => {
                const partSpec = sceneSpecification.parts.find((partSpec) => partSpec.id === part.id);
                if (!partSpec) {
                    throw new Error(`Cannot find part with id "${part.id}"`);
                }
                return this.renderPart(part, partSpec);
            }));
            this.rootNode.children = [];
            for (const nodeFactory of nodesFactories) {
                this.rootNode.add(nodeFactory());
            }
            if (this.showRulerFlag) {
                this.showRuler();
            }
            this.plugin.onUpdateLayout();
        });
    }
    showSceneV2() {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error('Not supported');
        });
    }
    exportGltfScene() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.gltfExporter.export(this.createExportScene());
        });
    }
    exportUsdzScene() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.usdzExporter.export(this.createExportScene());
        });
    }
    destroy() {
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('toggle-fullscreen', this.resizeHandler);
    }
    initLights() {
        const ambientLight = new AmbientLight('#ffffff', 0.3);
        this.scene.add(ambientLight);
    }
    startRendering() {
        requestAnimationFrame(() => {
            var _a, _b;
            this.cameraManager.update();
            const delta = (_b = (_a = this.clock) === null || _a === void 0 ? void 0 : _a.getDelta()) !== null && _b !== void 0 ? _b : 0;
            this.plugin.update(delta);
            if (this.renderer instanceof WebGLRenderer) {
                this.renderer.clear();
                this.renderer.render(this.scene, this.cameraManager.camera);
                if (this.cameraManager && this.rootNode) {
                    this.dimensionsRenderer.render(this.dimensionScene2D, this.dimensionLinesNode, this.rootNode, this.cameraManager.camera, this.cameraManager.getControlsAzimuthalAngle(), this.showRulerFlag);
                }
                this.plugin.render();
                this.startRendering();
            }
        });
    }
    initScene() {
        this.scene = new Scene();
        this.sceneNode = new Object3D();
        this.sceneNode.name = 'Scene';
        this.scene.add(this.sceneNode);
        this.dimensionScene2D = new Scene();
        this.rootNode = new Object3D();
        this.rootNode.name = 'Root';
        this.sceneNode.add(this.rootNode);
        this.dimensionLinesNode = new Object3D();
        this.dimensionLinesNode.name = 'Ruler';
        this.scene.add(this.dimensionLinesNode);
        if (this.debug) {
            const axesHelper = new AxesHelper(1);
            this.scene.add(axesHelper);
        }
        this.applyConfig();
    }
    resize() {
        this.resizeHandler();
    }
    initHandlers() {
        window.addEventListener('resize', this.resizeHandler);
        window.addEventListener('toggle-fullscreen', this.resizeHandler);
    }
    renderPart(containerPart, partSpecification) {
        return __awaiter(this, void 0, void 0, function* () {
            let model = null;
            if (partSpecification.fileName) {
                const template = yield this.modelLoader.load(partSpecification.fileName);
                model = template.clone();
                model.visible = partSpecification.visible;
                setUserData(model, { takePlace: false, webAr: { visible: true } });
                if (model.visible) {
                    changeUserData(model, { takePlace: partSpecification.takePlace });
                    for (const material of partSpecification.materials) {
                        const modelMesh = model.getObjectByName(material.mesh);
                        if (!(modelMesh instanceof Mesh)) {
                            console.warn(`Cannot find mesh "${material.mesh}" in model "${partSpecification.fileName}"`);
                            continue;
                        }
                        const modelMaterial = yield this.materialsLibrary.get(material.materialId);
                        if (!modelMaterial) {
                            console.warn(`Cannot find material "${material.materialId}" in library`);
                            continue;
                        }
                        if (modelMaterial instanceof MeshStandardMaterial && modelMaterial.envMapIntensity != 1) {
                            modelMaterial.envMap = this.scene.environment;
                        }
                        modelMesh.material = modelMaterial;
                    }
                    for (const { mesh, visible, webAr } of partSpecification.meshes) {
                        let found = false;
                        model.traverse((node) => {
                            if (node.name === mesh) {
                                found = true;
                                node.visible = visible;
                                changeUserData(node, { webAr });
                            }
                        });
                        if (!found) {
                            console.warn(`Cannot find mesh or group "${mesh}" in model "${partSpecification.fileName}"`);
                        }
                    }
                }
            }
            const outer = new Object3D();
            outer.applyMatrix4(containerPart.transform);
            return () => {
                outer.name = createPartContainerName(containerPart.id);
                if (model) {
                    outer.add(model);
                }
                return outer;
            };
        });
    }
    updateSizes() {
        const parent = this.canvas.parentElement;
        if (parent) {
            this.height = parent.clientHeight;
            this.width = parent.clientWidth;
        }
        else {
            console.error('No parent for canvas found');
        }
    }
    hideRuler() {
        this.showRulerFlag = false;
        this.dimensionsRenderer.clearScene(this.dimensionScene2D, this.dimensionLinesNode);
    }
    showRuler() {
        this.showRulerFlag = true;
        this.dimensionsRenderer.buildRuler(this.dimensionScene2D, this.dimensionLinesNode, this.rootNode, this.cameraManager.getControlsAzimuthalAngle());
    }
    createExportScene() {
        const sceneCopy = this.sceneNode.clone(true);
        const meshesToRemove = [];
        sceneCopy.traverse((node) => {
            const userData = getUserData(node);
            if (hasUserData(node) && !userData.webAr.visible) {
                meshesToRemove.push(node);
            }
            else {
                node.visible = true;
            }
        });
        for (const mesh of meshesToRemove) {
            mesh.removeFromParent();
        }
        return sceneCopy;
    }
    updateMaterials(materials) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.all(materials.map((material) => this.materialsLibrary.updateMaterial(material)));
        });
    }
    createScreenshot(width, height, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const cameraPosition = (_a = options === null || options === void 0 ? void 0 : options.cameraPosition) !== null && _a !== void 0 ? _a : this.view3dConfiguration.pdfCameraPosition;
            const glbConverter = new GlbConverter(width, height);
            const container = this.sceneNode.clone(true);
            return yield glbConverter.convertObject3DToBase64(container, this.view3dConfiguration.hdr, {
                cameraPosition,
            });
        });
    }
    applyConfig() {
        const sceneConfig = this.sceneConfig.scene;
        if (sceneConfig) {
            if (sceneConfig.environmentIntensity) {
                this.scene.environmentIntensity = sceneConfig.environmentIntensity;
            }
        }
    }
}

class JointManagerView3D {
    constructor(canvas, cameraManager, environment, modelLoader, renderer) {
        this.canvas = canvas;
        this.cameraManager = cameraManager;
        this.environment = environment;
        this.modelLoader = modelLoader;
        this.renderer = renderer;
        this.debug = false;
        this.disableRerender = false;
        this.boundsHelper = null;
        this.onJointPointChanged = () => { };
        this.resizeHandler = () => {
            this.updateSizes();
            this.cameraManager.resize(this.width, this.height);
            this.renderer.setSize(this.width, this.height);
        };
        this.handleTransformControlDragging = (event) => {
            this.cameraManager.enabled = !event.value;
        };
        this.handleTransformControlChange = () => {
            const position = this.jointPointNode.position.clone();
            const rotation = this.jointPointNode.rotation.clone();
            const jp = Object.assign(Object.assign({}, this.jointPointNode.userData), { jointPoint: {
                    position,
                    rotation,
                } });
            this.onJointPointChanged(jp);
        };
        this.width = canvas.offsetWidth;
        this.height = canvas.offsetHeight;
        const params = new URLSearchParams(location.search);
        this.debug = params.get('debug') !== null;
    }
    setConfiguration(configuration) {
        this.configuration = configuration;
    }
    getConfiguration() {
        return this.configuration;
    }
    setMode(mode) {
        this.control.setMode(mode);
    }
    jointPointChanged(callback) {
        this.onJointPointChanged = callback;
    }
    setBounds(bounds) {
        if (this.boundsHelper) {
            this.boundsHelper.removeFromParent();
            this.boundsHelper.dispose();
            this.boundsHelper = null;
        }
        if (!bounds) {
            return;
        }
        const box = new Box3(new Vector3(bounds.min.x, bounds.min.y, bounds.min.z), new Vector3(bounds.max.x, bounds.max.y, bounds.max.z));
        this.boundsHelper = new Box3Helper(box, new Color('#1677ff'));
        this.rootNode.add(this.boundsHelper);
    }
    computeBounds() {
        return __awaiter(this, void 0, void 0, function* () {
            const model = yield this.modelLoaded;
            return computeAABB(model);
        });
    }
    setActiveJointPoint(jointPoint) {
        const { name, jointPoint: { rotation }, } = jointPoint;
        if (jointPoint.jointPoint.direction) {
            const euler = this.getRotation({
                direction: jointPoint.jointPoint.direction,
                rotation: jointPoint.jointPoint.direction,
            });
            this.jointPointNode.rotation.copy(euler);
        }
        else {
            this.jointPointNode.rotation.set(rotation.x, rotation.y, rotation.z);
        }
        const dirArrow = new ArrowHelper(new Vector3(0, 1, 0), new Vector3(), 0.3, '#009688');
        const rotArrow = new ArrowHelper(new Vector3(1, 0, 0), new Vector3(), 0.3, '#af0606');
        const sphereGeometry = new SphereGeometry(0.01);
        const sphere = new Mesh(sphereGeometry, new MeshBasicMaterial({ color: 'red' }));
        this.jointPointNode.name = name;
        this.jointPointNode.userData = jointPoint;
        this.jointPointNode.add(rotArrow, dirArrow, sphere);
        this.jointPointNode.position.copy(jointPoint.jointPoint.position);
        this.control.enabled = true;
        this.control.getHelper().visible = true;
    }
    dispose() {
        if (this.renderer instanceof WebGLRenderer) {
            this.renderer.dispose();
            this.renderer = null;
            this.destroy();
        }
    }
    getRotation(params) {
        const { direction, rotation } = params;
        const dirVector = new Vector3().copy(direction).normalize();
        const rotVector = new Vector3().copy(rotation).normalize();
        const zz = new Vector3().crossVectors(rotVector.normalize(), dirVector.normalize());
        const m = new Matrix4().makeBasis(rotVector, dirVector, zz);
        const r = new Matrix4();
        r.extractRotation(m);
        return new Euler().setFromRotationMatrix(r, 'XYZ');
    }
    init(configuration) {
        return __awaiter(this, void 0, void 0, function* () {
            this.configuration = configuration;
            this.cameraManager.init({
                camera: {
                    minDistance: 1,
                    maxDistance: 10,
                    maxPolarAngle: 180,
                    position: { x: 0.2, y: 2, z: 2 },
                    lookAt: { x: 0, y: 0, z: 0 },
                    enablePan: true,
                },
            }, this.canvas);
            this.initScene();
            this.initLights();
            this.updateSizes();
            this.renderer.setSize(this.width, this.height);
            this.initHandlers();
            this.startRendering();
        });
    }
    destroy() {
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('toggle-fullscreen', this.resizeHandler);
        this.control.removeEventListener('dragging-changed', this.handleTransformControlDragging);
        this.control.removeEventListener('change', this.handleTransformControlChange);
    }
    initLights() {
        const ambientLight = new AmbientLight('#ffffff', 0.3);
        this.scene.add(ambientLight);
    }
    startRendering() {
        requestAnimationFrame(() => {
            this.cameraManager.update();
            if (this.renderer) {
                this.renderer.render(this.scene, this.cameraManager.camera);
                this.startRendering();
            }
        });
    }
    initScene() {
        this.scene = new Scene();
        this.sceneNode = new Object3D();
        this.sceneNode.name = 'Scene';
        this.scene.background = new Color(0xeeeeee);
        const pMREMGenerator = new PMREMGenerator(this.renderer);
        pMREMGenerator.compileEquirectangularShader();
        new RGBELoader$1().setDataType(HalfFloatType).load(this.configuration.hdr, (texture) => {
            this.scene.environment = pMREMGenerator.fromEquirectangular(texture).texture;
            texture.dispose();
            pMREMGenerator.dispose();
        }, () => { }, (e) => console.error(e));
        this.scene.add(this.sceneNode);
        this.rootNode = new Object3D();
        this.rootNode.name = 'Root';
        this.sceneNode.add(this.rootNode);
        if (this.debug) {
            const axesHelper = new AxesHelper(1);
            this.scene.add(axesHelper);
        }
        this.loadView();
        this.createTransformControl();
    }
    initHandlers() {
        window.addEventListener('resize', this.resizeHandler);
        window.addEventListener('toggle-fullscreen', this.resizeHandler);
    }
    loadView() {
        return __awaiter(this, void 0, void 0, function* () {
            const modelPromise = this.modelLoader.load(this.configuration.model3d.url);
            this.modelLoaded = modelPromise;
            const model = yield modelPromise;
            this.rootNode.add(model);
            const glbConverter = new GlbConverter(this.width, this.height);
            const { position, lookAt } = glbConverter.getCameraParams(this.cameraManager.camera, this.rootNode, 'perspectiveRight');
            this.cameraManager.camera.position.copy(position);
            this.cameraManager.updateControlsTarget(lookAt);
        });
    }
    updateSizes() {
        const parent = this.canvas.parentElement;
        if (parent) {
            this.height = parent.clientHeight;
            this.width = parent.clientWidth;
        }
        else {
            console.error('No parent for canvas found');
        }
    }
    createTransformControl() {
        this.jointPointNode = new Object3D();
        this.jointPointNode.name = '';
        this.control = new TransformControls(this.cameraManager.camera, this.renderer.domElement);
        this.control.enabled = false;
        this.control.attach(this.jointPointNode);
        this.control.addEventListener('dragging-changed', this.handleTransformControlDragging);
        this.control.addEventListener('change', this.handleTransformControlChange);
        const gizmo = this.control.getHelper();
        this.rootNode.add(this.jointPointNode);
        this.scene.add(gizmo);
        this.control.getHelper().visible = false;
    }
}

const MATERIAL_MAP_NAMES = [
    'map',
    'metalnessMap',
    'roughnessMap',
    'alphaMap',
    'aoMap',
    'bumpMap',
    'displacementMap',
    'emissiveMap',
    'envMap',
    'lightMap',
    'normalMap',
];
const STANDARD_MATERIAL_OPTIONS = ['roughness', 'color', 'metalness', 'envMapIntensity', ...MATERIAL_MAP_NAMES];
const PHYSICAL_MATERIAL_OPTIONS = ['clearcoat', 'clearcoatRoughness', ...STANDARD_MATERIAL_OPTIONS];
class MaterialsLibrary {
    constructor(materialLoader, textureLibrary) {
        this.materialLoader = materialLoader;
        this.textureLibrary = textureLibrary;
        this.specsById = {};
        this.materialsById = {};
    }
    addMaterials(materialAssets) {
        for (const materialAsset of materialAssets) {
            this.specsById[materialAsset.id] = materialAsset;
        }
    }
    get(id) {
        return __awaiter(this, void 0, void 0, function* () {
            if (id in this.materialsById) {
                return this.materialsById[id];
            }
            const spec = this.specsById[id];
            if (!spec) {
                throw new Error(`Cannot find material "${id}" in a library`);
            }
            yield this.createMaterial(spec);
            return this.materialsById[id];
        });
    }
    createMaterial(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            let material;
            switch (spec.type) {
                case 'meshMaterialAsset':
                    material = yield this.createMeshMaterial(spec);
                    break;
                case 'shaderMaterialAsset':
                    material = yield this.createShaderMaterial(spec);
                    break;
                case 'standardMaterialAsset':
                    material = yield this.createStandardMaterial(spec);
                    break;
                case 'physicalMaterialAsset':
                    material = yield this.createPhysicalMaterial(spec);
                    break;
                default:
                    assertUnreachable(spec, 'Invalid spec');
            }
            if (material === undefined) {
                throw new Error(`Cannot find material "${spec.id}"`);
            }
            this.materialsById[spec.id] = material;
        });
    }
    createMeshMaterial(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.materialLoader.get(spec);
        });
    }
    createShaderMaterial(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            const specUniforms = Object.entries(spec.uniforms);
            const uniforms = {};
            yield Promise.all(specUniforms.map((_a) => __awaiter(this, [_a], void 0, function* ([textureName, textureId]) {
                uniforms[textureName] = { value: yield this.textureLibrary.get(textureId) };
            })));
            const material = new ShaderMaterial({
                fragmentShader: spec.fragmentShader,
                vertexShader: spec.vertexShader,
                transparent: spec.transparent,
                uniforms,
            });
            material.needsUpdate = true;
            material.name = spec.id;
            return material;
        });
    }
    createStandardMaterial(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            const maps = yield this.loadTexturesMaps(spec);
            const material = new MeshStandardMaterial(Object.assign({ color: spec.color, roughness: spec.roughness, metalness: spec.metalness, envMapIntensity: spec.envMapIntensity }, maps));
            return material;
        });
    }
    loadTexturesMaps(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            const mapKeys = [];
            const mapValues = [];
            for (const mapName of MATERIAL_MAP_NAMES) {
                if (spec[mapName]) {
                    mapKeys.push(mapName);
                    mapValues.push(this.textureLibrary.get(spec[mapName]));
                }
            }
            const values = yield Promise.all(mapValues);
            const result = {};
            for (let i = 0; i < values.length; i++) {
                const mapName = mapKeys[i];
                const value = values[i];
                if (!value) {
                    throw new Error(`Cannot load texture for ${mapName} with id ${spec[mapName]}`);
                }
                result[mapName] = value;
            }
            return result;
        });
    }
    updateMaterial(materialSpec) {
        return __awaiter(this, void 0, void 0, function* () {
            const material = yield this.get(materialSpec.id);
            const type = materialSpec.type;
            switch (type) {
                case 'standardMaterialAsset':
                    if (!(material instanceof MeshStandardMaterial)) {
                        throw new Error(`Invalid material specification for ${materialSpec.id}`);
                    }
                    yield this.updateMaterialProperties(materialSpec, material, STANDARD_MATERIAL_OPTIONS);
                    break;
                case 'physicalMaterialAsset':
                    if (!(material instanceof MeshPhysicalMaterial)) {
                        throw new Error(`Invalid material specification for ${materialSpec.id}`);
                    }
                    yield this.updateMaterialProperties(materialSpec, material, PHYSICAL_MATERIAL_OPTIONS);
                    break;
                case 'meshMaterialAsset':
                case 'shaderMaterialAsset':
                    console.warn(`Material changes for ${type} is not supported yet`);
                    break;
                default:
                    assertUnreachable(type, 'Invalid material type');
            }
        });
    }
    updateMaterialProperties(materialSpec, material, properties) {
        return __awaiter(this, void 0, void 0, function* () {
            const maps = yield this.loadTexturesMaps(materialSpec);
            for (const optionName of properties) {
                let optionValue = materialSpec[optionName];
                if (optionValue !== undefined) {
                    if (!MATERIAL_MAP_NAMES.includes(optionName)) {
                        if (optionName === 'color') {
                            optionValue = new Color(optionValue);
                        }
                        material[optionName] = optionValue;
                    }
                }
            }
            for (const [key, value] of Object.entries(maps)) {
                value.flipY = false;
                material[key] = value;
            }
        });
    }
    createPhysicalMaterial(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            const maps = yield this.loadTexturesMaps(spec);
            const material = new MeshPhysicalMaterial(Object.assign({ color: spec.color, roughness: spec.roughness, metalness: spec.metalness }, maps));
            return material;
        });
    }
}

class TextureLibrary {
    constructor(textureLoader) {
        this.textureLoader = textureLoader;
        this.texturesById = {};
        this.specsById = {};
    }
    get(id) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.texturesById[id]) {
                return this.texturesById[id];
            }
            const spec = this.specsById[id];
            if (!spec) {
                throw new Error(`Cannot find texture "${id}" in a library`);
            }
            const texture = yield this.textureLoader.load(spec.file);
            if (!texture) {
                throw new Error(`Cannot find texture with id "${spec.id}"`);
            }
            return texture;
        });
    }
    addTextures(textures) {
        for (const texture of textures) {
            this.specsById[texture.id] = texture;
        }
    }
}

class MaterialManagerView3D {
    constructor(canvas, cameraManager, renderer, material, hdr) {
        this.canvas = canvas;
        this.cameraManager = cameraManager;
        this.renderer = renderer;
        this.material = material;
        this.hdr = hdr;
        this.resizeHandler = () => {
            this.updateSizes();
            this.cameraManager.resize(this.width, this.height);
            this.renderer.setSize(this.width, this.height);
        };
        this.width = canvas.offsetWidth;
        this.height = canvas.offsetHeight;
        this.hdrPath = hdr;
        const { color, roughness, metalness, opacity, transparent } = material;
        this.mat = new MeshStandardMaterial({ color, roughness, metalness, opacity, transparent });
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            this.cameraManager.init({
                camera: {
                    minDistance: 10,
                    maxDistance: 60,
                    maxPolarAngle: 180,
                    enablePan: false,
                    position: { x: 0, y: 0, z: 40 },
                },
            }, this.canvas);
            this.initScene();
            this.updateSizes();
            this.renderer.setSize(this.width, this.height);
            this.initHandlers();
            this.startRendering();
        });
    }
    initScene() {
        this.scene = new Scene();
        this.sceneNode = new Object3D();
        this.sceneNode.name = 'Scene';
        this.scene.background = new Color(0xffffff);
        const pMREMGenerator = new PMREMGenerator(this.renderer);
        this.scene.environment = pMREMGenerator.fromScene(new RoomEnvironment()).texture;
        this.scene.add(this.sceneNode);
        this.rootNode = new Object3D();
        this.rootNode.name = 'Root';
        this.sceneNode.add(this.rootNode);
        this.loadView();
    }
    loadView() {
        const geometry = new TorusKnotGeometry(5, 1.5, 200, 32);
        this.mesh = new Mesh(geometry, this.mat);
        this.rootNode.add(this.mesh);
    }
    updateSizes() {
        const parent = this.canvas.parentElement;
        if (parent) {
            this.height = parent.clientHeight;
            this.width = parent.clientWidth;
        }
        else {
            console.error('No parent for canvas found');
        }
    }
    initHandlers() {
        window.addEventListener('resize', this.resizeHandler);
        window.addEventListener('toggle-fullscreen', this.resizeHandler);
    }
    startRendering() {
        requestAnimationFrame(() => {
            this.cameraManager.update();
            if (this.renderer) {
                this.renderer.render(this.scene, this.cameraManager.camera);
                this.startRendering();
            }
        });
    }
    setMaterial(material) {
        var _a, _b;
        let newMaterial;
        if (material.materialType === 'MeshPhysicalMaterial') {
            newMaterial = new MeshPhysicalMaterial({
                color: material.color,
                roughness: material.roughness,
                metalness: material.metalness,
                opacity: material.opacity,
                transparent: material.transparent,
                emissive: material.emissive,
                emissiveIntensity: material.emissiveIntensity,
                clearcoat: (_a = material.clearcoat) !== null && _a !== void 0 ? _a : 0,
                clearcoatRoughness: (_b = material.clearcoatRoughness) !== null && _b !== void 0 ? _b : 0,
            });
        }
        else {
            newMaterial = new MeshStandardMaterial({
                color: material.color,
                roughness: material.roughness,
                metalness: material.metalness,
                opacity: material.opacity,
                transparent: material.transparent,
                emissive: material.emissive,
                emissiveIntensity: material.emissiveIntensity,
            });
        }
        const updateTexture = (prop, texture) => {
            var _a, _b;
            if (newMaterial[prop]) {
                newMaterial[prop].dispose();
            }
            newMaterial[prop] = texture || null;
            if (texture) {
                texture.needsUpdate = true;
                texture.wrapS = texture.wrapT = RepeatWrapping;
                texture.repeat.set((_a = material.repeatX) !== null && _a !== void 0 ? _a : 1, (_b = material.repeatY) !== null && _b !== void 0 ? _b : 1);
            }
        };
        for (const map of Object.values(MaterialMaps)) {
            const materialMap = material[map];
            if (materialMap) {
                updateTexture(map, new Texture(materialMap));
            }
            else {
                updateTexture(map, undefined);
            }
        }
        newMaterial.needsUpdate = true;
        this.mesh.material = newMaterial;
    }
}

const TEXTURE_PARAM_NAMES = [
    'map',
    'metalnessMap',
    'roughnessMap',
    'alphaMap',
    'aoMap',
    'bumpMap',
    'displacementMap',
    'emissiveMap',
    'envMap',
    'lightMap',
    'normalMap',
];
const textureParams = TEXTURE_PARAM_NAMES.reduce((acc, name) => {
    acc[name] = { kind: 'texture', requiresNeedsUpdateOnChange: true };
    return acc;
}, {});
const BASE_MATERIAL_SCHEMA = {
    transparent: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    depthTest: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    depthWrite: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    polygonOffset: { kind: 'boolean', requiresNeedsUpdateOnChange: false },
    dithering: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    fog: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    flatShading: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    wireframe: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    toneMapped: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    premultipliedAlpha: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    clipIntersection: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    clipShadows: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
    opacity: { kind: 'number', requiresNeedsUpdateOnChange: false },
    alphaTest: { kind: 'number', requiresNeedsUpdateOnChange: true },
    polygonOffsetFactor: { kind: 'number', requiresNeedsUpdateOnChange: false },
    polygonOffsetUnits: { kind: 'number', requiresNeedsUpdateOnChange: false },
    side: { kind: 'number', requiresNeedsUpdateOnChange: true },
    shadowSide: { kind: 'number', requiresNeedsUpdateOnChange: true },
    blending: { kind: 'number', requiresNeedsUpdateOnChange: true },
    normalMapType: { kind: 'number', requiresNeedsUpdateOnChange: true },
};
const STANDARD_MATERIAL_SCHEMA = Object.assign({ color: { kind: 'color', requiresNeedsUpdateOnChange: false }, metalness: { kind: 'number', requiresNeedsUpdateOnChange: false }, roughness: { kind: 'number', requiresNeedsUpdateOnChange: false }, normalScale: { kind: 'vector2', requiresNeedsUpdateOnChange: false }, emissive: { kind: 'color', requiresNeedsUpdateOnChange: false }, emissiveIntensity: { kind: 'number', requiresNeedsUpdateOnChange: false }, aoMapIntensity: { kind: 'number', requiresNeedsUpdateOnChange: false }, lightMapIntensity: { kind: 'number', requiresNeedsUpdateOnChange: false }, bumpScale: { kind: 'number', requiresNeedsUpdateOnChange: false }, displacementScale: { kind: 'number', requiresNeedsUpdateOnChange: false }, displacementBias: { kind: 'number', requiresNeedsUpdateOnChange: false }, envMapIntensity: { kind: 'number', requiresNeedsUpdateOnChange: false } }, textureParams);
const PHYSICAL_MATERIAL_SCHEMA = Object.assign(Object.assign({}, STANDARD_MATERIAL_SCHEMA), { clearcoat: { kind: 'number', requiresNeedsUpdateOnChange: false }, clearcoatRoughness: { kind: 'number', requiresNeedsUpdateOnChange: false }, clearcoatNormalScale: { kind: 'vector2', requiresNeedsUpdateOnChange: false }, ior: { kind: 'number', requiresNeedsUpdateOnChange: true }, transmission: { kind: 'number', requiresNeedsUpdateOnChange: true }, thickness: { kind: 'number', requiresNeedsUpdateOnChange: false }, attenuationDistance: { kind: 'number', requiresNeedsUpdateOnChange: false }, attenuationColor: { kind: 'color', requiresNeedsUpdateOnChange: false }, specularIntensity: { kind: 'number', requiresNeedsUpdateOnChange: false }, specularColor: { kind: 'color', requiresNeedsUpdateOnChange: false }, sheenColor: { kind: 'color', requiresNeedsUpdateOnChange: false }, sheenRoughness: { kind: 'number', requiresNeedsUpdateOnChange: false }, clearcoatMap: { kind: 'texture', requiresNeedsUpdateOnChange: true }, clearcoatNormalMap: { kind: 'texture', requiresNeedsUpdateOnChange: true }, clearcoatRoughnessMap: { kind: 'texture', requiresNeedsUpdateOnChange: true }, transmissionMap: { kind: 'texture', requiresNeedsUpdateOnChange: true }, thicknessMap: { kind: 'texture', requiresNeedsUpdateOnChange: true } });
const SHADER_MATERIAL_SCHEMA = {
    transparent: { kind: 'boolean', requiresNeedsUpdateOnChange: true },
};
function getSchemaFor(type) {
    switch (type) {
        case 'standardMaterial':
            return Object.assign(Object.assign({}, BASE_MATERIAL_SCHEMA), STANDARD_MATERIAL_SCHEMA);
        case 'physicalMaterial':
            return Object.assign(Object.assign({}, BASE_MATERIAL_SCHEMA), PHYSICAL_MATERIAL_SCHEMA);
        case 'shaderMaterial':
            return Object.assign(Object.assign({}, BASE_MATERIAL_SCHEMA), SHADER_MATERIAL_SCHEMA);
        default:
            return {};
    }
}
function convertValue(kind, value, assetsManager) {
    if (value === undefined || value === null)
        return value;
    switch (kind) {
        case 'color':
            return new Color(value);
        case 'texture':
            return assetsManager.getAsset(value);
        case 'vector2':
            if (value instanceof Vector2)
                return value;
            if (Array.isArray(value))
                return new Vector2(value[0], value[1]);
            if (typeof value === 'object')
                return new Vector2(value.x, value.y);
            return value;
        default:
            return value;
    }
}

class BuilderPlatformInfo {
    constructor(view3DConfiguration, dynamicValueService, partsRegistry, meta = {}) {
        this.view3DConfiguration = view3DConfiguration;
        this.dynamicValueService = dynamicValueService;
        this.partsRegistry = partsRegistry;
        this.meta = meta;
        this.product = {};
        this.partsRegistry.addParts(this.view3DConfiguration.parts);
    }
    setProduct(product) {
        this.product = product;
    }
    getProduct() {
        return this.product;
    }
    getView3d() {
        return this.view3DConfiguration;
    }
    setSlotProvider(provider) {
        this.slotProvider = provider;
    }
    getSlots() {
        return this.slotProvider ? this.slotProvider() : Promise.resolve([]);
    }
    getMeta() {
        return this.meta;
    }
    setMeta(meta) {
        this.meta = meta;
    }
    getModel(modelKey) {
        return this.findModel((model) => model.key === modelKey);
    }
    findModel(predicate) {
        return this.view3DConfiguration.views.find(predicate);
    }
    getParts() {
        return this.partsRegistry.getParts(this.product);
    }
    getHiddenMeshesForPart(part) {
        const meshesVisibility = {};
        for (const rule of part.rules) {
            if (rule.type === 'hideMeshRule') {
                const isActive = isConditionActive(rule.condition, this.product);
                rule.meshes.forEach((mesh) => {
                    var _a, _b;
                    if (!(mesh in meshesVisibility)) {
                        meshesVisibility[mesh] = {
                            scene: true,
                            webAr: true,
                        };
                    }
                    if (rule.hideInScene) {
                        (_a = meshesVisibility[mesh]).scene && (_a.scene = !isActive);
                    }
                    if (rule.hideInWebAR) {
                        (_b = meshesVisibility[mesh]).webAr && (_b.webAr = !isActive);
                    }
                });
            }
        }
        const specs = [];
        for (const [mesh, options] of Object.entries(meshesVisibility)) {
            specs.push({
                mesh,
                visible: options.scene,
                webAr: { visible: options.webAr },
            });
        }
        return specs;
    }
    getTextures() {
        const textures = this.view3DConfiguration.textures || [];
        return textures.map((texture) => {
            var _a;
            const params = { url: texture.file };
            for (const [optionName, optionValue] of Object.entries((_a = texture.options) !== null && _a !== void 0 ? _a : {})) {
                if (!(optionName in TEXTURE_SCHEMA)) {
                    console.warn(`Unknown option name ${optionName} in texture ${texture.id}`);
                }
                params[optionName] = this.dynamicValueService.calculate(optionValue, this.product);
            }
            return {
                key: texture.id,
                params,
            };
        });
    }
    getPartMeshes(partKey) {
        const part = this.view3DConfiguration.parts.find((part) => part.partKey === partKey);
        if (!part) {
            throw new Error(`Cannot find part: ${partKey}`);
        }
        return this.createPartMaterialSpecification(part.materials, this.product);
    }
    createPartMaterialSpecification(materialConfigurations, configuration) {
        const result = [];
        for (const materialConfiguration of materialConfigurations) {
            const materialReferenceId = this.dynamicValueService.calculate(materialConfiguration.material, configuration);
            if (!materialReferenceId) {
                console.warn(`Skipped material for "${JSON.stringify(materialConfiguration.material)}"`);
                continue;
            }
            const material = {
                materialKey: materialReferenceId,
                meshName: materialConfiguration.mesh,
            };
            result.push(material);
        }
        return result;
    }
    getMaterials() {
        var _a, _b;
        const configuration = this.product;
        const materials = [];
        for (const material of (_a = this.view3DConfiguration.materials) !== null && _a !== void 0 ? _a : []) {
            const type = material.type;
            switch (type) {
                case 'physicalMaterial':
                case 'standardMaterial': {
                    const params = {
                        type,
                    };
                    const schema = getSchemaFor(material.type);
                    for (const [optionName, optionValue] of Object.entries(material.options)) {
                        if (!(optionName in schema)) {
                            console.warn(`Unknown optionName ${optionName} in material ${type}`);
                        }
                        params[optionName] = this.dynamicValueService.calculate(optionValue, configuration);
                    }
                    materials.push({ key: material.id, params: params });
                    break;
                }
                case 'shaderMaterial': {
                    const params = {
                        type: 'shaderMaterial',
                        fragmentShader: material.shader.fragment,
                        vertexShader: material.shader.vertex,
                        transparent: (_b = material.shader.transparent) !== null && _b !== void 0 ? _b : false,
                        uniforms: material.shader.uniforms,
                    };
                    materials.push({
                        key: material.id,
                        params,
                    });
                    break;
                }
                case 'meshMaterial':
                    throw new Error('Not supported material type: meshMaterial');
                default:
                    assertUnreachable(type, 'Invalid material type');
            }
        }
        return materials;
    }
    getComponentModel(componentKey) {
        const variant = this.product[componentKey];
        if (!variant) {
            return;
        }
        const viewKeyAttribute = variant[VariantProperties.viewModel3dKey];
        if (viewKeyAttribute) {
            const viewKey = getAttributeValue(viewKeyAttribute);
            const overriddenModel = this.findModel((model) => model.key === viewKey);
            if (overriddenModel) {
                return overriddenModel;
            }
        }
        const modelIdAttribute = variant['_model3dId'];
        if (!modelIdAttribute) {
            return;
        }
        const modelId = getAttributeValue(modelIdAttribute);
        const model = this.findModel((model) => model.id === modelId);
        if (!model) {
            return;
        }
        return model;
    }
    getModelJP(model, jpKey) {
        return model.model3dJointPoints.find((jp) => jp.key === jpKey);
    }
    getComponentJointPoint(componentKey, jpKey) {
        const model = this.getComponentModel(componentKey);
        if (!model) {
            return;
        }
        return this.getModelJP(model, jpKey);
    }
}

class ViewNode {
    static getContainerNodeName(key) {
        return key + '_container';
    }
    static getContentNodeName(key) {
        return key + '_content';
    }
    constructor(key) {
        this.key = key;
        this.__ctx = null;
        this.modifiers = [];
        this.children = [];
        this.position = defaultPosition;
        this.rotation = defaultRotation;
        this.scale = { x: 1, y: 1, z: 1 };
        this.visible = true;
        this.settings = {};
        this.params = {};
    }
    init(params = {}) {
        this.params = params;
    }
    getPrefetchAssets() {
        return [];
    }
    setPosition(position) {
        this.position = position;
        return this;
    }
    setRotation(rotation) {
        this.rotation = rotation;
        return this;
    }
    setScale(scale) {
        this.scale = scale;
        return this;
    }
    setVisible(visible) {
        this.visible = visible;
        return this;
    }
    setSettings(settings) {
        this.settings = Object.assign(Object.assign({}, this.settings), settings);
        return this;
    }
    getSettings() {
        return resolveNodeSettings(this.settings);
    }
    addModifier(modifier) {
        this.modifiers.push(modifier);
        return this;
    }
    getModifiers() {
        return [...this.modifiers];
    }
    applyModifiers(obj, context) {
        return this.modifiers.reduce((currentObj, modifier) => {
            try {
                return modifier.apply(currentObj, context);
            }
            catch (error) {
                console.error(`Failed to apply modifier for node "${this.key}":`, error);
                return currentObj;
            }
        }, obj);
    }
}

class Model3dNode extends ViewNode {
    constructor(key) {
        super(key);
        this.key = key;
    }
    init(params) {
        this.params = params;
        if (params.settings) {
            this.setSettings(params.settings);
        }
    }
    getPrefetchAssets() {
        const url = this.params.url;
        return [
            {
                type: 'Model3d',
                key: url,
                url,
            },
        ];
    }
    build() { }
    mount(params) {
        var _a;
        const { container, assetsApi, materialsApi } = params;
        const model3d = (_a = assetsApi.getModelAsset(this.params.url)) === null || _a === void 0 ? void 0 : _a.clone();
        setUserData(container, this.getSettings());
        if (model3d) {
            this.updateModelMaterials(model3d, materialsApi);
            this.hideRequestedMeshes(model3d);
            container.add(model3d);
        }
    }
    updateModelMaterials(model3d, materialsApi) {
        var _a;
        const params = this.params;
        for (const material of (_a = params.meshesMaterials) !== null && _a !== void 0 ? _a : []) {
            const modelMesh = model3d.getObjectByName(material.meshName);
            if (!(modelMesh instanceof Mesh)) {
                console.warn(`Cannot find mesh "${material.meshName}" in model "${this.key}. Skipped material ${material.materialKey}"`);
                continue;
            }
            const modelMaterial = materialsApi.get(material.materialKey);
            if (!modelMaterial) {
                console.warn(`Cannot find material "${material.materialKey}"`);
                continue;
            }
            modelMesh.material = modelMaterial;
        }
    }
    hideRequestedMeshes(model3d) {
        var _a;
        const params = this.params;
        for (const { mesh, visible, webAr } of (_a = params.hiddenMeshes) !== null && _a !== void 0 ? _a : []) {
            let found = false;
            model3d.traverse((node) => {
                if (node.name === mesh) {
                    found = true;
                    node.visible = visible;
                    changeUserData(node, { webAr });
                }
            });
            if (!found) {
                console.warn(`Cannot find mesh or group "${mesh}" in model "${this.key}"`);
            }
        }
    }
}

class ComponentNode extends ViewNode {
    constructor(key) {
        super(key);
        this.key = key;
    }
    init(params) {
        this.params = params;
        if (params.settings) {
            this.setSettings(params.settings);
        }
    }
    build(options) {
        const { builder, context } = options;
        const params = this.params;
        const model = context.getComponentModel(params.componentKey);
        if (!model) {
            return;
        }
        builder.addModel3dNode({
            key: this.key + '_model3d',
            params: {
                url: model.url,
                meshesMaterials: params.meshesMaterials,
                hiddenMeshes: params.hiddenMeshes,
                settings: this.params.settings,
            },
        });
    }
    mount() { }
}

class ViewNodeBuilder {
    constructor(viewBuilder, assetsManager, parentBuilder) {
        this.viewBuilder = viewBuilder;
        this.assetsManager = assetsManager;
        this.innerNodes = [];
        this.innerNodesKeys = new Set();
        this.nodesConnections = new Map();
        this.builders = new Map();
        this.connectActions = [];
        this.nodeRegistry = new Map();
        this.object3dRegistry = new Map();
        this.parentBuilder = parentBuilder;
    }
    clear() {
        this.innerNodes = [];
        this.builders.clear();
        this.innerNodesKeys.clear();
        this.nodesConnections.clear();
        this.connectActions = [];
    }
    addViewNode(options) {
        const node = this.viewBuilder.createViewNode(options);
        this.attachNodeInstance(node);
        return node;
    }
    attachNodeInstance(node) {
        this.innerNodes.push(node);
        this.innerNodesKeys.add(node.key);
        this.nodeRegistry.set(node.key, node);
        const innerBuilder = new ViewNodeBuilder(this.viewBuilder, this.assetsManager, this);
        this.builders.set(node.key, innerBuilder);
        node.build({ builder: innerBuilder, context: this.viewBuilder.getContext() });
    }
    addModel3dNode(options) {
        return this.addViewNode({ type: StandardViewNodes.Model3d, key: options.key, params: options.params });
    }
    addComponentNode(options) {
        return this.addViewNode({ type: StandardViewNodes.Component, key: options.key, params: options.params });
    }
    addLightNode(options) {
        return this.addViewNode({ type: StandardViewNodes.Light, key: options.key, params: options.params });
    }
    connectNodes(params) {
        this.connectActions.push(() => {
            const parentJP = params.parent.jp;
            const childJP = params.child.jp;
            const parentKey = params.parent.key;
            const childKey = params.child.key;
            if (!this.innerNodesKeys.has(childKey)) {
                throw new Error(`No node with key ${childKey}`);
            }
            if (!this.innerNodesKeys.has(parentKey)) {
                throw new Error(`No node with key ${parentKey}`);
            }
            this.addNodeConnection(parentKey, { childKey, childJP, parentJP });
        });
    }
    mountTo(root) {
        var _a, _b, _c;
        const map = new Map();
        this.innerNodes.forEach((innerNode) => {
            const contentLayer = createContentLayer(innerNode);
            contentLayer.visible = innerNode.visible;
            const nativeLayer = createNativeLayer(innerNode);
            innerNode.mount({
                container: nativeLayer,
                context: this.viewBuilder.getContext(),
                assetsApi: this.assetsManager,
                materialsApi: this.viewBuilder.materialsManager,
            });
            const builder = this.builders.get(innerNode.key);
            if (!builder) {
                throw new Error(`Cannot find builder for node ${innerNode.key}`);
            }
            const innerLayer = createInnerLayer(innerNode);
            builder.mountTo(innerLayer);
            contentLayer.add(nativeLayer);
            contentLayer.add(innerLayer);
            const containerLayer = createContainerLayer(innerNode);
            containerLayer.add(contentLayer);
            containerLayer.position.set(innerNode.position.x, innerNode.position.y, innerNode.position.z);
            containerLayer.rotation.set(innerNode.rotation.x, innerNode.rotation.y, innerNode.rotation.z);
            containerLayer.scale.set(innerNode.scale.x, innerNode.scale.y, innerNode.scale.z);
            map.set(innerNode.key, containerLayer);
            this.object3dRegistry.set(innerNode.key, containerLayer);
            root.add(containerLayer);
        });
        this.connectActions.forEach((action) => action());
        for (const [parentKey, connections] of this.nodesConnections.entries()) {
            const parentContainer = map.get(parentKey);
            if (!parentContainer) {
                throw new Error(`Cannot find parent ${parentKey}`);
            }
            for (const { childKey, childJP, parentJP } of connections) {
                const childContainer = map.get(childKey);
                if (!childContainer) {
                    throw new Error(`Cannot find child ${childKey}`);
                }
                let childrenLayer = parentContainer.getObjectByName(getChildrenLayerName(parentKey));
                if (!childrenLayer) {
                    childrenLayer = createChildrenLayer(parentKey);
                    parentContainer.add(childrenLayer);
                }
                if (!childJP && !parentJP) {
                    childrenLayer.add(childContainer);
                }
                else {
                    const parentJp = (_a = parentJP === null || parentJP === void 0 ? void 0 : parentJP.jointPoint) !== null && _a !== void 0 ? _a : defaultJP;
                    const childJp = (_b = childJP === null || childJP === void 0 ? void 0 : childJP.jointPoint) !== null && _b !== void 0 ? _b : defaultJP;
                    const jpLayer = new Object3D();
                    jpLayer.name = (_c = parentJP === null || parentJP === void 0 ? void 0 : parentJP.name) !== null && _c !== void 0 ? _c : `JP_${parentKey}=>${childKey}`;
                    jointPointToThreeMatrix(parentJp).decompose(jpLayer.position, jpLayer.quaternion, jpLayer.scale);
                    childrenLayer.add(jpLayer);
                    const childInverse = jointPointInverseMatrix(childJp);
                    childInverse.decompose(childContainer.position, childContainer.quaternion, childContainer.scale);
                    jpLayer.add(childContainer);
                }
            }
        }
        this.connectActions = [];
        this.applyAllModifiers();
    }
    addNodeConnection(parentKey, connection) {
        const connections = this.nodesConnections.get(parentKey);
        if (!connections) {
            this.nodesConnections.set(parentKey, [connection]);
        }
        else {
            connections.push(connection);
        }
    }
    collectAllNodes() {
        const allNodes = [...this.innerNodes];
        for (const builder of this.builders.values()) {
            allNodes.push(...builder.collectAllNodes());
        }
        return allNodes;
    }
    getAllNodeRegistries() {
        const registry = new Map(this.nodeRegistry);
        for (const builder of this.builders.values()) {
            for (const [key, node] of builder.getAllNodeRegistries()) {
                registry.set(key, node);
            }
        }
        return registry;
    }
    getAllObject3DRegistries() {
        const registry = new Map(this.object3dRegistry);
        for (const builder of this.builders.values()) {
            for (const [key, obj] of builder.getAllObject3DRegistries()) {
                registry.set(key, obj);
            }
        }
        return registry;
    }
    buildDependencyGraph(nodes) {
        const graph = new Map();
        for (const node of nodes) {
            const deps = new Set();
            for (const modifier of node.getModifiers()) {
                if (modifier.getDependencies) {
                    modifier.getDependencies().forEach((dep) => deps.add(dep));
                }
            }
            graph.set(node.key, deps);
        }
        return graph;
    }
    validateDependencies(graph) {
        for (const [nodeKey, deps] of graph) {
            for (const dep of deps) {
                if (!graph.has(dep)) {
                    throw new Error(`Modifier dependency error: Node "${nodeKey}" depends on non-existent node "${dep}"`);
                }
            }
        }
    }
    topologicalSort(graph) {
        const sorted = [];
        const visited = new Set();
        const visiting = new Set();
        const visit = (nodeKey, path = []) => {
            if (visited.has(nodeKey))
                return;
            if (visiting.has(nodeKey)) {
                const cycle = [...path, nodeKey].join(' → ');
                console.warn(`[ViewNodeBuilder] Circular dependency detected: ${cycle}`);
                return;
            }
            visiting.add(nodeKey);
            const deps = graph.get(nodeKey) || new Set();
            for (const dep of deps) {
                visit(dep, [...path, nodeKey]);
            }
            visiting.delete(nodeKey);
            visited.add(nodeKey);
            sorted.push(nodeKey);
        };
        for (const nodeKey of graph.keys()) {
            visit(nodeKey);
        }
        return sorted;
    }
    applyAllModifiers() {
        if (this.parentBuilder) {
            return;
        }
        const context = {
            findNodeByKey: (key) => this.getAllObject3DRegistries().get(key),
            getViewNode: (key) => this.getAllNodeRegistries().get(key),
        };
        const allNodes = this.collectAllNodes();
        const dependencyGraph = this.buildDependencyGraph(allNodes);
        try {
            this.validateDependencies(dependencyGraph);
        }
        catch (error) {
            console.error('[ViewNodeBuilder] Modifier dependency validation failed:', error);
            throw error;
        }
        const sortedKeys = this.topologicalSort(dependencyGraph);
        const allNodeRegistries = this.getAllNodeRegistries();
        const allObject3DRegistries = this.getAllObject3DRegistries();
        for (const nodeKey of sortedKeys) {
            const viewNode = allNodeRegistries.get(nodeKey);
            const obj3d = allObject3DRegistries.get(nodeKey);
            if (viewNode && obj3d) {
                const modifiers = viewNode.getModifiers();
                if (modifiers.length > 0) {
                    try {
                        viewNode.applyModifiers(obj3d, context);
                    }
                    catch (error) {
                        console.error(`[ViewNodeBuilder] Failed to apply modifiers for node "${nodeKey}":`, error);
                    }
                }
            }
        }
    }
}
function createContentLayer(innerNode) {
    const node = new Object3D();
    node.name = ViewNode.getContentNodeName(innerNode.key);
    return node;
}
function createContainerLayer(innerNode) {
    const node = new Object3D();
    node.name = ViewNode.getContainerNodeName(innerNode.key);
    return node;
}
function getChildrenLayerName(key) {
    return key + '_children';
}
function createChildrenLayer(key) {
    const node = new Object3D();
    node.name = getChildrenLayerName(key);
    return node;
}
function createNativeLayer(innerNode) {
    const node = new Object3D();
    node.name = innerNode.key + '_native';
    return node;
}
function createInnerLayer(innerNode) {
    const node = new Object3D();
    node.name = innerNode.key + '_inner';
    return node;
}

class MaterialDefaultsCache {
    static getDefaultsFor(material) {
        const type = material.constructor.name;
        const defaults = this.cache.get(type);
        if (defaults) {
            return defaults;
        }
        const defaultMaterial = new material.constructor();
        this.cache.set(type, defaultMaterial);
        return defaultMaterial;
    }
}
MaterialDefaultsCache.cache = new Map();

class MaterialsManager {
    constructor(assetsManager) {
        this.assetsManager = assetsManager;
        this.materialsParams = new Map();
        this.materials = new Map();
    }
    clear() {
        this.materialsParams.clear();
    }
    addMaterial(options) {
        this.updateOrCreate(options.key, options.params);
    }
    has(key) {
        return this.materials.has(key);
    }
    get(key) {
        return this.materials.get(key);
    }
    getThreeJsParams(params) {
        const { type } = params, threeJsParams = __rest(params, ["type"]);
        return threeJsParams;
    }
    updateOrCreate(key, params) {
        const existing = this.materials.get(key);
        const prevParams = this.materialsParams.get(key);
        this.materialsParams.set(key, params);
        if (!existing) {
            const newMat = this.createMaterial(key, params);
            this.materials.set(key, newMat);
            return newMat;
        }
        this.updateMaterial(existing, prevParams, params);
        return existing;
    }
    updateMaterial(material, prevParams, newParams) {
        const params = this.getThreeJsParams(newParams);
        const oldParams = !prevParams ? {} : this.getThreeJsParams(prevParams);
        const schema = getSchemaFor(newParams.type);
        for (const key of Object.keys(params)) {
            const value = params[key];
            if (value === oldParams[key])
                continue;
            const paramSchema = schema[key];
            if (paramSchema) {
                material[key] = convertValue(paramSchema.kind, value, this.assetsManager);
                if (paramSchema.requiresNeedsUpdateOnChange)
                    material.needsUpdate = true;
            }
            else {
                material[key] = value;
                material.needsUpdate = true;
            }
        }
        for (const key of Object.keys(oldParams)) {
            if (!(key in params)) {
                this.resetMaterialProperty(material, key);
                const paramSchema = schema[key];
                if (!paramSchema || paramSchema.requiresNeedsUpdateOnChange) {
                    material.needsUpdate = true;
                }
            }
        }
    }
    createMaterial(key, params) {
        let material;
        const type = params.type;
        switch (type) {
            case 'shaderMaterial':
                material = this.createShaderMaterial(key, params);
                break;
            case 'standardMaterial':
                material = this.createStandardMaterial(key, params);
                break;
            case 'physicalMaterial':
                material = this.createPhysicalMaterial(key, params);
                break;
            default:
                assertUnreachable(type, `Unhandled material type ${type}`);
        }
        if (material === undefined) {
            throw new Error(`Cannot find material "${key}"`);
        }
        material.name = key;
        return material;
    }
    createShaderMaterial(materialName, params) {
        var _a, _b;
        const materialUniforms = Object.entries((_a = params.uniforms) !== null && _a !== void 0 ? _a : {});
        const uniforms = {};
        materialUniforms.map(([textureName, textureKey]) => {
            uniforms[textureName] = { value: this.assetsManager.getAsset(textureKey) };
        });
        const material = new ShaderMaterial({
            fragmentShader: params.fragmentShader,
            vertexShader: params.vertexShader,
            transparent: (_b = params.transparent) !== null && _b !== void 0 ? _b : false,
            uniforms,
        });
        material.needsUpdate = true;
        material.name = materialName;
        return material;
    }
    createStandardMaterial(materialName, params) {
        const maps = this.getMaterialTextures(params);
        const threeJsParams = this.getThreeJsParams(params);
        const materialParams = Object.assign(Object.assign({}, threeJsParams), maps);
        const material = new MeshStandardMaterial(Object.assign({}, materialParams));
        return material;
    }
    getMaterialTextures(params) {
        const result = {};
        for (const mapName of TEXTURE_PARAM_NAMES) {
            const textureKey = params[mapName];
            if (textureKey) {
                const texture = this.assetsManager.getAsset(textureKey);
                if (!texture) {
                    throw new Error(`Cannot load texture for ${mapName} with key ${params[mapName]}`);
                }
                result[mapName] = texture;
            }
        }
        return result;
    }
    createPhysicalMaterial(materialName, params) {
        const maps = this.getMaterialTextures(params);
        const threeJsParams = this.getThreeJsParams(params);
        const materialParams = Object.assign(Object.assign({}, threeJsParams), maps);
        const material = new MeshPhysicalMaterial(Object.assign({}, materialParams));
        material.name = materialName;
        return material;
    }
    resetMaterialProperty(material, key) {
        const defaultMaterial = MaterialDefaultsCache.getDefaultsFor(material);
        if (key in defaultMaterial) {
            material[key] = defaultMaterial[key];
        }
        else {
            console.warn(`You are trying to reset unknown material property ${key} in material type ${material.type}. Setting it to undefined`);
            material[key] = undefined;
        }
    }
}

var StandardViewNodes;
(function (StandardViewNodes) {
    StandardViewNodes["Model3d"] = "Model3d";
    StandardViewNodes["Component"] = "Component";
    StandardViewNodes["Light"] = "Light";
})(StandardViewNodes || (StandardViewNodes = {}));
class ViewBuilder {
    constructor(context) {
        this.context = context;
        this.registeredViewNodes = new Map();
        this.createdNodes = new Map();
        this.assetsManager = new AssetsManager();
        this.materialsManager = new MaterialsManager(this.assetsManager);
        this.builder = new ViewNodeBuilder(this, this.assetsManager);
        this.materialsChanges = [];
        this.fonts = [];
        this.buildCounter = 0;
    }
    getContext() {
        return this.context;
    }
    clear() {
        this.createdNodes.clear();
        this.builder.clear();
        this.materialsChanges = [];
    }
    buildTo(root) {
        return __awaiter(this, void 0, void 0, function* () {
            const currentCounter = ++this.buildCounter;
            yield this.prefetchAssets();
            if (currentCounter !== this.buildCounter) {
                return;
            }
            this.updateMaterials();
            this.builder.mountTo(root);
            this.materialsChanges = [];
        });
    }
    test(_node) { }
    registerViewNode(type, nodeFactory) {
        if (this.registeredViewNodes.has(type)) {
            throw new Error('ViewNode with type "' + type + '" already registered!');
        }
        this.registeredViewNodes.set(type, nodeFactory);
    }
    createViewNode(options) {
        const { type, key, params } = options;
        const nodeFactory = this.registeredViewNodes.get(type);
        if (!nodeFactory) {
            throw new Error('Node with type "' + type + '" not registered!');
        }
        if (this.createdNodes.has(key)) {
            throw new Error(`Duplicated node key ${key}`);
        }
        const node = nodeFactory(key);
        node.init(params);
        this.createdNodes.set(key, node);
        return node;
    }
    addViewNode(options) {
        return this.builder.addViewNode(options);
    }
    addModel3dNode(options) {
        return this.builder.addModel3dNode(options);
    }
    addComponentNode(options) {
        return this.builder.addComponentNode(options);
    }
    addLightNode(options) {
        return this.builder.addLightNode(options);
    }
    addMaterial(options) {
        this.materialsChanges.push(() => this.materialsManager.addMaterial(options));
    }
    addTexture(options) {
        this.assetsManager.wantsAssets([
            {
                key: options.key,
                type: 'Texture',
                url: options.params.url,
                params: options.params,
            },
        ]);
    }
    addFont(options) {
        this.assetsManager.wantsAssets([
            {
                key: options.key,
                type: 'Font',
                url: options.params.url,
            },
        ]);
    }
    prefetchAssets() {
        return __awaiter(this, void 0, void 0, function* () {
            for (const node of this.createdNodes.values()) {
                const nodeWantedAssets = node.getPrefetchAssets();
                this.assetsManager.wantsAssets(nodeWantedAssets);
            }
            yield this.assetsManager.loadMissingAssets();
        });
    }
    connectNodes(params) {
        this.builder.connectNodes(params);
    }
    updateMaterials() {
        this.materialsChanges.forEach((action) => action());
    }
}

class PlatformBuilder {
    get assetsManager() {
        return this.builder.assetsManager;
    }
    constructor(context) {
        this.context = context;
        this.builder = new ViewBuilder(this.context);
        this.builder.registerViewNode(StandardViewNodes.Model3d, (key) => new Model3dNode(key));
        this.builder.registerViewNode(StandardViewNodes.Component, (key) => new ComponentNode(key));
    }
    getContext() {
        return this.context;
    }
    buildTo(root) {
        return __awaiter(this, void 0, void 0, function* () {
            this.builder.clear();
            this.context.getTextures().forEach((texture) => {
                this.builder.addTexture(texture);
            });
            this.build(this.builder, this.context);
            return this.builder.buildTo(root);
        });
    }
    build(builder, context) {
        context.getMaterials().forEach((material) => {
            this.builder.addMaterial(material);
        });
        const parts = context.getParts();
        parts.forEach((part) => {
            var _a, _b, _c, _d;
            const model = context.getComponentModel(part.componentKey);
            const meshesMaterials = context.getPartMeshes(part.partKey);
            const hiddenMeshes = context.getHiddenMeshesForPart(part);
            const node = builder.addComponentNode({
                key: part.partKey,
                params: {
                    componentKey: part.componentKey,
                    meshesMaterials,
                    hiddenMeshes,
                    settings: part.settings,
                },
            });
            if (part.layout.type === 'rootPartLayout') {
                if (!model) {
                    console.warn(`No model for part ${part.partKey} found. Using default positioning`);
                }
                const transforms = (_a = part.layout.modelsTransforms) !== null && _a !== void 0 ? _a : {};
                const transformKeys = Object.keys(transforms);
                const layoutOptions = (_b = (model ? transforms[model.key] : undefined)) !== null && _b !== void 0 ? _b : (transformKeys.length === 1 ? transforms[transformKeys[0]] : undefined);
                if (layoutOptions) {
                    node.setPosition((_c = layoutOptions.translation) !== null && _c !== void 0 ? _c : defaultPosition);
                    node.setRotation((_d = layoutOptions.rotation) !== null && _d !== void 0 ? _d : defaultRotation);
                }
            }
            else {
                const parentPart = parts.find((it) => it.partKey === part.parentPartKey);
                if (!parentPart) {
                    throw new Error(`Cannot find parent part ${part.parentPartKey}`);
                }
                const mainJP = context.getComponentJointPoint(part.componentKey, part.layout.mainJointPointKey);
                const parentJP = context.getComponentJointPoint(parentPart.componentKey, part.layout.parentJointPointKey);
                builder.connectNodes({
                    parent: {
                        key: part.parentPartKey,
                        jp: parentJP,
                    },
                    child: {
                        key: part.partKey,
                        jp: mainJP,
                    },
                });
            }
        });
    }
}

class DynamicValueService {
    constructor(pipeRegistry) {
        this.pipeRegistry = pipeRegistry;
    }
    calculate(value, model) {
        if (value === null || typeof value !== 'object') {
            return value;
        }
        switch (value.type) {
            case 'constant':
                return value.value;
            case 'attributeValue': {
                return this.getAttributeValue(model, value);
            }
            case 'tableValue': {
                return this.getTableValue(model, value);
            }
            default: {
                const type = value.type;
                if (typeof type === 'string') {
                    console.warn(`Unknown dynamic value type "${type}". Treating the value as a literal.`);
                }
                return value;
            }
        }
    }
    getAttributeValue(model, value) {
        const component = model[value.componentKey];
        if (component === null) {
            throw new Error(`Component ${value.componentKey} not found in model`);
        }
        const dtoValue = component[value.attributeKey];
        let attributeValue = getAttributeValue(dtoValue);
        if (value.pipes) {
            attributeValue = this.pipeRegistry.runPipeN(value.pipes, attributeValue, value);
        }
        return attributeValue;
    }
    getTableValue(model, value) {
        const fieldValues = value.columns.map((column) => this.getColumnValue(column, model));
        const matchedRecord = value.data.find((record) => {
            if (isEqualFirstElements(record, fieldValues, fieldValues.length)) {
                return record;
            }
        });
        return matchedRecord ? getRecordOutputValue(matchedRecord) : value.fallbackValue;
    }
    getColumnValue(column, model) {
        const component = model[column.componentKey];
        if (component === null) {
            throw new Error(`Component ${column.componentKey} not found in model`);
        }
        const dtoValue = component[column.attributeKey];
        return getAttributeValue(dtoValue);
    }
}
function isEqualFirstElements(xs, ys, count) {
    for (let i = 0; i < count; i++) {
        if (xs[i] != ys[i] && xs[i] !== '*') {
            return false;
        }
    }
    return true;
}
function getRecordOutputValue(record) {
    return record[record.length - 1];
}

class PipeRegistry {
    constructor() {
        this.map = new Map();
    }
    registerPipe(name, pipe) {
        this.map.set(name, pipe);
    }
    getPipe(name) {
        return this.map.get(name);
    }
    getPipeN(names) {
        const pipes = [];
        for (const pipeName of names) {
            const registeredPipe = this.map.get(pipeName);
            if (!registeredPipe) {
                throw new Error(`Cannot find registered pipe`);
            }
            pipes.push(registeredPipe);
        }
        return (initialValue, context) => {
            let value = initialValue;
            for (const pipe of pipes) {
                value = pipe(value, context);
            }
            return value;
        };
    }
    runPipeN(names, value, context) {
        const pipe = this.getPipeN(names);
        return pipe(value, context);
    }
}

const SLOT_PREFIX = 'slot__';
const SLOT_ITEM_PREFIX = 'slotItem__';
function slotGroupName(slotKey) {
    return SLOT_PREFIX + slotKey;
}
function slotItemGroupName(itemId) {
    return SLOT_ITEM_PREFIX + itemId;
}
function parseSlotGroupName(name) {
    return name.startsWith(SLOT_PREFIX) ? name.slice(SLOT_PREFIX.length) : null;
}
function parseSlotItemGroupName(name) {
    return name.startsWith(SLOT_ITEM_PREFIX) ? name.slice(SLOT_ITEM_PREFIX.length) : null;
}

function createCompositeContext(view3d, variant) {
    const pipeRegistry = new PipeRegistry();
    pipeRegistry.registerPipe('ralToRgb', ralToHex);
    const context = new BuilderPlatformInfo(view3d, new DynamicValueService(pipeRegistry), new PartsRegistry());
    context.setProduct(variant);
    return context;
}
function renderComposite(spec, target) {
    return __awaiter(this, void 0, void 0, function* () {
        const view3d = spec.view3d;
        const builder = new PlatformBuilder(createCompositeContext(view3d, spec.variant));
        yield builder.buildTo(target);
        yield renderSlotsInto(spec.slots, view3d, target);
    });
}
function renderSlotsInto(slots, view3d, target) {
    return __awaiter(this, void 0, void 0, function* () {
        const containersByName = new Map();
        target.traverse((o) => {
            if (o.name)
                containersByName.set(o.name, o);
        });
        for (const slot of slots) {
            yield renderSlot(slot, view3d, target, containersByName);
        }
    });
}
function renderSlot(slot, view3d, target, containersByName) {
    return __awaiter(this, void 0, void 0, function* () {
        const slotGroup = new Object3D();
        slotGroup.name = slotGroupName(slot.key);
        slotGroup.position.set(slot.origin.x, slot.origin.y, slot.origin.z);
        let parent;
        if (slot.space.kind === 'part') {
            const container = containersByName.get(ViewNode.getContainerNodeName(slot.space.partKey));
            if (!container) {
                console.warn(`Slot "${slot.key}": part container "${slot.space.partKey}" not found in scene — slot not rendered.`);
                return;
            }
            parent = container;
        }
        else {
            parent = target;
        }
        parent.add(slotGroup);
        for (const item of slot.items) {
            const itemGroup = new Object3D();
            itemGroup.name = slotItemGroupName(item.id);
            changeUserData(itemGroup, {
                slotItem: {
                    id: item.id,
                    kind: item.geometry.kind === 'composite' ? 'composite' : 'basic',
                    slotKey: slot.key,
                },
            });
            toThreeMatrix(item.transform).decompose(itemGroup.position, itemGroup.quaternion, itemGroup.scale);
            slotGroup.add(itemGroup);
            if (item.geometry.kind === 'composite') {
                yield renderComposite(item.geometry.composite, itemGroup);
            }
            else {
                yield mountModel3d(item.geometry.model3dId, view3d, itemGroup);
            }
        }
    });
}
function mountModel3d(model3dId, view3d, target) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const url = (_a = view3d.views.find((m) => m.id === model3dId)) === null || _a === void 0 ? void 0 : _a.url;
        if (!url)
            return;
        const vb = new ViewBuilder(createCompositeContext(Object.assign(Object.assign({}, view3d), { parts: [] }), {}));
        vb.registerViewNode(StandardViewNodes.Model3d, (key) => new Model3dNode(key));
        vb.addModel3dNode({ key: `model_${model3dId}`, params: { url } });
        yield vb.buildTo(target);
    });
}

class ConfiguratorRenderer {
    constructor(sceneConfig, builder, canvas, gltfExporter, usdzExporter, cameraManager, environment, modelLoader, materialsLibrary, renderer, plugin) {
        this.sceneConfig = sceneConfig;
        this.builder = builder;
        this.canvas = canvas;
        this.gltfExporter = gltfExporter;
        this.usdzExporter = usdzExporter;
        this.cameraManager = cameraManager;
        this.environment = environment;
        this.modelLoader = modelLoader;
        this.materialsLibrary = materialsLibrary;
        this.renderer = renderer;
        this.plugin = plugin;
        this.debug = false;
        this.dimensionOptions = { unitSystem: 'metric', region: 'DE' };
        this.disableRerender = false;
        this.showRulerFlag = false;
        this.clock = null;
        this.resizeHandler = () => {
            this.updateSizes();
            this.dimensionsRenderer.dimensionsRenderer2D.setSize(this.width, this.height);
            this.cameraManager.resize(this.width, this.height);
            this.renderer.setSize(this.width, this.height, false);
        };
        this.width = canvas.offsetWidth;
        this.height = canvas.offsetHeight;
        const params = new URLSearchParams(location.search);
        this.debug = params.get('debug') !== null;
    }
    dispose() {
        if (this.renderer instanceof WebGLRenderer) {
            this.renderer.dispose();
            this.renderer = null;
            this.destroy();
        }
    }
    init(view3dConfiguration) {
        return __awaiter(this, void 0, void 0, function* () {
            this.clock = new Clock();
            this.view3dConfiguration = view3dConfiguration;
            this.initScene();
            this.initLights();
            this.updateSizes();
            this.renderer.setSize(this.width, this.height, false);
            this.cameraManager.init(view3dConfiguration, this.canvas);
            yield this.environment.init(this.renderer, this.scene);
            this.dimensionsRenderer = new DimensionsRenderer(this.width, this.height, this.dimensionOptions);
            this.initHandlers();
            this.startRendering();
            const getWidth = () => this.width;
            const getHeight = () => this.height;
            const cameraFocus = new CameraFocusController(this.cameraManager, () => ({
                width: this.width,
                height: this.height,
            }));
            this.plugin.init({
                cameraManager: this.cameraManager,
                cameraFocus,
                renderer: this.renderer,
                scene: this.scene,
                rootNode: this.rootNode,
                getWidth,
                getHeight,
            });
        });
    }
    setDimensionOptions(options) {
        var _a;
        this.dimensionOptions = options;
        (_a = this.dimensionsRenderer) === null || _a === void 0 ? void 0 : _a.setDimensionOptions(options);
    }
    showSceneV2() {
        return __awaiter(this, void 0, void 0, function* () {
            const rootContent = new Object3D();
            rootContent.name = 'Root__content';
            yield this.builder.buildTo(rootContent);
            if (this.builder.getContext) {
                const context = this.builder.getContext();
                yield renderSlotsInto(yield context.getSlots(), context.getView3d(), rootContent);
            }
            this.rootNode.clear();
            this.rootNode.add(rootContent);
            if (this.showRulerFlag) {
                this.showRuler();
            }
            this.plugin.onUpdateLayout();
        });
    }
    showScene() {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error('Not supported');
        });
    }
    exportGltfScene() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.gltfExporter.export(this.createExportScene());
        });
    }
    exportUsdzScene() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.usdzExporter.export(this.createExportScene());
        });
    }
    destroy() {
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('toggle-fullscreen', this.resizeHandler);
    }
    initLights() {
        const ambientLight = new AmbientLight('#ffffff', 0.3);
        this.scene.add(ambientLight);
    }
    startRendering() {
        requestAnimationFrame(() => {
            var _a, _b;
            this.cameraManager.update();
            const delta = (_b = (_a = this.clock) === null || _a === void 0 ? void 0 : _a.getDelta()) !== null && _b !== void 0 ? _b : 0;
            this.plugin.update(delta);
            if (this.renderer instanceof WebGLRenderer) {
                this.renderer.clear();
                this.renderer.render(this.scene, this.cameraManager.camera);
                if (this.cameraManager && this.rootNode) {
                    this.dimensionsRenderer.render(this.dimensionScene2D, this.dimensionLinesNode, this.rootNode, this.cameraManager.camera, this.cameraManager.getControlsAzimuthalAngle(), this.showRulerFlag);
                }
                this.plugin.render();
                this.startRendering();
            }
        });
    }
    initScene() {
        this.scene = new Scene();
        this.sceneNode = new Object3D();
        this.sceneNode.name = 'Scene';
        this.scene.add(this.sceneNode);
        this.dimensionScene2D = new Scene();
        this.rootNode = new Object3D();
        this.rootNode.name = 'Root';
        this.sceneNode.add(this.rootNode);
        this.dimensionLinesNode = new Object3D();
        this.dimensionLinesNode.name = 'Ruler';
        this.scene.add(this.dimensionLinesNode);
        if (this.debug) {
            const axesHelper = new AxesHelper(1);
            this.scene.add(axesHelper);
        }
        this.applyConfig();
    }
    resize() {
        this.resizeHandler();
    }
    initHandlers() {
        window.addEventListener('resize', this.resizeHandler);
        window.addEventListener('toggle-fullscreen', this.resizeHandler);
    }
    updateSizes() {
        const parent = this.canvas.parentElement;
        if (parent) {
            this.height = parent.clientHeight;
            this.width = parent.clientWidth;
        }
        else {
            console.error('No parent for canvas found');
        }
    }
    hideRuler() {
        this.showRulerFlag = false;
        this.dimensionsRenderer.clearScene(this.dimensionScene2D, this.dimensionLinesNode);
    }
    showRuler() {
        this.showRulerFlag = true;
        this.dimensionsRenderer.buildRuler(this.dimensionScene2D, this.dimensionLinesNode, this.rootNode, this.cameraManager.getControlsAzimuthalAngle());
    }
    createExportScene() {
        const sceneCopy = this.sceneNode.clone(true);
        const meshesToRemove = [];
        sceneCopy.traverse((node) => {
            const userData = getUserData(node);
            if (hasUserData(node) && !userData.webAr.visible) {
                meshesToRemove.push(node);
            }
            else {
                node.visible = true;
            }
        });
        for (const mesh of meshesToRemove) {
            mesh.removeFromParent();
        }
        return sceneCopy;
    }
    updateMaterials(materials) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.all(materials.map((material) => this.materialsLibrary.updateMaterial(material)));
        });
    }
    createScreenshot(width, height, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const cameraPosition = (_a = options === null || options === void 0 ? void 0 : options.cameraPosition) !== null && _a !== void 0 ? _a : this.view3dConfiguration.pdfCameraPosition;
            const glbConverter = new GlbConverter(width, height);
            this.plugin.suspendForCapture();
            let container;
            try {
                container = this.sceneNode.clone(true);
            }
            finally {
                this.plugin.resumeAfterCapture();
            }
            return yield glbConverter.convertObject3DToBase64(container, this.view3dConfiguration.hdr, {
                cameraPosition,
            });
        });
    }
    applyConfig() {
        const sceneConfig = this.sceneConfig.scene;
        if (sceneConfig) {
            if (sceneConfig.environmentIntensity) {
                this.scene.environmentIntensity = sceneConfig.environmentIntensity;
            }
        }
    }
}

class View3dPlugin {
    constructor() {
        this.options = null;
    }
    init(options) {
        this.options = options;
        this.onInit(options);
    }
    onInit(_options) { }
    findNodeByName(name) {
        var _a;
        if (!((_a = this.options) === null || _a === void 0 ? void 0 : _a.rootNode))
            return;
        let result = undefined;
        this.options.rootNode.traverse((node) => {
            if (node.name === name) {
                result = node;
            }
        });
        return result;
    }
    findNodeContainer(key) {
        return this.findNodeByName(ViewNode.getContainerNodeName(key));
    }
    findNodeByKey(key) {
        return this.findNodeByName(ViewNode.getContentNodeName(key));
    }
    onDestroy() { }
    onUpdateLayout() { }
    suspendForCapture() { }
    resumeAfterCapture() { }
    update(_delta) { }
    render() { }
}

class View3dFactory {
    constructor(canvas) {
        this.canvas = canvas;
    }
    create(view3dConfiguration_1) {
        return __awaiter(this, arguments, void 0, function* (view3dConfiguration, plugin = new View3dPlugin()) {
            var _a;
            const usdzExporter = new UsdzExporter();
            const gltfExporter = new GltfExporter();
            const cameraManager = new OrbitControlsCameraManager();
            const hdrEnvironment = new HdrEnvironment(view3dConfiguration);
            const modelLoader = new ModelLoader();
            const textureLoader = new ClTextureLoader();
            const materialLoader = new MaterialLoader();
            const textureLibrary = new TextureLibrary(textureLoader);
            textureLibrary.addTextures(view3dConfiguration.assets.textures);
            const materialsLibrary = new MaterialsLibrary(materialLoader, textureLibrary);
            materialsLibrary.addMaterials(view3dConfiguration.assets.materials);
            const renderer = new ThreeJSViewRenderer(this.canvas, view3dConfiguration);
            renderer.autoClear = false;
            renderer.setClearColor(view3dConfiguration.clearColor, ((_a = view3dConfiguration.renderer) === null || _a === void 0 ? void 0 : _a.transparent) ? 0 : 1);
            const threeJsView3d = new ConfiguratorView3D(this.canvas, view3dConfiguration, gltfExporter, usdzExporter, cameraManager, hdrEnvironment, modelLoader, materialsLibrary, renderer, plugin);
            yield threeJsView3d.init(view3dConfiguration);
            return threeJsView3d;
        });
    }
    createJointManager(configuration) {
        return __awaiter(this, void 0, void 0, function* () {
            const cameraManager = new OrbitControlsCameraManager();
            const hdrEnvironment = new HdrEnvironment({ hdr: configuration.hdr });
            const modelLoader = new ModelLoader();
            new MaterialLoader();
            const renderer = new ThreeJSViewRenderer(this.canvas, configuration);
            const jointManagerView3D = new JointManagerView3D(this.canvas, cameraManager, hdrEnvironment, modelLoader, renderer);
            yield jointManagerView3D.init(configuration);
            return jointManagerView3D;
        });
    }
    createMaterialManager(material, hdr) {
        return __awaiter(this, void 0, void 0, function* () {
            const cameraManager = new OrbitControlsCameraManager();
            const renderer = new ThreeJSViewRenderer(this.canvas, {});
            const materialManagerView3D = new MaterialManagerView3D(this.canvas, cameraManager, renderer, material, hdr);
            yield materialManagerView3D.init();
            return materialManagerView3D;
        });
    }
    createV2(view3dConfiguration_1, builder_1) {
        return __awaiter(this, arguments, void 0, function* (view3dConfiguration, builder, plugin = new View3dPlugin()) {
            var _a;
            const usdzExporter = new UsdzExporter();
            const gltfExporter = new GltfExporter();
            const cameraManager = new OrbitControlsCameraManager();
            const hdrEnvironment = new HdrEnvironment(view3dConfiguration, builder.assetsManager);
            const modelLoader = new ModelLoader();
            const textureLoader = new ClTextureLoader();
            const materialLoader = new MaterialLoader();
            const textureLibrary = new TextureLibrary(textureLoader);
            textureLibrary.addTextures(view3dConfiguration.assets.textures);
            const materialsLibrary = new MaterialsLibrary(materialLoader, textureLibrary);
            materialsLibrary.addMaterials(view3dConfiguration.assets.materials);
            const renderer = new ThreeJSViewRenderer(this.canvas, view3dConfiguration);
            renderer.autoClear = false;
            renderer.setClearColor(view3dConfiguration.clearColor, ((_a = view3dConfiguration.renderer) === null || _a === void 0 ? void 0 : _a.transparent) ? 0 : 1);
            const threeJsView3d = new ConfiguratorRenderer(view3dConfiguration, builder, this.canvas, gltfExporter, usdzExporter, cameraManager, hdrEnvironment, modelLoader, materialsLibrary, renderer, plugin);
            yield threeJsView3d.init(view3dConfiguration);
            return threeJsView3d;
        });
    }
}

class SceneSpecificationBuilderBase {
    constructor() {
        this.activeLayoutRules = new Map();
        this.partsMeshesSceneVisibility = new Map();
    }
    build() {
        const rootPartId = '';
        const parts = [
            {
                id: rootPartId,
                fileName: '',
                meshes: [],
                visible: true,
                materials: [],
                takePlace: true,
            },
        ];
        const availableParts = new Set();
        const connections = this.createConnections(availableParts);
        traverseForest(this.getNodes(), (partConfiguration) => {
            if (availableParts.has(partConfiguration.partKey)) {
                const part = this.createSceneSpecificationPart(partConfiguration);
                if (part) {
                    parts.push(part);
                }
            }
        });
        return {
            materials: this.createMaterialChanges(),
            parts,
            connections,
            rootPartId,
        };
    }
    createConnections(availablePartsKeys) {
        const connections = [];
        traverseForest(this.getNodes(), (node) => {
            return this.createConnection(node, connections, availablePartsKeys);
        });
        return connections;
    }
    createConnection(node, allConnections, availablePartsKeys) {
        const nodeModel = this.getNodeModel3d(node);
        if (!nodeModel) {
            console.warn(`Cannot find model for ${node.partKey}`);
            return false;
        }
        availablePartsKeys.add(node.partKey);
        const layout = this.getNodeActiveLayout(node);
        if (layout.type === 'rootPartLayout') {
            const modelKey = nodeModel.key;
            let transform = layout.modelsTransforms[modelKey];
            if (!transform) {
                transform = defaultTransform;
            }
            const parentSpec = {
                id: '',
                model3dKey: '',
                jointPoint: {
                    rotation: transform.rotation,
                    position: transform.translation,
                },
            };
            const nodeSpec = {
                id: node.partKey,
                model3dKey: nodeModel.key,
                jointPoint: defaultJP,
            };
            allConnections.push({ parts: [parentSpec, nodeSpec] });
            return true;
        }
        const parentNode = this.getNodeActiveParent(node);
        if (!parentNode) {
            throw new Error(`Invalid configuration hierarchy. Expected parent for part ${node.partKey}`);
        }
        const parentModel = this.getNodeModel3d(parentNode);
        if (!parentModel) {
            console.warn(`Cannot find parent model for ${node.partKey}`);
            return false;
        }
        const parentSpec = {
            id: parentNode.partKey,
            model3dKey: parentModel.key,
            jointPoint: this.getJointPoint(parentModel, layout.parentJointPointKey),
        };
        const nodeSpec = {
            id: node.partKey,
            model3dKey: nodeModel.key,
            jointPoint: this.getJointPoint(nodeModel, layout.mainJointPointKey),
        };
        allConnections.push({
            parts: [parentSpec, nodeSpec],
        });
        return true;
    }
    getJointPoint(model3dDto, jpKey) {
        var _a;
        const jp = model3dDto.model3dJointPoints.find((jp) => jp.key === jpKey);
        return (_a = jp === null || jp === void 0 ? void 0 : jp.jointPoint) !== null && _a !== void 0 ? _a : defaultJP;
    }
    activateRule(partKey, ruleKey) {
        let partRules = [];
        traverseForest(this.getNodes(), (node) => {
            if (node.partKey == partKey) {
                partRules = node.rules;
            }
        });
        const partRule = partRules.find((it) => it.key === ruleKey);
        if (!partRule) {
            throw new Error(`Cannot activate rule "${ruleKey}" for part "${partKey}"`);
        }
        if (partRule.type === 'layoutRule') {
            this.activeLayoutRules.set(partKey, partRule);
        }
    }
    deactivateRules() {
        this.activeLayoutRules.clear();
        this.partsMeshesSceneVisibility.clear();
    }
    getNodeActiveLayout(node) {
        const rule = this.activeLayoutRules.get(node.partKey);
        if (rule) {
            if (rule.type !== 'layoutRule') {
                throw new Error(`Unexpected rule type for ${node.partKey}. Expected "layoutRule" but got "${rule.type}"`);
            }
            return rule.layout;
        }
        return node.layout;
    }
    getNodeActiveParent(node) {
        const partKey = node.partKey;
        const rule = this.activeLayoutRules.get(partKey);
        if (rule) {
            if (rule.type !== 'layoutRule') {
                throw new Error(`Unexpected rule type for ${partKey}. Expected "layoutRule" but got "${rule.type}"`);
            }
            const [node] = findPartNodes(this.getNodes(), [rule.parentPartKey]);
            if (!node) {
                throw new Error(`Cannot find parent node "${rule.parentPartKey}"`);
            }
            return node;
        }
        return node.parent;
    }
}

class SceneSpecificationBuilder extends SceneSpecificationBuilderBase {
    constructor(dynamicValueService, view3DConfiguration) {
        super();
        this.dynamicValueService = dynamicValueService;
        this.view3DConfiguration = view3DConfiguration;
    }
    buildSpec(productVariant) {
        this.productVariant = productVariant;
        this.deactivateRules();
        this.applyRules();
        return this.build();
    }
    getNodes() {
        return this.view3DConfiguration.nodes;
    }
    createSceneSpecificationPart(node) {
        var _a, _b;
        const partConfiguration = node;
        const productVariant = this.productVariant;
        const visible = true;
        const referenceId = this.getReferenceId(partConfiguration);
        if (!referenceId) {
            return;
        }
        const modelConfiguration = this.view3DConfiguration.views.find((model) => model.id === referenceId);
        if (!modelConfiguration) {
            console.warn(`Cannot find model with id ${referenceId} for part ${partConfiguration.partKey}`);
            return undefined;
        }
        const materials = this.createPartMaterialSpecification(partConfiguration.materials || [], productVariant);
        const meshes = this.createMeshSpecifications(partConfiguration);
        return {
            id: partConfiguration.partKey,
            visible: visible,
            takePlace: (_b = (_a = node === null || node === void 0 ? void 0 : node.settings) === null || _a === void 0 ? void 0 : _a.takePlace) !== null && _b !== void 0 ? _b : true,
            meshes,
            fileName: modelConfiguration.url,
            materials,
        };
    }
    createPartMaterialSpecification(materialConfigurations, configuration) {
        const result = [];
        for (const materialConfiguration of materialConfigurations) {
            const materialReferenceId = this.dynamicValueService.calculate(materialConfiguration.material, configuration);
            if (!materialReferenceId) {
                console.warn(`Skipped material for "${JSON.stringify(materialConfiguration.material)}"`);
                continue;
            }
            const material = {
                materialId: materialReferenceId,
                mesh: materialConfiguration.mesh,
            };
            result.push(material);
        }
        return result;
    }
    createMaterialChanges() {
        var _a;
        const configuration = this.productVariant;
        const changes = [];
        for (const material of (_a = this.view3DConfiguration.configDto.materials) !== null && _a !== void 0 ? _a : []) {
            const type = material.type;
            switch (type) {
                case 'standardMaterial': {
                    const change = {
                        id: material.id,
                        type: 'standardMaterialAsset',
                    };
                    for (const optionName of STANDARD_MATERIAL_OPTIONS) {
                        if (optionName in material.options) {
                            change[optionName] = this.dynamicValueService.calculate(material.options[optionName], configuration);
                        }
                    }
                    changes.push(change);
                    break;
                }
                case 'physicalMaterial': {
                    const change = {
                        id: material.id,
                        type: 'physicalMaterialAsset',
                    };
                    for (const optionName of PHYSICAL_MATERIAL_OPTIONS) {
                        if (optionName in material.options) {
                            change[optionName] = this.dynamicValueService.calculate(material.options[optionName], configuration);
                        }
                    }
                    changes.push(change);
                    break;
                }
                case 'meshMaterial':
                case 'shaderMaterial':
                    console.warn(`Material changes only supported for standardMaterial. Skipped "${material.type}"`);
                    continue;
                default:
                    assertUnreachable(type, 'Invalid material type');
            }
        }
        return changes;
    }
    getNodeModel3d(node) {
        const referenceId = this.getReferenceId(node);
        const modelConfiguration = this.view3DConfiguration.views.find((model) => model.id === referenceId);
        if (!modelConfiguration) {
            console.warn(`Cannot find model with id ${referenceId} in models`);
        }
        return modelConfiguration;
    }
    getReferenceId(node) {
        const component = this.productVariant[node.componentKey];
        const modelAttribute = component ? component[StandardAttributes.model3dId] : undefined;
        return modelAttribute ? getAttributeValue(modelAttribute) : undefined;
    }
    applyRules() {
        traverseForest(this.getNodes(), (node) => {
            this.applyNodeRules(node);
        });
    }
    applyNodeRules(node) {
        const meshesVisibility = {};
        for (const rule of node.rules) {
            const ruleJS = new Function('product', rule.condition.fn);
            const isActive = Boolean(ruleJS(this.productVariant));
            if (isActive) {
                if (rule.type === 'layoutRule') {
                    this.activateRule(node.partKey, rule.key);
                    break;
                }
                else {
                    rule.meshes.forEach((mesh) => {
                        var _a, _b;
                        if (!(mesh in meshesVisibility)) {
                            meshesVisibility[mesh] = {
                                scene: true,
                                webAr: true,
                            };
                        }
                        if (rule.hideInScene) {
                            (_a = meshesVisibility[mesh]).scene && (_a.scene = !isActive);
                        }
                        if (rule.hideInWebAR) {
                            (_b = meshesVisibility[mesh]).webAr && (_b.webAr = !isActive);
                        }
                    });
                }
            }
        }
        this.partsMeshesSceneVisibility.set(node.partKey, meshesVisibility);
    }
    createMeshSpecifications(node) {
        const meshesVisibility = this.partsMeshesSceneVisibility.get(node.partKey);
        if (!meshesVisibility) {
            return [];
        }
        const result = [];
        for (const [meshName, visible] of Object.entries(meshesVisibility)) {
            result.push({
                mesh: meshName,
                visible: visible.scene,
                webAr: {
                    visible: visible.webAr,
                },
            });
        }
        return result;
    }
}

function getSelectorValue(selector, model, defaultValue) {
    if (selector === '.') {
        return model;
    }
    const selectorParts = selector.split('.');
    let value = model;
    while (selectorParts.length) {
        const part = selectorParts.shift();
        value = value[part];
        if (value === null || value === undefined) {
            if (arguments.length === 3) {
                return defaultValue;
            }
            throw new Error(`Cannot get value for selector "${selector}" from model "${JSON.stringify(model)}"`);
        }
    }
    return value;
}
function matchObject(mapper, model, emptyValue) {
    const selectorValue = getSelectorValue(mapper.selector, model, null);
    if (selectorValue === null) {
        return emptyValue;
    }
    const matcherValue = mapper.match[selectorValue];
    if (matcherValue == null || undefined) {
        const wildCardValue = mapper.match['*'];
        if (typeof wildCardValue === 'string') {
            if (wildCardValue === '*') {
                return selectorValue;
            }
            if (wildCardValue === '$') {
                return model;
            }
        }
        if (wildCardValue !== undefined) {
            return wildCardValue;
        }
        throw new Error(`Cannot find match for "${selectorValue}" in "${JSON.stringify(mapper.match)}"`);
    }
    return matcherValue;
}

class InvalidViewError extends Error {
    constructor(message, view) {
        super(message);
        this.view = view;
    }
}

class View3dConfiguration {
    constructor(configDto) {
        var _a, _b, _c, _d;
        this.configDto = configDto;
        this.nodes = [];
        this.existingMaterials = new Set();
        this.existingViews = new Set();
        this.existingParts = new Set();
        this.views = configDto.views;
        this.parts = configDto.parts;
        this.nodes = createNodes(configDto.parts);
        this.pdfCameraPosition = configDto.pdfCameraPosition;
        this.scene = configDto.scene;
        this.renderer = configDto.renderer;
        this.camera = this.createCameraConfig(configDto.camera);
        this.hdr = getAdaptiveConfigurationValue((_a = configDto.hdr) !== null && _a !== void 0 ? _a : '');
        this.clearColor = configDto.clearColor || '#eeee';
        this.assets = {
            materials: (_c = (_b = configDto.materials) === null || _b === void 0 ? void 0 : _b.map(createMaterial)) !== null && _c !== void 0 ? _c : [],
            models: [],
            textures: (_d = configDto.textures) !== null && _d !== void 0 ? _d : [],
        };
        this.validateInvariants();
    }
    validateInvariants() {
        this.registerViews();
        this.registerParts();
        this.registerMaterials();
        this.validateViewsInvariants();
        this.validateHierarchyInvariants();
        this.validateMaterialsInvariants();
        this.validatePartsInvariants();
    }
    validateHierarchyInvariants() {
        const visitedParts = new Set();
        const parts = new Set(this.parts.map((part) => part.partKey));
        traverseForest(this.nodes, (next) => {
            visitedParts.add(next.partKey);
        });
        const invalidParts = [];
        for (const part of parts) {
            if (!visitedParts.has(part)) {
                invalidParts.push(part);
            }
        }
        if (invalidParts.length > 0) {
            invalidParts.sort();
            throw new Error(`Invalid hierarchy for parts "${invalidParts.join(', ')}"`);
        }
    }
    validateViewsInvariants() {
        const visitedViews = new Set();
        for (const view of this.views) {
            if (view.key === '') {
                throw new InvalidViewError(`Invalid views. Empty key for view`, view);
            }
            if (view.url === '') {
                throw new InvalidViewError(`Invalid views. Empty url for view`, view);
            }
            if (visitedViews.has(view.key)) {
                throw new InvalidViewError(`Invalid views. Duplicated key "${view.key}"`, view);
            }
            visitedViews.add(view.key);
        }
    }
    createCameraConfig(camera) {
        var _a, _b, _c, _d, _e, _f, _g;
        return {
            minDistance: (_a = camera.minDistance) !== null && _a !== void 0 ? _a : View3dConfiguration.defaultCameraMinDistance,
            maxDistance: (_b = camera.maxDistance) !== null && _b !== void 0 ? _b : View3dConfiguration.defaultCameraMaxDistance,
            maxPolarAngle: (_c = camera.maxPolarAngle) !== null && _c !== void 0 ? _c : View3dConfiguration.defaultCameraMaxPolarAngle,
            minAzimuthAngle: (_d = camera.minAzimuthAngle) !== null && _d !== void 0 ? _d : View3dConfiguration.defaultMinAzimuthAngle,
            maxAzimuthAngle: (_e = camera.maxAzimuthAngle) !== null && _e !== void 0 ? _e : View3dConfiguration.defaultMaxAzimuthAngle,
            farFactor: (_f = camera.farFactor) !== null && _f !== void 0 ? _f : 2,
            position: getAdaptiveConfigurationValue(camera.position),
            lookAt: camera.lookAt ? getAdaptiveConfigurationValue(camera.lookAt) : { x: 0, y: 0, z: 0 },
            autoZoomDisable: (_g = camera.autoZoomDisable) !== null && _g !== void 0 ? _g : false,
        };
    }
    registerViews() {
        for (const view of this.views) {
            this.existingViews.add(view.key);
        }
    }
    registerParts() {
        for (const part of this.parts) {
            this.existingParts.add(part.partKey);
        }
    }
    validateMaterialsInvariants() {
        var _a;
        const visitedMaterials = new Set();
        for (const material of (_a = this.configDto.materials) !== null && _a !== void 0 ? _a : []) {
            if (material.id === '') {
                throw new Error(`Invalid materials. Empty id for material "${JSON.stringify(material)}"`);
            }
            if ('file' in material) {
                if (material.file === '') {
                    throw new Error(`Invalid materials. Empty file for material "${material.id}"`);
                }
                if (material.name === '') {
                    throw new Error(`Invalid materials. Empty name for material "${material.id}"`);
                }
            }
            if (visitedMaterials.has(material.id)) {
                throw new Error(`Invalid materials. Duplicated id "${material.id}"`);
            }
            visitedMaterials.add(material.id);
        }
    }
    validatePartsInvariants() {
        for (const part of this.parts) {
            if (part.partKey === '') {
                throw new Error(`Invalid parts. Empty id for "${part}"`);
            }
            const visitedMeshes = new Set();
            for (const material of part.materials) {
                if (visitedMeshes.has(material.mesh)) {
                    throw new Error(`Invalid parts. Materials of part "${part.partKey}" reference the same mesh "${material.mesh}"`);
                }
                visitedMeshes.add(material.mesh);
            }
        }
    }
    registerMaterials() {
        var _a;
        for (const material of (_a = this.configDto.materials) !== null && _a !== void 0 ? _a : []) {
            this.existingMaterials.add(material.id);
        }
    }
}
View3dConfiguration.defaultCameraMinDistance = 1.3;
View3dConfiguration.defaultCameraMaxDistance = 10;
View3dConfiguration.defaultCameraMaxPolarAngle = 90;
View3dConfiguration.defaultMinAzimuthAngle = Infinity;
View3dConfiguration.defaultMaxAzimuthAngle = Infinity;
function createMaterial(config) {
    const type = config.type;
    switch (type) {
        case 'shaderMaterial': {
            const shaderMaterial = {
                id: config.id,
                type: 'shaderMaterialAsset',
                fragmentShader: config.shader.fragment,
                vertexShader: config.shader.vertex,
                uniforms: config.shader.uniforms || {},
                transparent: config.shader.transparent || false,
            };
            return shaderMaterial;
        }
        case 'meshMaterial': {
            const meshMaterial = {
                id: config.id,
                type: 'meshMaterialAsset',
                fileName: config.file,
                meshName: config.name,
            };
            return meshMaterial;
        }
        case 'standardMaterial': {
            const material = {
                id: config.id,
                type: 'standardMaterialAsset',
                metalness: 0,
                roughness: 0,
                envMapIntensity: 1,
                color: '#000',
            };
            return material;
        }
        case 'physicalMaterial': {
            const material = {
                id: config.id,
                type: 'physicalMaterialAsset',
                metalness: 0,
                roughness: 0,
                color: '#000',
                clearcoat: 0,
                clearcoatRoughness: 0,
            };
            return material;
        }
        default:
            assertUnreachable(type, `Unsupported material type "${type}" for ${JSON.stringify(config)}`);
    }
}

function partId(id) {
    return id;
}

function viewId(id) {
    return id;
}

function materialId(id) {
    return id;
}

class EnvironmentPreviewService {
    constructor() {
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            preserveDrawingBuffer: true,
        });
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.0;
        this.scene = new THREE.Scene();
        this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    }
    convertToJPG(file_1) {
        return __awaiter(this, arguments, void 0, function* (file, options = {}) {
            var _a;
            const { maxWidth = 800, quality = 0.9, exposure = 1.0, toneMapping = THREE.ACESFilmicToneMapping } = options;
            const fileExtension = (_a = file.name.split('.').at(-1)) === null || _a === void 0 ? void 0 : _a.toLowerCase();
            let loader;
            if (fileExtension === 'exr') {
                loader = new EXRLoader();
            }
            else if (fileExtension === 'hdr') {
                loader = new RGBELoader$1();
            }
            else {
                throw new Error('Unsupported file format. Only .hdr and .exr files are supported.');
            }
            try {
                const fileURL = URL.createObjectURL(file);
                const texture = yield this.loadTexture(loader, fileURL);
                const imageWidth = texture.image.width;
                const imageHeight = texture.image.height;
                let width, height;
                if (imageWidth >= imageHeight) {
                    width = maxWidth;
                    height = Math.round((imageHeight / imageWidth) * maxWidth);
                }
                else {
                    height = maxWidth;
                    width = Math.round((imageWidth / imageHeight) * maxWidth);
                }
                this.renderer.setSize(width, height);
                this.renderer.toneMapping = toneMapping;
                this.renderer.toneMappingExposure = exposure;
                const material = new THREE.MeshBasicMaterial({ map: texture });
                const geometry = new THREE.PlaneGeometry(2, 2);
                const mesh = new THREE.Mesh(geometry, material);
                this.scene.add(mesh);
                this.renderer.render(this.scene, this.camera);
                const base64Data = this.renderer.domElement.toDataURL('image/jpeg', quality);
                this.scene.remove(mesh);
                geometry.dispose();
                material.dispose();
                texture.dispose();
                URL.revokeObjectURL(fileURL);
                return base64Data;
            }
            catch (error) {
                console.error('Error converting HDR/EXR to JPG:', error);
                throw error;
            }
        });
    }
    loadTexture(loader, url) {
        return new Promise((resolve, reject) => {
            loader.load(url, (texture) => resolve(texture), undefined, (error) => reject(error));
        });
    }
    dispose() {
        this.renderer.dispose();
    }
}

class GLTFMaterialParser {
    constructor() {
        this.loader = new GLTFLoader();
        const dracoLoader = new DRACOLoader$1();
        dracoLoader.setDecoderPath('./three/draco/gltf/');
        this.loader.setDRACOLoader(dracoLoader);
    }
    parseFromBuffer(buffer) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                this.loader.parse(buffer, '', (gltf) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        const materials = yield this.extractMaterials(gltf);
                        resolve(materials);
                    }
                    catch (error) {
                        reject(error);
                    }
                }), (error) => {
                    reject(new Error(`Error parsing model: ${error.message}`));
                });
            });
        });
    }
    parseFromFile(file) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (event) => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    if (((_a = event.target) === null || _a === void 0 ? void 0 : _a.result) instanceof ArrayBuffer) {
                        try {
                            const materials = yield this.parseFromBuffer(event.target.result);
                            resolve(materials);
                        }
                        catch (error) {
                            reject(error);
                        }
                    }
                    else {
                        reject(new Error('Failed to read file as ArrayBuffer'));
                    }
                });
                reader.onerror = () => {
                    reject(new Error('Error reading file'));
                };
                reader.readAsArrayBuffer(file);
            });
        });
    }
    extractMaterials(gltf) {
        return __awaiter(this, void 0, void 0, function* () {
            const materials = new Map();
            const materialProcessingPromises = [];
            gltf.scene.traverse((object) => {
                if ('material' in object) {
                    const mesh = object;
                    if (Array.isArray(mesh.material)) {
                        mesh.material.forEach((material) => {
                            if (!materials.has(material.uuid)) {
                                materialProcessingPromises.push(this.processMaterial(material, materials));
                            }
                        });
                    }
                    else if (mesh.material) {
                        if (!materials.has(mesh.material.uuid)) {
                            materialProcessingPromises.push(this.processMaterial(mesh.material, materials));
                        }
                    }
                }
            });
            yield Promise.all(materialProcessingPromises);
            return Array.from(materials.values());
        });
    }
    processMaterial(material, materials) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            if (materials.has(material.uuid)) {
                return;
            }
            const materialInfo = {
                name: material.name || `Material_${material.uuid.substring(0, 8)}`,
                type: material.type,
                uuid: material.uuid,
                transparent: material.transparent,
                opacity: material.opacity,
            };
            if (material.type === 'MeshStandardMaterial') {
                const stdMaterial = material;
                materialInfo.color = stdMaterial.color ? `#${stdMaterial.color.getHexString()}` : undefined;
                materialInfo.metalness = stdMaterial.metalness;
                materialInfo.roughness = stdMaterial.roughness;
                materialInfo.emissive = stdMaterial.emissive ? `#${stdMaterial.emissive.getHexString()}` : undefined;
                materialInfo.emissiveIntensity = stdMaterial.emissiveIntensity;
                materialInfo.map = (_a = (yield GLTFMaterialParser.serializeTexture(stdMaterial.map))) !== null && _a !== void 0 ? _a : undefined;
                materialInfo.normalMap = (_b = (yield GLTFMaterialParser.serializeTexture(stdMaterial.normalMap))) !== null && _b !== void 0 ? _b : undefined;
                materialInfo.aoMap = (_c = (yield GLTFMaterialParser.serializeTexture(stdMaterial.aoMap))) !== null && _c !== void 0 ? _c : undefined;
                materialInfo.metalnessMap = (_d = (yield GLTFMaterialParser.serializeTexture(stdMaterial.metalnessMap))) !== null && _d !== void 0 ? _d : undefined;
                materialInfo.roughnessMap = (_e = (yield GLTFMaterialParser.serializeTexture(stdMaterial.roughnessMap))) !== null && _e !== void 0 ? _e : undefined;
                materialInfo.emissiveMap = (_f = (yield GLTFMaterialParser.serializeTexture(stdMaterial.emissiveMap))) !== null && _f !== void 0 ? _f : undefined;
                materialInfo.side = stdMaterial.side;
            }
            else if (material.type === 'MeshBasicMaterial') {
                const basicMaterial = material;
                materialInfo.color = basicMaterial.color ? `#${basicMaterial.color.getHexString()}` : undefined;
                materialInfo.map = (_g = (yield GLTFMaterialParser.serializeTexture(basicMaterial.map))) !== null && _g !== void 0 ? _g : undefined;
            }
            else if (material.type === 'MeshPhongMaterial') {
                const phongMaterial = material;
                materialInfo.color = phongMaterial.color ? `#${phongMaterial.color.getHexString()}` : undefined;
                materialInfo.emissive = phongMaterial.emissive ? `#${phongMaterial.emissive.getHexString()}` : undefined;
                materialInfo.map = (_h = (yield GLTFMaterialParser.serializeTexture(phongMaterial.map))) !== null && _h !== void 0 ? _h : undefined;
                materialInfo.normalMap = (_j = (yield GLTFMaterialParser.serializeTexture(phongMaterial.normalMap))) !== null && _j !== void 0 ? _j : undefined;
            }
            else if (material.type === 'MeshPhysicalMaterial') {
                const physicalMaterial = material;
                materialInfo.color = physicalMaterial.color ? `#${physicalMaterial.color.getHexString()}` : undefined;
                materialInfo.metalness = physicalMaterial.metalness;
                materialInfo.roughness = physicalMaterial.roughness;
                materialInfo.emissive = physicalMaterial.emissive ? `#${physicalMaterial.emissive.getHexString()}` : undefined;
                materialInfo.emissiveIntensity = physicalMaterial.emissiveIntensity;
            }
            materials.set(material.uuid, materialInfo);
        });
    }
    static serializeTexture(texture) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!texture) {
                return undefined;
            }
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx && texture.image) {
                canvas.width = texture.image.width;
                canvas.height = texture.image.height;
                ctx.drawImage(texture.image, 0, 0);
            }
            const blob = yield new Promise((resolve, reject) => canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                }
                else {
                    reject(new Error('Failed to create blob from canvas'));
                }
            }, 'image/png'));
            return {
                image: blob,
                format: texture.format,
                type: texture.type,
                wrapS: texture.wrapS,
                wrapT: texture.wrapT,
                magFilter: texture.magFilter,
                minFilter: texture.minFilter,
                flipY: texture.flipY,
                offset: { x: texture.offset.x, y: texture.offset.y },
                repeat: { x: texture.repeat.x, y: texture.repeat.y },
                rotation: texture.rotation,
                center: { x: texture.center.x, y: texture.center.y },
            };
        });
    }
}

class MaterialPreviewRenderer {
    constructor() {
        this.canvas = document.createElement('canvas');
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            preserveDrawingBuffer: true,
        });
        this.textureLoader = new THREE.TextureLoader();
        this.setupScene();
    }
    setupScene() {
        const pMREMGenerator = new PMREMGenerator(this.renderer);
        this.scene.environment = pMREMGenerator.fromScene(new RoomEnvironment()).texture;
        this.camera.position.set(0, 0, 5);
        this.camera.lookAt(0, 0, 0);
    }
    createThreeMaterial(materialInfo) {
        var _a, _b;
        let material;
        switch (materialInfo.materialType) {
            case 'MeshStandardMaterial':
                material = new THREE.MeshStandardMaterial();
                const stdMat = material;
                if (materialInfo.color) {
                    stdMat.color = new THREE.Color(materialInfo.color);
                }
                if (materialInfo.metalness !== undefined) {
                    stdMat.metalness = materialInfo.metalness;
                }
                if (materialInfo.roughness !== undefined) {
                    stdMat.roughness = materialInfo.roughness;
                }
                if (materialInfo.emissive) {
                    stdMat.emissive = new THREE.Color(materialInfo.emissive);
                }
                if (materialInfo.emissiveIntensity !== undefined) {
                    stdMat.emissiveIntensity = materialInfo.emissiveIntensity;
                }
                break;
            case 'MeshPhysicalMaterial':
                material = new THREE.MeshPhysicalMaterial();
                const physMat = material;
                if (materialInfo.color) {
                    physMat.color = new THREE.Color(materialInfo.color);
                }
                if (materialInfo.metalness !== undefined) {
                    physMat.metalness = materialInfo.metalness;
                }
                if (materialInfo.roughness !== undefined) {
                    physMat.roughness = materialInfo.roughness;
                }
                if (materialInfo.emissive) {
                    physMat.emissive = new THREE.Color(materialInfo.emissive);
                }
                if (materialInfo.emissiveIntensity !== undefined) {
                    physMat.emissiveIntensity = materialInfo.emissiveIntensity;
                }
                break;
            default:
                material = new THREE.MeshBasicMaterial();
                const basicMat = material;
                if (materialInfo.color) {
                    basicMat.color = new THREE.Color(materialInfo.color);
                }
                break;
        }
        material.transparent = materialInfo.transparent;
        material.opacity = materialInfo.opacity;
        material.name = 'MATERIAL';
        material.side = [FrontSide, BackSide, DoubleSide].includes(materialInfo.side)
            ? materialInfo.side
            : THREE.FrontSide;
        for (const map of Object.values(MaterialMaps)) {
            const materialMap = materialInfo[map];
            if (materialMap) {
                const texture = new Texture(materialMap);
                texture.needsUpdate = true;
                texture.wrapS = texture.wrapT = RepeatWrapping;
                texture.repeat.set((_a = materialInfo.repeatX) !== null && _a !== void 0 ? _a : 1, (_b = materialInfo.repeatY) !== null && _b !== void 0 ? _b : 1);
                material[map] = texture;
            }
        }
        return material;
    }
    renderMaterialPreview(materialInfo_1) {
        return __awaiter(this, arguments, void 0, function* (materialInfo, { width = 512, height = 512, lightIntensity: _lightIntensity = 1, cameraDistance = 5 } = {}) {
            this.canvas.width = width;
            this.canvas.height = height;
            this.renderer.setSize(width, height);
            this.renderer.shadowMap.enabled = true;
            this.renderer.shadowMap.type = PCFSoftShadowMap;
            this.renderer.setClearColor(0xffffff);
            this.renderer.outputColorSpace = SRGBColorSpace;
            this.camera.aspect = width / height;
            this.camera.position.z = cameraDistance;
            this.camera.updateProjectionMatrix();
            const meshesToRemove = [];
            this.scene.traverse((object) => {
                if (object instanceof THREE.Mesh) {
                    meshesToRemove.push(object);
                }
            });
            meshesToRemove.forEach((mesh) => {
                this.scene.remove(mesh);
            });
            const geometry = new THREE.TorusKnotGeometry(1, 0.3, 100, 16);
            const material = this.createThreeMaterial(materialInfo);
            const torusKnot = new THREE.Mesh(geometry, material);
            this.scene.add(torusKnot);
            this.renderer.render(this.scene, this.camera);
            return new Promise((resolve, reject) => {
                this.canvas.toBlob((blob) => {
                    this.dispose();
                    if (blob) {
                        resolve(blob);
                    }
                    else {
                        reject();
                    }
                }, 'image/png');
            });
        });
    }
    dispose() {
        this.renderer.dispose();
        this.scene.traverse((object) => {
            if (object instanceof THREE.Mesh) {
                object.geometry.dispose();
                if (Array.isArray(object.material)) {
                    object.material.forEach((material) => material.dispose());
                }
                else {
                    object.material.dispose();
                }
            }
        });
        this.canvas.remove();
    }
}

function lerp(from, to, speed) {
    const amount = (1 - speed) * from + speed * to;
    return Math.abs(from - to) < 0.001 ? to : amount;
}
class Ball extends Mesh {
    constructor(geometry, material) {
        super();
        this.isAnimationStarted = false;
        this.geometry = geometry;
        this.material = material;
        this.castShadow = true;
    }
    isAnimationFinished() {
        return this.scale.x === 0;
    }
    update(delta) {
        if (this.isAnimationStarted && this.scale.x != 0) {
            this.scale.set(lerp(this.scale.x, 0, delta * 14), lerp(this.scale.y, 0, delta * 14), lerp(this.scale.z, 0, delta * 14));
        }
    }
}

class BallScene {
    constructor() {
        this.STICKS_COUNT = 123;
        this.dynamicBodies = [];
    }
    loadScene(scene, position, world) {
        return __awaiter(this, void 0, void 0, function* () {
            yield new GLTFLoader$1().loadAsync('assets/models/scene.glb').then((gltf) => {
                const sceneMesh = gltf.scene;
                sceneMesh.traverse((o) => {
                    o.castShadow = true;
                });
                sceneMesh.position.setX(-0.8);
                sceneMesh.position.setY(0.095);
                scene.add(sceneMesh);
                this.plane = gltf.scene.getObjectByName('Plane');
                this.plane.material.envMapIntensity = 1.2;
                this.plane.material.envMap = scene.environment;
                for (let i = 1; i < this.STICKS_COUNT; i++) {
                    const mesh = gltf.scene.getObjectByName('c' + i);
                    if (mesh) {
                        const worldPosition = mesh.getWorldPosition(new Vector3());
                        const shape = RAPIER.ColliderDesc.cylinder(0.5, 0.045)
                            .setRotation(new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), Math.PI / 2))
                            .setTranslation(worldPosition.x, worldPosition.y, worldPosition.z)
                            .setRestitution(0.5);
                        const body = world.createRigidBody(RAPIER.RigidBodyDesc.fixed()
                            .setTranslation(...position)
                            .setCanSleep(false));
                        world.createCollider(shape, body);
                    }
                }
            });
        });
    }
    loadBall() {
        return __awaiter(this, void 0, void 0, function* () {
            yield new GLTFLoader$1().loadAsync('assets/models/ball.glb').then((gltf) => {
                const ballMesh = gltf.scene.children[0];
                ballMesh.traverse((o) => {
                    o.castShadow = true;
                });
                this.ball = ballMesh;
            });
        });
    }
    update() {
        for (let i = 0, n = this.dynamicBodies.length; i < n; i++) {
            this.dynamicBodies[i][0].position.copy(this.dynamicBodies[i][1].translation());
            this.dynamicBodies[i][0].quaternion.copy(this.dynamicBodies[i][1].rotation());
        }
    }
}

var AuthSceneEvents;
(function (AuthSceneEvents) {
    AuthSceneEvents["throwWhiteBall"] = "throwWhiteBall";
    AuthSceneEvents["throwGreenBall"] = "throwGreenBall";
})(AuthSceneEvents || (AuthSceneEvents = {}));
class AuthSceneLogic {
    constructor(width, height, canvas) {
        this.balls = [];
        this.animatedBalls = [];
        this.receiveEvents = true;
        this.disposed = false;
        this.clock = new Clock();
        this.throwWhiteBall = this.throwWhiteBall.bind(this);
        this.throwGreenBall = this.throwGreenBall.bind(this);
        this.removeBall = this.removeBall.bind(this);
        this.ballScene = new BallScene();
        const gravity = new RAPIER.Vector3(0.0, -6.5, 0.0);
        this.world = new RAPIER.World(gravity);
        this.dynamicBodies = [];
        this.scene = new Scene();
        const light1 = new DirectionalLight(undefined, 1.2);
        light1.position.set(5, 11, 5);
        light1.castShadow = true;
        light1.shadow.mapSize = new Vector2(256, 256);
        light1.shadow.camera.far = 12;
        light1.shadow.blurSamples = 25;
        light1.shadow.radius = 25;
        const targetObject = new Object3D();
        targetObject.position.set(0, 8, 0);
        this.scene.add(targetObject);
        light1.target = targetObject;
        this.scene.add(light1);
        const light2 = light1.clone();
        light2.position.set(-5, 11, 5);
        light2.target = targetObject;
        this.scene.add(light2);
        this.camera = new PerspectiveCamera(35, (0.5 * width) / height, 0.1, 100);
        this.camera.position.set(-1.8, 7.5, 6.7);
        this.renderer = new WebGLRenderer({ antialias: true, canvas });
        this.renderer.setSize(0.5 * width, height);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = PCFSoftShadowMap;
        this.renderer.toneMapping = ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1;
    }
    static initRapier() {
        return __awaiter(this, void 0, void 0, function* () {
            yield RAPIER.init();
        });
    }
    loadBallScene() {
        return __awaiter(this, void 0, void 0, function* () {
            yield new EXRLoader().load('assets/env/studio_small_08_1k.exr', (texture) => {
                const pMREMGenerator = new PMREMGenerator(this.renderer);
                pMREMGenerator.compileEquirectangularShader();
                const prTexture = pMREMGenerator.fromEquirectangular(texture).texture;
                this.scene.environment = prTexture;
                this.scene.background = prTexture;
            });
            this.bs = new BallScene();
            yield this.bs.loadBall();
            yield this.bs.loadScene(this.scene, [0, 0, 0], this.world);
        });
    }
    updateCameraOnWindowResize(width, height) {
        this.camera.aspect = (0.5 * width) / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(0.5 * width, height);
    }
    throwWhiteBall() {
        if (this.receiveEvents) {
            this.createBall(0xffffff);
        }
    }
    throwGreenBall() {
        if (this.receiveEvents) {
            this.createBall(0x7ba158);
        }
    }
    createBall(ballColor) {
        const sphereMesh = new Ball(this.bs.ball.geometry, new MeshStandardMaterial({
            roughness: 0.3,
            color: ballColor,
        }));
        this.scene.add(sphereMesh);
        this.balls.push(sphereMesh);
        const sphereBody = this.world.createRigidBody(RAPIER.RigidBodyDesc.dynamic()
            .setTranslation(Math.random() * -3, 10, 0.2)
            .setCanSleep(false));
        const sphereShape = RAPIER.ColliderDesc.ball(0.2).setMass(1).setRestitution(0.5);
        this.world.createCollider(sphereShape, sphereBody);
        this.dynamicBodies.push([sphereMesh, sphereBody]);
    }
    removeBall(event) {
        if (event.key === 'Backspace') {
            if (this.balls.length > 0) {
                const bl = this.balls.pop();
                bl.isAnimationStarted = true;
                this.animatedBalls.push(bl);
                this.receiveEvents = false;
            }
        }
        else {
            this.receiveEvents = true;
        }
    }
    animate() {
        if (this.disposed) {
            return;
        }
        requestAnimationFrame(this.animate.bind(this));
        const delta = this.clock.getDelta();
        this.world.timestep = Math.min(delta, 0.1);
        this.world.step();
        this.animatedBalls.forEach((ball, index) => {
            if (ball.isAnimationStarted && !ball.isAnimationFinished()) {
                ball.update(delta);
            }
            else if (ball.isAnimationFinished()) {
                this.scene.remove(ball);
                this.animatedBalls.splice(index, 1);
            }
        });
        this.balls.forEach((ball, index) => {
            if (ball.position.y > 0 && ball.position.y < 4) {
                this.scene.remove(ball);
                this.balls.splice(index, 1);
            }
        });
        for (let i = 0, n = this.dynamicBodies.length; i < n; i++) {
            this.dynamicBodies[i][0].position.copy(this.dynamicBodies[i][1].translation());
            this.dynamicBodies[i][0].quaternion.copy(this.dynamicBodies[i][1].rotation());
        }
        this.ballScene.update();
        this.renderer.render(this.scene, this.camera);
    }
    dispose() {
        this.disposed = true;
        this.renderer.dispose();
    }
}

class JointPointsPlugin extends View3dPlugin {
    constructor() {
        super(...arguments);
        this.control = null;
        this.onJointPointChanged = () => { };
        this.layout = null;
        this.prevTransformMatrix = null;
        this.jointPoint = null;
        this.partKey = '';
        this.isRootPart = false;
        this.handleTransformControlDragging = (event) => {
            if (!this.options) {
                return;
            }
            this.options.cameraManager.enabled = !event.value;
        };
        this.handleTransformControlChange = () => {
            const position = this.jointPointNode.position.clone();
            const rot = this.jointPointNode.rotation.clone();
            const jointPoint = {
                position: getXYZ(position),
                rotation: {
                    x: rot.x,
                    y: rot.y,
                    z: rot.z,
                },
            };
            const jp = Object.assign(Object.assign({}, this.jointPointNode.userData), { jointPoint });
            this.onJointPointChanged(jp, this.isRootPart);
        };
        this.detectChanges = () => {
            if (!this.prevTransformMatrix) {
                return;
            }
            if (!this.jointPointNode.matrixWorld.equals(this.prevTransformMatrix)) {
                this.prevTransformMatrix.copy(this.jointPointNode.matrixWorld);
                this.handleTransformControlChange();
            }
        };
    }
    onInit(_options) {
        this.createTransformControl();
        this.setSpace('local');
    }
    onDestroy() {
        var _a, _b;
        (_a = this.control) === null || _a === void 0 ? void 0 : _a.removeEventListener('dragging-changed', this.handleTransformControlDragging);
        (_b = this.control) === null || _b === void 0 ? void 0 : _b.removeEventListener('change', this.handleTransformControlChange);
    }
    onUpdateLayout() {
        this.redrawJointPoint();
    }
    setMode(mode) {
        if (this.control) {
            this.control.setMode(mode);
        }
    }
    setSpace(space) {
        if (this.control) {
            this.control.setSpace(space);
        }
    }
    setActiveJointPoint(partKey, jointPoint, isRootPart = false) {
        this.partKey = partKey;
        this.isRootPart = isRootPart;
        this.jointPoint = jointPoint;
        this.redrawJointPoint();
    }
    redrawJointPoint() {
        if (!this.jointPoint) {
            this.hide();
            return;
        }
        if (!this.control) {
            return;
        }
        const jointPoint = this.jointPoint;
        const partKey = this.partKey;
        const { name, jointPoint: { position, rotation }, } = jointPoint;
        const container = this.findNodeContainer(partKey);
        if (!container) {
            console.warn(`Cannot find container for ${partKey}`);
            return;
        }
        if (this.isRootPart) {
            this.jpContainer.position.copy(new Vector3());
            this.jpContainer.quaternion.copy(new Quaternion());
        }
        else {
            container.getWorldPosition(this.jpContainer.position);
            container.getWorldQuaternion(this.jpContainer.quaternion);
        }
        this.show();
        this.jointPointNode.rotation.set(rotation.x, rotation.y, rotation.z);
        const posVector = new Vector3(position.x, position.y, position.z);
        this.jointPointNode.position.copy(posVector);
        this.jointPointNode.name = name;
        this.jointPointNode.userData = jointPoint;
        this.control.enabled = true;
        this.control.getHelper().visible = true;
        setTimeout(() => {
            this.prevTransformMatrix = new Matrix4();
            this.prevTransformMatrix.copy(this.jointPointNode.matrixWorld);
        });
    }
    unsetActiveJointPoint() {
        this.jointPoint = null;
        this.hide();
    }
    hide() {
        if (this.control) {
            this.control.enabled = false;
            this.control.showX = false;
            this.control.showY = false;
            this.control.showZ = false;
        }
    }
    show() {
        if (this.control) {
            this.control.showX = true;
            this.control.showY = true;
            this.control.showZ = true;
        }
    }
    createTransformControl() {
        if (!this.options) {
            return;
        }
        this.jointPointNode = new Object3D();
        this.jointPointNode.name = '';
        this.control = new TransformControls(this.options.cameraManager.camera, this.options.renderer.domElement);
        this.control.enabled = false;
        this.control.attach(this.jointPointNode);
        this.control.addEventListener('dragging-changed', this.handleTransformControlDragging);
        this.control.addEventListener('change', this.detectChanges);
        const gizmo = this.control.getHelper();
        this.options.scene.add(gizmo);
        this.attachNodes();
        this.control.getHelper().visible = true;
        if (!this.jointPoint) {
            this.unsetActiveJointPoint();
        }
    }
    attachNodes() {
        this.jpContainer = new Object3D();
        this.jpContainer.name = 'jp-container';
        this.jpContainer.add(this.jointPointNode);
        if (this.options) {
            this.options.rootNode.add(this.jpContainer);
        }
    }
}
function getXYZ(vector) {
    return {
        x: getRoundedValue(vector.x),
        y: getRoundedValue(vector.y),
        z: getRoundedValue(vector.z),
    };
}
function getRoundedValue(value) {
    if (Math.abs(value) <= 1e-6) {
        return 0;
    }
    return Math.round(value * 1e3) / 1e3;
}

const ACCENT_COLOR = 0x52c41a;
const OPEN_BOUND_EXTENT = 0.5;
const FILL_BASE_ALPHA = 0.14;
class SlotEditPlugin extends View3dPlugin {
    constructor() {
        super(...arguments);
        this.control = null;
        this.spec = null;
        this.gizmoActive = false;
        this.visual = null;
        this.dragStartProxyWorld = null;
        this.dragStartGroupLocal = null;
        this.onSlotOriginDelta = () => { };
        this.onDraggingChanged = () => { };
        this.handleDraggingChanged = (event) => {
            const dragging = !!event.value;
            if (this.options) {
                this.options.cameraManager.enabled = !dragging;
            }
            this.onDraggingChanged(dragging);
            const group = this.findSlotGroup();
            if (dragging) {
                this.dragStartProxyWorld = this.proxy.position.clone();
                this.dragStartGroupLocal = group ? group.position.clone() : null;
                return;
            }
            if (!this.dragStartProxyWorld || !this.spec)
                return;
            const worldDelta = this.proxy.position.clone().sub(this.dragStartProxyWorld);
            this.dragStartProxyWorld = null;
            this.dragStartGroupLocal = null;
            if (worldDelta.lengthSq() === 0)
                return;
            const localDelta = this.worldDeltaToParentLocal(worldDelta, group);
            const axes = this.spec.editableAxes;
            this.onSlotOriginDelta(this.spec.slotKey, {
                x: axes.x ? localDelta.x : 0,
                y: axes.y ? localDelta.y : 0,
                z: axes.z ? localDelta.z : 0,
            });
        };
        this.handleChange = () => {
            if (!this.dragStartProxyWorld || !this.dragStartGroupLocal)
                return;
            const group = this.findSlotGroup();
            if (!group)
                return;
            const worldDelta = this.proxy.position.clone().sub(this.dragStartProxyWorld);
            const localDelta = this.worldDeltaToParentLocal(worldDelta, group);
            if (this.spec) {
                const axes = this.spec.editableAxes;
                if (!axes.x)
                    localDelta.x = 0;
                if (!axes.y)
                    localDelta.y = 0;
                if (!axes.z)
                    localDelta.z = 0;
            }
            group.position.copy(this.dragStartGroupLocal.clone().add(localDelta));
            group.updateMatrixWorld();
        };
    }
    onInit(_options) {
        var _a, _b;
        this.proxy = new Object3D();
        this.proxy.name = 'slot-edit-proxy';
        (_a = this.options) === null || _a === void 0 ? void 0 : _a.scene.add(this.proxy);
        this.control = new TransformControls(this.options.cameraManager.camera, this.options.renderer.domElement);
        this.control.setMode('translate');
        this.control.setSpace('world');
        this.control.enabled = false;
        this.control.attach(this.proxy);
        this.control.addEventListener('dragging-changed', this.handleDraggingChanged);
        this.control.addEventListener('change', this.handleChange);
        (_b = this.options) === null || _b === void 0 ? void 0 : _b.scene.add(this.control.getHelper());
        this.hideGizmo();
    }
    onDestroy() {
        var _a, _b;
        (_a = this.control) === null || _a === void 0 ? void 0 : _a.removeEventListener('dragging-changed', this.handleDraggingChanged);
        (_b = this.control) === null || _b === void 0 ? void 0 : _b.removeEventListener('change', this.handleChange);
        this.disposeVisual();
    }
    onUpdateLayout() {
        this.redraw();
    }
    setActiveSlot(spec) {
        this.spec = spec;
        if (!spec) {
            this.gizmoActive = false;
        }
        this.redraw();
    }
    setGizmoActive(active) {
        this.gizmoActive = active;
        this.redraw();
    }
    isGizmoActive() {
        return this.gizmoActive;
    }
    findSlotGroup() {
        return this.spec ? this.findNodeByName(slotGroupName(this.spec.slotKey)) : undefined;
    }
    redraw() {
        this.disposeVisual();
        const spec = this.spec;
        if (!spec) {
            this.hideGizmo();
            return;
        }
        const group = this.findSlotGroup();
        if (!group) {
            this.hideGizmo();
            return;
        }
        this.visual = this.buildVisual(spec, group);
        group.add(this.visual);
        if (this.gizmoActive && this.control) {
            group.getWorldPosition(this.proxy.position);
            this.proxy.quaternion.identity();
            this.control.enabled = true;
            this.control.getHelper().visible = true;
            this.control.showX = spec.editableAxes.x;
            this.control.showY = spec.editableAxes.y;
            this.control.showZ = spec.editableAxes.z;
        }
        else {
            this.hideGizmo();
        }
    }
    hideGizmo() {
        if (this.control) {
            this.control.enabled = false;
            this.control.getHelper().visible = false;
            this.control.showX = false;
            this.control.showY = false;
            this.control.showZ = false;
        }
    }
    buildVisual(spec, group) {
        const visual = new Group();
        visual.name = 'slot-edit-visual';
        visual.add(new AxesHelper(0.15));
        if (spec.boundary) {
            const box = this.resolveDisplayBox(spec.boundary, this.computeContainerLocalBox(group));
            this.addBoundaryFill(visual, box);
            this.addBoundaryEdges(visual, box);
            this.addOpenFaceArrows(visual, box);
        }
        return visual;
    }
    computeContainerLocalBox(group) {
        const parent = group.parent;
        if (!parent)
            return null;
        parent.updateMatrixWorld(true);
        const box = new Box3().setFromObject(parent);
        if (box.isEmpty())
            return null;
        box.applyMatrix4(new Matrix4().copy(parent.matrixWorld).invert());
        box.translate(group.position.clone().negate());
        return box;
    }
    resolveDisplayBox(boundary, container) {
        const axis = (range, cMin, cMax) => {
            const minOpen = (range === null || range === void 0 ? void 0 : range.min) === undefined;
            const maxOpen = (range === null || range === void 0 ? void 0 : range.max) === undefined;
            const stub = OPEN_BOUND_EXTENT;
            let min;
            let max;
            if (minOpen && maxOpen) {
                min = isFinite(cMin) ? Math.max(cMin, -stub) : -stub;
                max = isFinite(cMax) ? Math.min(cMax, stub) : stub;
            }
            else if (maxOpen) {
                min = range.min;
                max = isFinite(cMax) ? Math.min(cMax, min + stub) : min + stub;
                if (max <= min)
                    max = min + stub;
            }
            else if (minOpen) {
                max = range.max;
                min = isFinite(cMin) ? Math.max(cMin, max - stub) : max - stub;
                if (min >= max)
                    min = max - stub;
            }
            else {
                min = range.min;
                max = range.max;
            }
            return { min, max, minOpen, maxOpen };
        };
        return {
            x: axis(boundary.x, container ? container.min.x : NaN, container ? container.max.x : NaN),
            y: axis(boundary.y, container ? container.min.y : NaN, container ? container.max.y : NaN),
            z: axis(boundary.z, container ? container.min.z : NaN, container ? container.max.z : NaN),
        };
    }
    addBoundaryFill(visual, box) {
        const size = { x: box.x.max - box.x.min, y: box.y.max - box.y.min, z: box.z.max - box.z.min };
        const center = {
            x: (box.x.min + box.x.max) / 2,
            y: (box.y.min + box.y.max) / 2,
            z: (box.z.min + box.z.max) / 2,
        };
        const geo = new BoxGeometry(size.x, size.y, size.z);
        const pos = geo.attributes.position;
        const colors = new Float32Array(pos.count * 4);
        const col = new Color(ACCENT_COLOR);
        const fade = (v, a) => {
            const t = (v - a.min) / (a.max - a.min || 1);
            let f = 1;
            if (a.minOpen)
                f *= t;
            if (a.maxOpen)
                f *= 1 - t;
            return f;
        };
        for (let i = 0; i < pos.count; i++) {
            const a = FILL_BASE_ALPHA *
                fade(pos.getX(i) + center.x, box.x) *
                fade(pos.getY(i) + center.y, box.y) *
                fade(pos.getZ(i) + center.z, box.z);
            colors[i * 4] = col.r;
            colors[i * 4 + 1] = col.g;
            colors[i * 4 + 2] = col.b;
            colors[i * 4 + 3] = Math.max(0, a);
        }
        geo.setAttribute('color', new Float32BufferAttribute(colors, 4));
        const mesh = new Mesh(geo, new MeshBasicMaterial({ transparent: true, depthWrite: false, vertexColors: true, side: DoubleSide }));
        mesh.position.set(center.x, center.y, center.z);
        visual.add(mesh);
    }
    addBoundaryEdges(visual, box) {
        const solid = [];
        const dashed = [];
        const others = { x: ['y', 'z'], y: ['x', 'z'], z: ['x', 'y'] };
        const sides = ['min', 'max'];
        const isOpen = (a, side) => (side === 'min' ? box[a].minOpen : box[a].maxOpen);
        for (const a of ['x', 'y', 'z']) {
            const [b, c] = others[a];
            for (const bs of sides) {
                for (const cs of sides) {
                    const start = { x: 0, y: 0, z: 0 };
                    const end = { x: 0, y: 0, z: 0 };
                    start[a] = box[a].min;
                    end[a] = box[a].max;
                    start[b] = end[b] = box[b][bs];
                    start[c] = end[c] = box[c][cs];
                    const arr = isOpen(b, bs) || isOpen(c, cs) ? dashed : solid;
                    arr.push(start.x, start.y, start.z, end.x, end.y, end.z);
                }
            }
        }
        if (solid.length) {
            const g = new BufferGeometry();
            g.setAttribute('position', new Float32BufferAttribute(solid, 3));
            visual.add(new LineSegments(g, new LineBasicMaterial({ color: ACCENT_COLOR })));
        }
        if (dashed.length) {
            const g = new BufferGeometry();
            g.setAttribute('position', new Float32BufferAttribute(dashed, 3));
            const ls = new LineSegments(g, new LineDashedMaterial({ color: ACCENT_COLOR, dashSize: 0.03, gapSize: 0.02 }));
            ls.computeLineDistances();
            visual.add(ls);
        }
    }
    addOpenFaceArrows(visual, box) {
        const center = {
            x: (box.x.min + box.x.max) / 2,
            y: (box.y.min + box.y.max) / 2,
            z: (box.z.min + box.z.max) / 2,
        };
        const diag = Math.hypot(box.x.max - box.x.min, box.y.max - box.y.min, box.z.max - box.z.min);
        const len = Math.min(0.18, Math.max(0.06, diag * 0.12));
        const faces = [
            { axis: 'x', open: box.x.minOpen, dir: new Vector3(-1, 0, 0), at: box.x.min },
            { axis: 'x', open: box.x.maxOpen, dir: new Vector3(1, 0, 0), at: box.x.max },
            { axis: 'y', open: box.y.minOpen, dir: new Vector3(0, -1, 0), at: box.y.min },
            { axis: 'y', open: box.y.maxOpen, dir: new Vector3(0, 1, 0), at: box.y.max },
            { axis: 'z', open: box.z.minOpen, dir: new Vector3(0, 0, -1), at: box.z.min },
            { axis: 'z', open: box.z.maxOpen, dir: new Vector3(0, 0, 1), at: box.z.max },
        ];
        for (const f of faces) {
            if (!f.open)
                continue;
            const origin = new Vector3(center.x, center.y, center.z);
            origin[f.axis] = f.at;
            visual.add(new ArrowHelper(f.dir, origin, len, ACCENT_COLOR, len * 0.4, len * 0.25));
        }
    }
    disposeVisual() {
        var _a;
        if (!this.visual)
            return;
        (_a = this.visual.parent) === null || _a === void 0 ? void 0 : _a.remove(this.visual);
        this.visual.traverse((node) => {
            var _a, _b;
            const disposable = node;
            (_b = (_a = disposable.geometry) === null || _a === void 0 ? void 0 : _a.dispose) === null || _b === void 0 ? void 0 : _b.call(_a);
            const material = disposable.material;
            if (material) {
                (Array.isArray(material) ? material : [material]).forEach((m) => { var _a; return (_a = m.dispose) === null || _a === void 0 ? void 0 : _a.call(m); });
            }
        });
        this.visual = null;
    }
    worldDeltaToParentLocal(worldDelta, group) {
        const local = worldDelta.clone();
        const parent = group === null || group === void 0 ? void 0 : group.parent;
        if (parent) {
            const parentWorldQuat = new Quaternion();
            parent.getWorldQuaternion(parentWorldQuat);
            local.applyQuaternion(parentWorldQuat.invert());
        }
        return local;
    }
}

const CONTAINER_SUFFIX = ViewNode.getContainerNodeName('');
const V1_CONTAINER_PREFIX = '$$$<part-container>$$$-';
function resolveHit(hitObject, rootNode, intersection = null, options = {}) {
    var _a;
    const isSelectable = (_a = options.isSelectable) !== null && _a !== void 0 ? _a : (() => true);
    const ancestors = [];
    let hitPartKey = null;
    let reachedRoot = false;
    for (let node = hitObject; node; node = node.parent) {
        if (node === rootNode) {
            reachedRoot = true;
            break;
        }
        const identity = readSlotItemIdentity(node);
        if (identity) {
            ancestors.push({ identity, node });
        }
        else if (ancestors.length === 0 && hitPartKey === null) {
            hitPartKey = parsePartContainerName(node.name);
        }
    }
    if (!reachedRoot) {
        return null;
    }
    const fullPath = ancestors
        .map(({ identity }) => ({ itemId: identity.id, kind: identity.kind, slotKey: identity.slotKey }))
        .reverse();
    for (let nearness = 0; nearness < ancestors.length; nearness++) {
        const pathIndex = ancestors.length - 1 - nearness;
        const entry = fullPath[pathIndex];
        const target = {
            type: 'slotItem',
            itemId: entry.itemId,
            kind: entry.kind,
            slotKey: entry.slotKey,
            parentItemId: pathIndex > 0 ? fullPath[pathIndex - 1].itemId : null,
            path: fullPath.slice(0, pathIndex + 1),
        };
        if (!isSelectable(target)) {
            continue;
        }
        return {
            target,
            hitPartKey: nearness === 0 && entry.kind !== 'basic' ? hitPartKey : null,
            node: ancestors[nearness].node,
            intersection,
        };
    }
    const rootTarget = { type: 'root' };
    if (isSelectable(rootTarget)) {
        return {
            target: rootTarget,
            hitPartKey: ancestors.length === 0 ? hitPartKey : null,
            node: rootNode,
            intersection,
        };
    }
    return null;
}
function findSlotItemNode(root, itemId) {
    const groupName = slotItemGroupName(itemId);
    let result;
    root.traverse((node) => {
        if (result)
            return;
        const stamped = node.userData.slotItem;
        if ((stamped === null || stamped === void 0 ? void 0 : stamped.id) === itemId || node.name === groupName) {
            result = node;
        }
    });
    return result;
}
function readSlotItemIdentity(node) {
    const stamped = node.userData.slotItem;
    if (stamped) {
        return stamped;
    }
    const id = parseSlotItemGroupName(node.name);
    if (id === null) {
        return null;
    }
    const slotKey = node.parent ? parseSlotGroupName(node.parent.name) : null;
    return { id, kind: hasOwnSlots(node) ? 'composite' : 'basic', slotKey: slotKey !== null && slotKey !== void 0 ? slotKey : '' };
}
function hasOwnSlots(itemGroup) {
    let found = false;
    itemGroup.traverse((child) => {
        if (child !== itemGroup && parseSlotGroupName(child.name) !== null) {
            found = true;
        }
    });
    return found;
}
function parsePartContainerName(name) {
    if (name.startsWith(V1_CONTAINER_PREFIX)) {
        return name.slice(V1_CONTAINER_PREFIX.length);
    }
    if (name.length > CONTAINER_SUFFIX.length && name.endsWith(CONTAINER_SUFFIX)) {
        return name.slice(0, -CONTAINER_SUFFIX.length);
    }
    return null;
}

class HighlightPlugin extends View3dPlugin {
    constructor() {
        super(...arguments);
        this.target = null;
        this.applied = null;
        this.originalMaterials = new WeakMap();
    }
    highlight(partKey) {
        this.resetHighlightImpl();
        this.target = { kind: 'part', partKey };
        this.redraw();
    }
    highlightSlotItem(itemId) {
        this.resetHighlightImpl();
        this.target = { kind: 'slotItem', itemId };
        this.redraw();
    }
    resetHighlight() {
        this.resetHighlightImpl();
        this.target = null;
    }
    onUpdateLayout() {
        this.redraw();
    }
    redraw() {
        if (this.target) {
            this.highlightImpl(this.target);
        }
        else {
            this.resetHighlightImpl();
        }
    }
    resolveTargetNode(target) {
        if (target.kind === 'part') {
            return this.findNodeByKey(target.partKey);
        }
        return this.options ? findSlotItemNode(this.options.rootNode, target.itemId) : undefined;
    }
    highlightImpl(target) {
        const node = this.resolveTargetNode(target);
        if (!node) {
            return;
        }
        this.highlightNode(node);
        this.applied = target;
    }
    resetHighlightImpl() {
        if (this.applied) {
            const node = this.resolveTargetNode(this.applied);
            if (node) {
                this.restoreOriginalMaterials(node);
            }
            this.applied = null;
        }
    }
    highlightNode(node) {
        node.traverse((child) => {
            if (child instanceof Mesh) {
                const mesh = child;
                if (!this.originalMaterials.has(mesh)) {
                    this.originalMaterials.set(mesh, mesh.material);
                }
                this.applyHighlightMaterial(mesh);
            }
        });
    }
    applyHighlightMaterial(mesh) {
        const color = new Color(0.2, 0.8, 0.3);
        const emissiveIntensity = 0.6;
        if (mesh.material instanceof MeshStandardMaterial) {
            const highlightMaterial = mesh.material.clone();
            highlightMaterial.emissive = color;
            highlightMaterial.emissiveIntensity = emissiveIntensity;
            mesh.material = Array.isArray(mesh.material) ? mesh.material.map(() => highlightMaterial) : highlightMaterial;
        }
        else if (mesh.material instanceof Material) {
            mesh.material = mesh.material.clone();
            if ('color' in mesh.material) {
                mesh.material.color = color;
            }
            else {
                mesh.material = new MeshStandardMaterial({
                    color: color,
                    emissive: color,
                    emissiveIntensity: emissiveIntensity,
                });
            }
        }
    }
    restoreOriginalMaterials(node) {
        node.traverse((child) => {
            if (child instanceof Mesh) {
                const mesh = child;
                const originalMaterial = this.originalMaterials.get(mesh);
                if (originalMaterial) {
                    mesh.material = originalMaterial;
                    this.originalMaterials.delete(mesh);
                }
            }
        });
    }
}

const TAP_NDC_EPS = 0.003;
const TAP_TOUCH_PX_EPS = 5;
const TAP_TIME_EPS = 1000;
const HOVER_DELAY = 10;
class PointerGestureController {
    constructor(element, callbacks, options) {
        var _a;
        this.element = element;
        this.callbacks = callbacks;
        this.press = null;
        this.activePointers = new Set();
        this.hoverTimer = 0;
        this.active = false;
        this.onPointerDown = (event) => {
            this.activePointers.add(event.pointerId);
            if (this.activePointers.size > 1) {
                if (this.press)
                    this.press.cancelled = true;
                return;
            }
            this.press = {
                pointerId: event.pointerId,
                pointerType: event.pointerType,
                clientX: event.clientX,
                clientY: event.clientY,
                ndc: this.toNdc(event),
                time: Date.now(),
                cancelled: false,
            };
        };
        this.onPointerUp = (event) => {
            this.activePointers.delete(event.pointerId);
            const press = this.press;
            if (!press || press.pointerId !== event.pointerId)
                return;
            this.press = null;
            if (press.cancelled || !this.isTap(press, event))
                return;
            blurActiveElement(this.element);
            this.callbacks.onTap(this.toNdc(event), event);
        };
        this.onPointerCancel = (event) => {
            var _a;
            this.activePointers.delete(event.pointerId);
            if (((_a = this.press) === null || _a === void 0 ? void 0 : _a.pointerId) === event.pointerId) {
                this.press = null;
            }
        };
        this.onPointerLeave = (event) => {
            var _a;
            if (((_a = this.press) === null || _a === void 0 ? void 0 : _a.pointerId) === event.pointerId) {
                this.press.cancelled = true;
            }
            window.clearTimeout(this.hoverTimer);
        };
        this.onPointerMove = (event) => {
            if (!this.hoverEnabled || !this.callbacks.onHoverMove)
                return;
            if (this.activePointers.size > 0)
                return;
            if (event.pointerType !== 'mouse' && event.pointerType !== 'pen')
                return;
            window.clearTimeout(this.hoverTimer);
            this.hoverTimer = window.setTimeout(() => {
                var _a, _b;
                (_b = (_a = this.callbacks).onHoverMove) === null || _b === void 0 ? void 0 : _b.call(_a, this.toNdc(event), event);
            }, HOVER_DELAY);
        };
        this.hoverEnabled = (_a = options === null || options === void 0 ? void 0 : options.hover) !== null && _a !== void 0 ? _a : true;
        this.activate();
    }
    activate() {
        if (this.active)
            return;
        this.active = true;
        this.element.addEventListener('pointerdown', this.onPointerDown);
        this.element.addEventListener('pointerup', this.onPointerUp);
        this.element.addEventListener('pointercancel', this.onPointerCancel);
        this.element.addEventListener('pointerleave', this.onPointerLeave);
        this.element.addEventListener('pointermove', this.onPointerMove);
    }
    deactivate() {
        if (!this.active)
            return;
        this.active = false;
        this.element.removeEventListener('pointerdown', this.onPointerDown);
        this.element.removeEventListener('pointerup', this.onPointerUp);
        this.element.removeEventListener('pointercancel', this.onPointerCancel);
        this.element.removeEventListener('pointerleave', this.onPointerLeave);
        this.element.removeEventListener('pointermove', this.onPointerMove);
        window.clearTimeout(this.hoverTimer);
        this.press = null;
        this.activePointers.clear();
    }
    dispose() {
        this.deactivate();
    }
    setHoverEnabled(enabled) {
        this.hoverEnabled = enabled;
        if (!enabled) {
            window.clearTimeout(this.hoverTimer);
        }
    }
    isTap(press, release) {
        if (Date.now() - press.time > TAP_TIME_EPS)
            return false;
        if (press.pointerType === 'touch') {
            return Math.hypot(release.clientX - press.clientX, release.clientY - press.clientY) <= TAP_TOUCH_PX_EPS;
        }
        const ndc = this.toNdc(release);
        return Math.abs(ndc.x - press.ndc.x) < TAP_NDC_EPS && Math.abs(ndc.y - press.ndc.y) < TAP_NDC_EPS;
    }
    toNdc(event) {
        const rect = this.element.getBoundingClientRect();
        return new Vector2(((event.clientX - rect.left) / rect.width) * 2 - 1, (-(event.clientY - rect.top) / rect.height) * 2 + 1);
    }
}
function blurActiveElement(element) {
    var _a;
    const active = (_a = element.ownerDocument) === null || _a === void 0 ? void 0 : _a.activeElement;
    if (active instanceof HTMLElement) {
        active.blur();
    }
}

const defaultRaycaster = new Raycaster();
function pick(ndc, camera, root, raycaster = defaultRaycaster) {
    raycaster.setFromCamera(ndc, camera);
    return raycaster.intersectObjects(root.children, true).filter((hit) => isChainVisible(hit.object, root));
}
function isChainVisible(object, root) {
    let node = object;
    while (node && node !== root) {
        if (!node.visible)
            return false;
        node = node.parent;
    }
    return true;
}

class SelectionPlugin extends View3dPlugin {
    constructor(pluginOptions = {}) {
        super();
        this.pluginOptions = pluginOptions;
        this.gestures = null;
        this.current = [];
        this.hovered = null;
        this.listeners = {
            selectionChanged: new Set(),
            hoverChanged: new Set(),
        };
        this.handleTap = (ndc) => {
            const picked = this.pickProduct(ndc);
            this.applySelection(picked ? [picked] : []);
        };
        this.handleHoverMove = (ndc) => {
            this.setHovered(this.pickProduct(ndc));
        };
    }
    get selection() {
        return [...this.current];
    }
    on(event, handler) {
        const handlers = this.listeners[event];
        handlers.add(handler);
        return () => handlers.delete(handler);
    }
    select(itemId) {
        if (!this.options)
            return;
        const node = findSlotItemNode(this.options.rootNode, itemId);
        if (!node)
            return;
        const picked = this.resolve(node, null);
        if (picked) {
            this.applySelection([picked]);
        }
    }
    clearSelection() {
        this.applySelection([]);
    }
    setEnabled(enabled) {
        if (!this.gestures)
            return;
        if (enabled) {
            this.gestures.activate();
        }
        else {
            this.gestures.deactivate();
            this.setHovered(null);
        }
    }
    onInit(options) {
        var _a;
        this.gestures = new PointerGestureController(options.renderer.domElement, { onTap: this.handleTap, onHoverMove: this.handleHoverMove }, { hover: (_a = this.pluginOptions.hover) !== null && _a !== void 0 ? _a : true });
    }
    onDestroy() {
        var _a;
        (_a = this.gestures) === null || _a === void 0 ? void 0 : _a.dispose();
        this.gestures = null;
        this.setHovered(null);
        this.current = [];
        this.listeners.selectionChanged.clear();
        this.listeners.hoverChanged.clear();
    }
    onUpdateLayout() {
        if (!this.options || this.current.length === 0)
            return;
        const next = [];
        for (const picked of this.current) {
            if (picked.target.type === 'root') {
                next.push(picked);
                continue;
            }
            const node = findSlotItemNode(this.options.rootNode, picked.target.itemId);
            const resolved = node ? this.resolve(node, null) : null;
            if (resolved) {
                next.push(resolved);
            }
        }
        this.applySelection(next);
    }
    pickProduct(ndc) {
        if (!this.options)
            return null;
        const { cameraManager, rootNode } = this.options;
        for (const hit of pick(ndc, cameraManager.camera, rootNode)) {
            const resolved = this.resolve(hit.object, hit);
            if (resolved)
                return resolved;
        }
        return null;
    }
    resolve(hitObject, intersection) {
        if (!this.options)
            return null;
        return resolveHit(hitObject, this.options.rootNode, intersection, {
            isSelectable: this.pluginOptions.isSelectable,
        });
    }
    applySelection(next) {
        const previous = this.current;
        this.current = next;
        if (sameTargets(previous, next)) {
            return;
        }
        this.emit('selectionChanged', [...next], previous);
    }
    setHovered(target) {
        var _a;
        if (sameTargets(this.hovered ? [this.hovered] : [], target ? [target] : []))
            return;
        this.hovered = target;
        const element = (_a = this.options) === null || _a === void 0 ? void 0 : _a.renderer.domElement;
        if (element) {
            element.style.cursor = target ? 'pointer' : 'auto';
        }
        this.emit('hoverChanged', target);
    }
    emit(event, ...args) {
        this.listeners[event].forEach((handler) => {
            handler(...args);
        });
    }
}
function sameTargets(a, b) {
    return a.length === b.length && a.every((picked, i) => isSameTarget(picked.target, b[i].target));
}
function isSameTarget(a, b) {
    if (a.type === 'root' || b.type === 'root') {
        return a.type === b.type;
    }
    return a.itemId === b.itemId;
}

class ScopePlugin extends View3dPlugin {
    constructor() {
        super(...arguments);
        this.scopedItemId = null;
        this.savedVisibility = new Map();
        this.listeners = {
            scopeChanged: new Set(),
        };
    }
    getScope() {
        return { itemId: this.scopedItemId };
    }
    on(event, handler) {
        const handlers = this.listeners[event];
        handlers.add(handler);
        return () => handlers.delete(handler);
    }
    enterScope(itemId) {
        if (!this.options || this.scopedItemId === itemId)
            return;
        this.restore();
        const node = this.isolate(itemId);
        this.scopedItemId = node ? itemId : null;
        if (node) {
            this.options.cameraFocus.focusOn(node, { fit: true });
        }
        this.emit('scopeChanged', this.getScope());
    }
    exitScope() {
        if (this.scopedItemId === null)
            return;
        this.restore();
        this.scopedItemId = null;
        this.focusRoot();
        this.emit('scopeChanged', this.getScope());
    }
    onDestroy() {
        this.restore();
        this.scopedItemId = null;
        this.listeners.scopeChanged.clear();
    }
    onUpdateLayout() {
        if (this.scopedItemId === null)
            return;
        this.savedVisibility.clear();
        const node = this.isolate(this.scopedItemId);
        if (!node) {
            this.scopedItemId = null;
            this.focusRoot();
            this.emit('scopeChanged', this.getScope());
        }
    }
    suspendForCapture() {
        if (this.scopedItemId !== null) {
            this.restore();
        }
    }
    resumeAfterCapture() {
        if (this.scopedItemId !== null) {
            this.isolate(this.scopedItemId);
        }
    }
    isolate(itemId) {
        if (!this.options)
            return null;
        const rootNode = this.options.rootNode;
        const node = findSlotItemNode(rootNode, itemId);
        if (!node)
            return null;
        for (let cur = node; cur.parent && cur !== rootNode; cur = cur.parent) {
            for (const sibling of cur.parent.children) {
                if (sibling !== cur) {
                    this.hide(sibling);
                }
            }
        }
        return node;
    }
    focusRoot() {
        if (this.options) {
            this.options.cameraFocus.focusOn(this.options.rootNode, { fit: true });
        }
    }
    hide(node) {
        if (!this.savedVisibility.has(node)) {
            this.savedVisibility.set(node, node.visible);
        }
        node.visible = false;
    }
    restore() {
        for (const [node, visible] of this.savedVisibility) {
            node.visible = visible;
        }
        this.savedVisibility.clear();
    }
    emit(event, ...args) {
        this.listeners[event].forEach((handler) => {
            handler(...args);
        });
    }
}

function resolveAnchorNode(anchor, rootNode) {
    var _a;
    switch (anchor.type) {
        case 'slotItem':
            return (_a = findSlotItemNode(rootNode, anchor.itemId)) !== null && _a !== void 0 ? _a : null;
        case 'slot':
            return findNodeByName(rootNode, slotGroupName(anchor.slotKey));
        case 'node':
            return findNodeByName(rootNode, anchor.nodeName);
        case 'world':
            return null;
    }
}
function isAnchorResolved(anchor, node) {
    return anchor.type === 'world' || node !== null;
}
function anchorWorldPosition(anchor, node, out) {
    if (anchor.type === 'world') {
        return out.set(anchor.position.x, anchor.position.y, anchor.position.z);
    }
    if (node) {
        node.getWorldPosition(out);
    }
    else {
        out.set(0, 0, 0);
    }
    if (anchor.offset) {
        out.x += anchor.offset.x;
        out.y += anchor.offset.y;
        out.z += anchor.offset.z;
    }
    return out;
}
function findNodeByName(root, name) {
    let result = null;
    root.traverse((node) => {
        if (!result && node.name === name) {
            result = node;
        }
    });
    return result;
}

class HotspotsPlugin extends View3dPlugin {
    constructor() {
        super(...arguments);
        this.renderer2d = null;
        this.overlayScene = new Scene();
        this.entries = new Map();
        this.layerVisible = true;
        this.lastWidth = 0;
        this.lastHeight = 0;
        this.listeners = {
            actionClick: new Set(),
        };
    }
    get domElement() {
        var _a, _b;
        return (_b = (_a = this.renderer2d) === null || _a === void 0 ? void 0 : _a.domElement) !== null && _b !== void 0 ? _b : null;
    }
    on(event, handler) {
        const handlers = this.listeners[event];
        handlers.add(handler);
        return () => handlers.delete(handler);
    }
    setHotspots(hotspots) {
        const next = new Map();
        for (const hotspot of hotspots) {
            if (next.has(hotspot.id)) {
                throw new Error(`HotspotsPlugin: duplicate hotspot id "${hotspot.id}"`);
            }
            const existing = this.entries.get(hotspot.id);
            if (existing) {
                this.entries.delete(hotspot.id);
                existing.hotspot = hotspot;
                next.set(hotspot.id, existing);
                this.resolveEntry(existing);
                this.refreshEntryContent(existing);
            }
            else {
                const entry = {
                    hotspot,
                    anchorNode: null,
                    resolved: false,
                    element: null,
                    object: null,
                    context: null,
                    boundNodes: new Map(),
                };
                next.set(hotspot.id, entry);
                this.resolveEntry(entry);
            }
        }
        for (const entry of this.entries.values()) {
            this.destroyEntry(entry);
        }
        this.entries = next;
    }
    updateHotspot(id, patch) {
        const entry = this.entries.get(id);
        if (!entry)
            return;
        const { actions } = patch, rest = __rest(patch, ["actions"]);
        entry.hotspot = Object.assign(Object.assign({}, entry.hotspot), rest);
        if (actions) {
            this.patchActions(entry, actions);
        }
        if (patch.anchor) {
            this.resolveEntry(entry);
        }
        else {
            this.syncEntryVisibility(entry);
        }
    }
    clear() {
        for (const entry of this.entries.values()) {
            this.destroyEntry(entry);
        }
        this.entries.clear();
    }
    setVisible(visible) {
        this.layerVisible = visible;
        if (this.renderer2d) {
            this.renderer2d.domElement.style.display = visible ? '' : 'none';
        }
    }
    onInit(options) {
        var _a;
        const renderer = new CSS2DRenderer();
        this.lastWidth = options.getWidth();
        this.lastHeight = options.getHeight();
        renderer.setSize(this.lastWidth, this.lastHeight);
        const element = renderer.domElement;
        element.style.position = 'absolute';
        element.style.top = '0px';
        element.style.left = '0px';
        element.style.pointerEvents = 'none';
        element.style.display = this.layerVisible ? '' : 'none';
        (_a = options.renderer.domElement.parentElement) === null || _a === void 0 ? void 0 : _a.appendChild(element);
        this.renderer2d = renderer;
        for (const entry of this.entries.values()) {
            this.resolveEntry(entry);
        }
    }
    onUpdateLayout() {
        for (const entry of this.entries.values()) {
            this.resolveEntry(entry);
        }
    }
    render() {
        if (!this.options || !this.renderer2d)
            return;
        const width = this.options.getWidth();
        const height = this.options.getHeight();
        if (width !== this.lastWidth || height !== this.lastHeight) {
            this.lastWidth = width;
            this.lastHeight = height;
            this.renderer2d.setSize(width, height);
        }
        for (const entry of this.entries.values()) {
            if (!entry.object || !entry.resolved)
                continue;
            anchorWorldPosition(entry.hotspot.anchor, entry.anchorNode, entry.object.position);
        }
        this.renderer2d.render(this.overlayScene, this.options.cameraManager.camera);
    }
    onDestroy() {
        var _a;
        this.clear();
        (_a = this.renderer2d) === null || _a === void 0 ? void 0 : _a.domElement.remove();
        this.renderer2d = null;
        this.listeners.actionClick.clear();
    }
    resolveEntry(entry) {
        if (!this.options)
            return;
        entry.anchorNode = resolveAnchorNode(entry.hotspot.anchor, this.options.rootNode);
        entry.resolved = isAnchorResolved(entry.hotspot.anchor, entry.anchorNode);
        if (entry.resolved && !entry.element) {
            this.createEntryElement(entry);
        }
        this.syncEntryVisibility(entry);
    }
    createEntryElement(entry) {
        const content = entry.hotspot.content;
        const ctx = this.createContext(entry);
        entry.context = ctx;
        if (content.type === 'element') {
            entry.element = content.create(ctx);
        }
        else {
            this.ensureDefaultStyles();
            const container = document.createElement('div');
            container.className = 'clp-hotspot';
            entry.element = container;
            this.renderDefaultActions(entry);
        }
        const object = new CSS2DObject$1(entry.element);
        this.overlayScene.add(object);
        entry.object = object;
    }
    refreshEntryContent(entry) {
        if (!entry.element)
            return;
        if (entry.hotspot.content.type === 'actions') {
            this.renderDefaultActions(entry);
        }
        else {
            this.syncAllActionStates(entry);
        }
    }
    createContext(entry) {
        const findAction = (actionId) => entry.hotspot.content.actions.find((action) => action.id === actionId);
        const assertAction = (actionId) => {
            if (!findAction(actionId)) {
                throw new Error(`HotspotsPlugin: hotspot "${entry.hotspot.id}" has no action "${actionId}"`);
            }
        };
        return {
            get hotspot() {
                return entry.hotspot;
            },
            bindAction: (actionId, el) => {
                var _a;
                assertAction(actionId);
                let bound = entry.boundNodes.get(actionId);
                if (!bound) {
                    bound = new Set();
                    entry.boundNodes.set(actionId, bound);
                }
                bound.add(el);
                el.style.pointerEvents = 'auto';
                const onPointerDown = (event) => event.stopPropagation();
                const onClick = (event) => {
                    event.stopPropagation();
                    const action = findAction(actionId);
                    if (!action || action.disabled)
                        return;
                    this.emit('actionClick', entry.hotspot, action);
                };
                el.addEventListener('pointerdown', onPointerDown);
                el.addEventListener('click', onClick);
                applyDisabledState(el, ((_a = findAction(actionId)) === null || _a === void 0 ? void 0 : _a.disabled) === true);
                return () => {
                    bound.delete(el);
                    el.removeEventListener('pointerdown', onPointerDown);
                    el.removeEventListener('click', onClick);
                };
            },
            trigger: (actionId) => {
                assertAction(actionId);
                const action = findAction(actionId);
                if (!action || action.disabled)
                    return;
                this.emit('actionClick', entry.hotspot, action);
            },
        };
    }
    renderDefaultActions(entry) {
        const container = entry.element;
        const ctx = entry.context;
        if (!container || !ctx)
            return;
        entry.boundNodes.clear();
        container.textContent = '';
        for (const action of entry.hotspot.content.actions) {
            container.appendChild(createActionButton(action, ctx));
        }
    }
    patchActions(entry, patches) {
        const content = entry.hotspot.content;
        const actions = content.actions.map((action) => {
            const patch = patches.find((p) => p.id === action.id);
            return patch ? Object.assign(Object.assign({}, action), patch) : action;
        });
        entry.hotspot = Object.assign(Object.assign({}, entry.hotspot), { content: Object.assign(Object.assign({}, content), { actions }) });
        this.syncAllActionStates(entry);
    }
    syncAllActionStates(entry) {
        for (const [actionId, nodes] of entry.boundNodes) {
            const action = entry.hotspot.content.actions.find((a) => a.id === actionId);
            const disabled = (action === null || action === void 0 ? void 0 : action.disabled) === true;
            nodes.forEach((el) => applyDisabledState(el, disabled));
        }
    }
    syncEntryVisibility(entry) {
        if (entry.object) {
            entry.object.visible = entry.resolved && entry.hotspot.visible !== false;
        }
    }
    destroyEntry(entry) {
        var _a, _b;
        if (entry.element && entry.hotspot.content.type === 'element') {
            (_b = (_a = entry.hotspot.content).destroy) === null || _b === void 0 ? void 0 : _b.call(_a, entry.element);
        }
        if (entry.object) {
            this.overlayScene.remove(entry.object);
        }
        entry.boundNodes.clear();
        entry.element = null;
        entry.object = null;
        entry.context = null;
    }
    ensureDefaultStyles() {
        var _a;
        const layer = (_a = this.renderer2d) === null || _a === void 0 ? void 0 : _a.domElement;
        if (!layer || layer.querySelector(`style[${STYLE_ATTR}]`))
            return;
        const style = document.createElement('style');
        style.setAttribute(STYLE_ATTR, '');
        style.textContent = DEFAULT_STYLES;
        layer.appendChild(style);
    }
    emit(event, ...args) {
        this.listeners[event].forEach((handler) => {
            handler(...args);
        });
    }
}
function createActionButton(action, ctx) {
    var _a;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'clp-hotspot__action';
    if (action.icon) {
        const builtin = BUILTIN_ICONS[action.icon];
        if (builtin) {
            button.innerHTML = builtin;
        }
        else {
            const img = document.createElement('img');
            img.className = 'clp-hotspot__action-icon';
            img.src = action.icon;
            img.alt = '';
            button.appendChild(img);
        }
    }
    if (action.label) {
        button.classList.add('clp-hotspot__action--labeled');
        const label = document.createElement('span');
        label.className = 'clp-hotspot__action-label';
        label.textContent = action.label;
        button.appendChild(label);
    }
    button.setAttribute('aria-label', (_a = action.label) !== null && _a !== void 0 ? _a : action.id);
    ctx.bindAction(action.id, button);
    return button;
}
function applyDisabledState(el, disabled) {
    if (el instanceof HTMLButtonElement) {
        el.disabled = disabled;
    }
    else if (disabled) {
        el.setAttribute('aria-disabled', 'true');
    }
    else {
        el.removeAttribute('aria-disabled');
    }
    el.classList.toggle('clp-hotspot__action--disabled', disabled);
}
const STYLE_ATTR = 'data-clp-hotspot-styles';
const BUILTIN_ICONS = {
    plus: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3v10M3 8h10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    minus: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    info: '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M8 7.25V11M8 5.25v.01" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
};
const DEFAULT_STYLES = `
.clp-hotspot {
  display: flex;
  align-items: center;
  gap: var(--clp-hotspot-gap, 6px);
}
.clp-hotspot__action {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: var(--clp-hotspot-action-size, 32px);
  height: var(--clp-hotspot-action-size, 32px);
  padding: 0;
  border: var(--clp-hotspot-action-border, none);
  border-radius: calc(var(--clp-hotspot-action-size, 32px) / 2);
  background: var(--clp-hotspot-action-bg, #ffffff);
  color: var(--clp-hotspot-action-color, #1f1f1f);
  box-shadow: var(--clp-hotspot-action-shadow, 0 2px 6px rgba(0, 0, 0, 0.25));
  cursor: pointer;
}
.clp-hotspot__action--labeled {
  gap: 4px;
  padding: 0 12px;
}
.clp-hotspot__action-label {
  font: var(--clp-hotspot-action-font, 500 13px/1 sans-serif);
}
.clp-hotspot__action svg,
.clp-hotspot__action-icon {
  width: 16px;
  height: 16px;
  display: block;
}
.clp-hotspot__action:disabled,
.clp-hotspot__action--disabled {
  opacity: var(--clp-hotspot-action-disabled-opacity, 0.4);
  cursor: default;
}
`;

class FitZoomPlugin extends View3dPlugin {
    constructor() {
        super(...arguments);
        this.camera = null;
        this.node = null;
        this.cameraManager = null;
        this.padding2D = emptyPadding;
    }
    onInit(options) {
        if (options.cameraManager.camera instanceof PerspectiveCamera) {
            this.camera = options.cameraManager.camera;
            this.cameraManager = options.cameraManager;
            this.node = options.rootNode;
        }
    }
    fit(cameraPosition = 'perspectiveRight') {
        if (!this.options || !this.camera || !this.node || !this.cameraManager)
            return false;
        if (!this.hasMeasurableGeometry())
            return false;
        const glbConverter = new GlbConverter(this.options.getWidth(), this.options.getHeight());
        glbConverter.padding2D = this.padding2D;
        const target = glbConverter.fitCameraToObject3D(this.camera, this.node, cameraPosition);
        this.cameraManager.updateControlsTarget(target);
        return true;
    }
    hasMeasurableGeometry() {
        if (!this.node)
            return false;
        const box = new Box3().setFromObject(getMeasurableObjects(this.node));
        if (box.isEmpty())
            return false;
        const size = box.getSize(new Vector3());
        return size.x > 0 || size.y > 0 || size.z > 0;
    }
}

class GridHelperPlugin extends View3dPlugin {
    onInit(options) {
        const gridHelper = new GridHelper(30, 30);
        options.scene.add(gridHelper);
        const groundPlane = new Mesh(new PlaneGeometry(5000, 5000), new MeshBasicMaterial({ visible: false }));
        groundPlane.rotation.x = -Math.PI / 2;
        options.scene.add(groundPlane);
    }
}

class ViewHelper extends ViewHelper$1 {
    constructor(editorCamera, domElement, panel) {
        super(editorCamera, domElement);
        panel.addEventListener('pointerup', (event) => {
            event.stopPropagation();
            this.handleClick(event);
        });
        panel.addEventListener('pointerdown', function (event) {
            event.stopPropagation();
        });
    }
}
class ViewHelperPlugin extends View3dPlugin {
    constructor(container) {
        super();
        this.container = container;
        this.viewHelper = null;
    }
    onInit(options) {
        this.viewHelper = new ViewHelper(options.cameraManager.camera, options.renderer.domElement, this.container);
        this.viewHelper.setLabels('X', 'Y', 'Z');
        this.options = options;
    }
    update(delta) {
        if (this.viewHelper && this.viewHelper.animating) {
            this.viewHelper.update(delta);
        }
    }
    render() {
        if (this.options !== null && this.viewHelper !== null && this.options.renderer instanceof WebGLRenderer) {
            this.viewHelper.render(this.options.renderer);
        }
    }
}

class LayoutDebugPlugin extends View3dPlugin {
    onUpdateLayout() {
        var _a;
        if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.rootNode) {
            const json = this.options.rootNode.toJSON();
            console.debug('Hierarchy: ', getHierarchy(this.options.rootNode));
            console.debug('LayoutInfo: ', getLayoutInfo(this.options.rootNode));
            console.debug('JSON: ', json);
        }
    }
}
function getHierarchy(node) {
    function rec(node, info) {
        info[node.name] = Object.create(null);
        for (const child of node.children) {
            rec(child, info[node.name]);
        }
    }
    const result = {};
    rec(node, result);
    return result;
}
function getLayoutInfo(node) {
    function rec(node, parentInfo) {
        const childInfo = Object.create(null);
        if (!parentInfo.children) {
            parentInfo.children = [];
        }
        parentInfo.children.push(childInfo);
        childInfo.name = node.name;
        childInfo.position = node.position;
        childInfo.rotation = node.rotation;
        childInfo.visible = node.visible;
        for (const childNode of node.children) {
            rec(childNode, childInfo);
        }
    }
    const result = {};
    rec(node, result);
    return result;
}

const DEFAULT_COLORS = {
    basic: '#52c41a',
    composite: '#722ed1',
    real: '#fa8c16',
};
class BoundsDebugPlugin extends View3dPlugin {
    constructor(options = {}) {
        var _a;
        super();
        this.sdkGroup = new Group();
        this.realGroup = new Group();
        this.tree = null;
        this.nodeStates = new Map();
        this.GuiClass = null;
        this.gui = null;
        this.folderOpen = new Map();
        this.folderKeys = new Map();
        const fromQuery = typeof location !== 'undefined' && new URLSearchParams(location.search).get('debug') !== null;
        this._enabled = (_a = options.enabled) !== null && _a !== void 0 ? _a : fromQuery;
        this.colors = Object.assign(Object.assign({}, DEFAULT_COLORS), options.colors);
        this.sdkGroup.name = 'BoundsDebug:sdk';
        this.realGroup.name = 'BoundsDebug:real';
    }
    get enabled() {
        return this._enabled;
    }
    onInit(options) {
        if (!this._enabled)
            return;
        options.scene.add(this.sdkGroup);
        options.scene.add(this.realGroup);
        import('lil-gui')
            .then((m) => {
            this.GuiClass = m.GUI;
            this.buildGui();
            this.refreshScene();
        })
            .catch((err) => console.warn('BoundsDebugPlugin: failed to load lil-gui', err));
    }
    setDebugInfo(info) {
        var _a;
        if (!this._enabled)
            return;
        this.tree = (_a = info === null || info === void 0 ? void 0 : info.root) !== null && _a !== void 0 ? _a : null;
        this.reconcileStates();
        this.buildGui();
        this.refreshScene();
    }
    onUpdateLayout() {
        if (!this._enabled)
            return;
        this.refreshScene();
    }
    onDestroy() {
        var _a;
        (_a = this.gui) === null || _a === void 0 ? void 0 : _a.destroy();
        this.gui = null;
        this.clearGroup(this.sdkGroup);
        this.clearGroup(this.realGroup);
        this.sdkGroup.removeFromParent();
        this.realGroup.removeFromParent();
    }
    refreshScene() {
        if (!this._enabled || !this.options)
            return;
        const root = this.options.rootNode;
        root.updateMatrixWorld(true);
        this.clearGroup(this.sdkGroup);
        this.clearGroup(this.realGroup);
        if (!this.tree)
            return;
        const itemIndex = new Map();
        root.traverse((o) => {
            var _a, _b;
            const id = (_b = (_a = o.userData) === null || _a === void 0 ? void 0 : _a.slotItem) === null || _b === void 0 ? void 0 : _b.id;
            if (id && !itemIndex.has(id))
                itemIndex.set(id, o);
        });
        this.walkNode(this.tree, root, itemIndex);
    }
    walkNode(node, parentObj, itemIndex) {
        const obj = this.resolveObject(node, parentObj, itemIndex);
        const st = this.stateFor(node);
        if (obj)
            obj.visible = st.visible;
        if (st.showSdk && node.bbox && this.options) {
            const box = new Box3(new Vector3(node.bbox.min.x, node.bbox.min.y, node.bbox.min.z), new Vector3(node.bbox.max.x, node.bbox.max.y, node.bbox.max.z));
            box.applyMatrix4(this.options.rootNode.matrixWorld);
            this.sdkGroup.add(new Box3Helper(box, new Color(st.sdkColor)));
        }
        if (st.showReal && obj) {
            const box = new Box3().setFromObject(obj);
            if (!box.isEmpty())
                this.realGroup.add(new Box3Helper(box, new Color(st.realColor)));
        }
        const nextParent = obj !== null && obj !== void 0 ? obj : parentObj;
        for (const child of node.children)
            this.walkNode(child, nextParent, itemIndex);
    }
    resolveObject(node, parentObj, itemIndex) {
        var _a;
        if (node.role === 'root')
            return (_a = this.options) === null || _a === void 0 ? void 0 : _a.rootNode;
        if (node.role === 'slotItem' && node.itemId)
            return itemIndex.get(node.itemId);
        if (node.role === 'component' && node.componentKey && parentObj) {
            return findContainerWithin(parentObj, ViewNode.getContainerNodeName(node.componentKey));
        }
        return undefined;
    }
    buildGui() {
        var _a;
        if (!this.GuiClass)
            return;
        (_a = this.gui) === null || _a === void 0 ? void 0 : _a.destroy();
        this.gui = null;
        if (!this.tree)
            return;
        this.folderKeys = new Map();
        const gui = new this.GuiClass({ title: 'Debug', width: 340 });
        this.applyFolderState(gui, '::panel', true);
        this.addProductFolder(gui, this.tree, true);
        gui.onOpenClose((changed) => {
            const key = this.folderKeys.get(changed);
            if (key !== undefined)
                this.folderOpen.set(key, !changed._closed);
        });
        this.gui = gui;
    }
    addProductFolder(parent, node, defaultOpen = false) {
        var _a;
        const folder = parent.addFolder(this.nodeTitle(node));
        this.applyFolderState(folder, node.id, defaultOpen);
        this.addControls(folder, node);
        const components = node.children.filter((c) => c.role === 'component');
        const items = node.children.filter((c) => c.role === 'slotItem');
        if (components.length) {
            const cf = folder.addFolder(`components (${components.length})`);
            this.applyFolderState(cf, `${node.id}::components`, false);
            for (const c of components)
                this.addProductFolder(cf, c);
        }
        if (items.length) {
            const sf = folder.addFolder('slots');
            this.applyFolderState(sf, `${node.id}::slots`, false);
            const bySlot = new Map();
            for (const it of items) {
                const key = (_a = it.slotKey) !== null && _a !== void 0 ? _a : '?';
                const arr = bySlot.get(key);
                if (arr)
                    arr.push(it);
                else
                    bySlot.set(key, [it]);
            }
            for (const [slotKey, slotItems] of bySlot) {
                const slotFolder = sf.addFolder(`${slotKey} (${slotItems.length})`);
                this.applyFolderState(slotFolder, `${node.id}::slot:${slotKey}`, true);
                for (const it of slotItems)
                    this.addProductFolder(slotFolder, it);
            }
        }
    }
    applyFolderState(folder, key, defaultOpen) {
        var _a;
        this.folderKeys.set(folder, key);
        const open = (_a = this.folderOpen.get(key)) !== null && _a !== void 0 ? _a : defaultOpen;
        if (open)
            folder.open();
        else
            folder.close();
    }
    addControls(folder, node) {
        const st = this.stateFor(node);
        folder
            .add(st, 'visible')
            .name('geometry')
            .onChange(() => this.refreshScene());
        folder
            .add(st, 'showSdk')
            .name('sdk box')
            .onChange(() => this.refreshScene());
        folder
            .addColor(st, 'sdkColor')
            .name('sdk color')
            .onChange(() => this.refreshScene());
        folder
            .add(st, 'showReal')
            .name('real box')
            .onChange(() => this.refreshScene());
        folder
            .addColor(st, 'realColor')
            .name('real color')
            .onChange(() => this.refreshScene());
    }
    nodeTitle(node) {
        const warn = node.bbox ? '' : ' ⚠';
        if (node.role === 'component') {
            return `${node.componentKey}: ${node.productKind}-product ${node.productId}${warn}`;
        }
        const short = node.role === 'slotItem' && node.itemId ? ` #${node.itemId.slice(0, 6)}` : '';
        return `${node.productKind}-product: ${node.productId}${short}${warn}`;
    }
    stateFor(node) {
        let st = this.nodeStates.get(node.id);
        if (!st) {
            st = {
                visible: true,
                showSdk: true,
                sdkColor: node.productKind === 'composite' ? this.colors.composite : this.colors.basic,
                showReal: false,
                realColor: this.colors.real,
            };
            this.nodeStates.set(node.id, st);
        }
        return st;
    }
    reconcileStates() {
        const live = new Set();
        const collect = (n) => {
            live.add(n.id);
            n.children.forEach(collect);
        };
        if (this.tree)
            collect(this.tree);
        for (const id of [...this.nodeStates.keys()]) {
            if (!live.has(id))
                this.nodeStates.delete(id);
        }
    }
    clearGroup(group) {
        for (const child of [...group.children]) {
            if (child instanceof Box3Helper)
                child.geometry.dispose();
            group.remove(child);
        }
    }
}
function findContainerWithin(parent, name) {
    var _a;
    const stack = [...parent.children];
    while (stack.length) {
        const o = stack.pop();
        if (!o)
            continue;
        if (o.name === name)
            return o;
        if ((_a = o.userData) === null || _a === void 0 ? void 0 : _a.slotItem)
            continue;
        stack.push(...o.children);
    }
    return undefined;
}

class CompositePlugin extends View3dPlugin {
    constructor(plugins, params = { debug: false }) {
        super();
        this.plugins = plugins;
        this.params = params;
    }
    init(options) {
        this.plugins.forEach((plugin) => plugin.init(options));
    }
    onInit(options) {
        this.plugins.forEach((plugin) => plugin.onInit(options));
    }
    onDestroy() {
        this.plugins.forEach((plugin) => plugin.onDestroy());
    }
    onUpdateLayout() {
        this.plugins.forEach((plugin, idx) => {
            if (this.params.debug) {
                const label = `onUpdateLayout[${idx}]`;
                console.time(label);
                plugin.onUpdateLayout();
                console.timeEnd(label);
            }
            else {
                plugin.onUpdateLayout();
            }
        });
    }
    suspendForCapture() {
        this.plugins.forEach((plugin) => plugin.suspendForCapture());
    }
    resumeAfterCapture() {
        this.plugins.forEach((plugin) => plugin.resumeAfterCapture());
    }
    update(delta) {
        this.plugins.forEach((plugin) => plugin.update(delta));
    }
    render() {
        this.plugins.forEach((plugin) => plugin.render());
    }
    add(plugin) {
        this.plugins.push(plugin);
    }
}

function buildMeshNodes(parent, node) {
    const result = {
        parent: parent,
        name: node.name,
        children: [],
    };
    for (const child of node.children) {
        result.children.push(buildMeshNodes(node, child));
        result.children.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
    }
    return result;
}
class ModelMeshManager {
    onSelectedNodeChanged(callback) {
        this.selectedNodeChanged = callback;
    }
    constructor(canvas, cameraManager, modelLoader, renderer, options) {
        this.canvas = canvas;
        this.cameraManager = cameraManager;
        this.modelLoader = modelLoader;
        this.renderer = renderer;
        this.options = options;
        this.allNodes = [];
        this.model = null;
        this.selectedNode = '';
        this.selectedNodeOriginalMaterials = [];
        this.selectedNodeChanged = () => { };
        this.disposers = [];
        this.initialModelRender = true;
        this._hiddenMeshes = [];
        this.resize = () => {
            this.updateSizes();
            this.cameraManager.resize(this.width, this.height);
            this.renderer.setSize(this.width, this.height);
        };
        this.onTouch = (event) => {
            event.preventDefault();
            const rect = this.canvas.getBoundingClientRect();
            const offsetX = event.targetTouches[0].pageX - rect.left;
            const x = (offsetX / this.renderer.domElement.clientWidth) * 2 - 1;
            const offsetY = event.targetTouches[0].pageY - rect.top;
            const y = -(offsetY / this.renderer.domElement.clientHeight) * 2 + 1;
            this.clickHandler({ x, y });
        };
        this.onClick = (event) => {
            const x = (event.offsetX / this.renderer.domElement.clientWidth) * 2 - 1;
            const y = -(event.offsetY / this.renderer.domElement.clientHeight) * 2 + 1;
            this.clickHandler({ x, y });
        };
        this.clickHandler = ({ x, y }) => {
            if (document === null || document === void 0 ? void 0 : document.activeElement) {
                document.activeElement.blur();
            }
            try {
                this.mouse.x = x;
                this.mouse.y = y;
                this.raycaster.setFromCamera(this.mouse, this.cameraManager.camera);
                const intersects = this.raycaster.intersectObjects(this.sceneNode.children, true);
                for (let i = 0; i < intersects.length; i++) {
                    let obj = intersects[i].object;
                    while (obj && !obj.visible && !(obj instanceof Mesh)) {
                        obj = obj.parent;
                    }
                    if (obj && obj.visible) {
                        this.selectedNodeChanged(obj.name);
                        break;
                    }
                }
            }
            catch (e) {
                console.error(e);
            }
        };
        this.width = canvas.offsetWidth;
        this.height = canvas.offsetHeight;
        this.init();
    }
    setModel(fileName) {
        return __awaiter(this, void 0, void 0, function* () {
            this.restoreSelectedMeshMaterial();
            this.model = yield this.modelLoader.load(fileName);
            this.allNodes = [];
            this.allNodes = [buildMeshNodes(null, this.model)];
            this.rootNode.remove(...this.rootNode.children);
            this.rootNode.add(this.model);
            this.setSelectedNode(this.selectedNode);
            this.hideMeshes(this.hiddenNodes);
            if (this.initialModelRender) {
                const converter = new GlbConverter(this.width, this.height);
                const target = converter.fitCameraToObject3D(this.cameraManager.camera, this.model, 'perspectiveRight');
                this.cameraManager.updateControlsTarget(target);
                this.initialModelRender = false;
            }
        });
    }
    dispose() {
        if (this.renderer instanceof WebGLRenderer) {
            this.renderer.dispose();
            this.renderer = null;
        }
        for (const dispose of this.disposers) {
            dispose();
        }
    }
    getAllNodes() {
        return this.allNodes;
    }
    get hiddenNodes() {
        return this._hiddenMeshes;
    }
    set hiddenNodes(meshes) {
        this._hiddenMeshes = meshes;
        this.hideMeshes(meshes);
    }
    hideMeshes(meshes) {
        var _a;
        (_a = this.model) === null || _a === void 0 ? void 0 : _a.traverse((node) => {
            node.visible = !meshes.includes(node.name);
        });
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            this.initScene();
            this.initLights();
            this.updateSizes();
            this.renderer.setSize(this.width, this.height);
            this.cameraManager.init({
                camera: {
                    minDistance: 0.01,
                    maxDistance: 1000,
                    lookAt: { x: 0, y: 0, z: 0 },
                    position: { x: 1, y: 1, z: 1 },
                    maxPolarAngle: Infinity,
                },
            }, this.canvas);
            this.initHandlers();
            if (this.options.environment) {
                yield this.options.environment.init(this.renderer, this.scene);
            }
            this.startRendering();
        });
    }
    setSelectedNode(node) {
        if (this.model) {
            this.restoreSelectedMeshMaterial();
            this.selectedNode = node;
            this.highlightSelectedMesh();
        }
    }
    initScene() {
        this.scene = new Scene();
        this.sceneNode = new Object3D();
        this.sceneNode.name = 'Scene';
        this.scene.add(this.sceneNode);
        this.rootNode = new Object3D();
        this.rootNode.name = 'Root';
        this.sceneNode.add(this.rootNode);
    }
    initLights() {
        const ambientLight = new AmbientLight('#ffffff', 0.3);
        this.scene.add(ambientLight);
    }
    updateSizes() {
        const parent = this.canvas.parentElement;
        if (parent) {
            this.height = parent.clientHeight;
            this.width = parent.clientWidth;
        }
        else {
            console.error('No parent for canvas found');
        }
    }
    startRendering() {
        requestAnimationFrame(() => {
            this.cameraManager.update();
            if (this.renderer) {
                this.renderer.render(this.scene, this.cameraManager.camera);
                this.startRendering();
            }
        });
    }
    restoreSelectedMeshMaterial() {
        if (this.model && this.selectedNodeOriginalMaterials.length > 0) {
            let found = false;
            this.model.traverse((node) => {
                if (found) {
                    return;
                }
                if (this.selectedNode === node.name) {
                    found = true;
                    node.traverse((child) => {
                        var _a;
                        if (child instanceof Mesh) {
                            const material = this.selectedNodeOriginalMaterials.shift();
                            if (!material) {
                                throw new Error(`Invalid material ${(_a = this.model) === null || _a === void 0 ? void 0 : _a.name}`);
                            }
                            child.material = material;
                        }
                    });
                }
            });
            if (this.selectedNodeOriginalMaterials.length) {
                throw new Error(`Missed some materials`);
            }
        }
    }
    highlightSelectedMesh() {
        if (this.model) {
            let found = false;
            this.model.traverse((node) => {
                var _a;
                if (found) {
                    return;
                }
                if (this.selectedNode === node.name) {
                    found = true;
                    if (this.selectedNodeOriginalMaterials.length) {
                        throw new Error(`You should restore materials before ${(_a = this.model) === null || _a === void 0 ? void 0 : _a.name}`);
                    }
                    node.traverse((child) => {
                        if (child instanceof Mesh) {
                            this.selectedNodeOriginalMaterials.push(child.material);
                            child.material = new MeshStandardMaterial({
                                color: '#7e9f5a',
                            });
                        }
                    });
                }
            });
        }
    }
    initHandlers() {
        this.raycaster = new Raycaster();
        this.mouse = new Vector2();
        this.canvas.addEventListener('touchstart', this.onTouch);
        this.canvas.addEventListener('mousedown', this.onClick);
        this.disposers.push(() => {
            this.canvas.removeEventListener('touchstart', this.onTouch);
            this.canvas.removeEventListener('mousedown', this.onClick);
        });
    }
}

class ModelMeshManagerFactory {
    create(canvas, settings) {
        const options = {};
        if (settings.hdr) {
            options.environment = new HdrEnvironment({ hdr: settings.hdr });
        }
        const cameraManager = new OrbitControlsCameraManager();
        const modelLoader = new ModelLoader();
        const renderer = new ThreeJSViewRenderer(canvas, settings);
        return new ModelMeshManager(canvas, cameraManager, modelLoader, renderer, options);
    }
}

class MeshCsgModifier {
    constructor(sourceMeshName, targetNodeKey, targetMeshName, operation) {
        this.sourceMeshName = sourceMeshName;
        this.targetNodeKey = targetNodeKey;
        this.targetMeshName = targetMeshName;
        this.operation = operation;
        console.warn('[MeshCsgModifier] is on WIP');
    }
    getDependencies() {
        return [this.targetNodeKey];
    }
    apply(obj, context) {
        const sourceMesh = this.findMeshByName(obj, this.sourceMeshName);
        if (!sourceMesh) {
            console.warn(`MeshCsgModifier: Source mesh "${this.sourceMeshName}" not found in current node`);
            return obj;
        }
        const targetNode = context.findNodeByKey(this.targetNodeKey);
        if (!targetNode) {
            console.warn(`MeshCsgModifier: Target node "${this.targetNodeKey}" not found`);
            return obj;
        }
        const targetMesh = this.findMeshByName(targetNode, this.targetMeshName);
        if (!targetMesh) {
            console.warn(`MeshCsgModifier: Target mesh "${this.targetMeshName}" not found in node "${this.targetNodeKey}"`);
            return obj;
        }
        if (!sourceMesh.geometry || !targetMesh.geometry) {
            console.warn('MeshCsgModifier: Both source and target must have valid geometry');
            return obj;
        }
        try {
            const resultGeometry = this.performCsgOperation(sourceMesh, targetMesh, this.operation);
            if (resultGeometry) {
                sourceMesh.geometry.dispose();
                sourceMesh.geometry = resultGeometry;
            }
            else {
                console.warn('MeshCsgModifier: CSG operation returned null geometry');
            }
        }
        catch (error) {
            console.error(`MeshCsgModifier: Failed to perform ${this.operation} operation:`, error);
        }
        return obj;
    }
    findMeshByName(root, name) {
        let foundMesh = null;
        root.traverse((node) => {
            if (node.name === name && node instanceof Mesh) {
                foundMesh = node;
            }
        });
        return foundMesh;
    }
    performCsgOperation(sourceMesh, targetMesh, operation) {
        try {
            let parent = sourceMesh;
            while (parent) {
                parent.updateMatrixWorld(true);
                parent = parent.parent;
            }
            parent = targetMesh;
            while (parent) {
                parent.updateMatrixWorld(true);
                parent = parent.parent;
            }
            const sourceMeshClone = sourceMesh.clone();
            const targetMeshClone = targetMesh.clone();
            sourceMeshClone.geometry = sourceMesh.geometry.clone();
            sourceMeshClone.geometry.applyMatrix4(sourceMesh.matrixWorld);
            targetMeshClone.geometry = targetMesh.geometry.clone();
            targetMeshClone.geometry.applyMatrix4(targetMesh.matrixWorld);
            sourceMeshClone.position.set(0, 0, 0);
            sourceMeshClone.rotation.set(0, 0, 0);
            sourceMeshClone.scale.set(1, 1, 1);
            sourceMeshClone.updateMatrix();
            targetMeshClone.position.set(0, 0, 0);
            targetMeshClone.rotation.set(0, 0, 0);
            targetMeshClone.scale.set(1, 1, 1);
            targetMeshClone.updateMatrix();
            let resultMesh = null;
            switch (operation) {
                case 'union':
                    resultMesh = CSG.union(sourceMeshClone, targetMeshClone);
                    break;
                case 'subtract':
                    resultMesh = CSG.subtract(sourceMeshClone, targetMeshClone);
                    break;
                case 'intersect':
                    resultMesh = CSG.intersect(sourceMeshClone, targetMeshClone);
                    break;
                default:
                    throw new Error(`Unknown CSG operation: ${operation}`);
            }
            if (resultMesh === null || resultMesh === void 0 ? void 0 : resultMesh.geometry) {
                const resultGeometry = resultMesh.geometry.clone();
                const inverseMatrix = sourceMesh.matrixWorld.clone().invert();
                resultGeometry.applyMatrix4(inverseMatrix);
                resultGeometry.computeVertexNormals();
                return resultGeometry;
            }
            return null;
        }
        catch (error) {
            console.error('CSG operation failed:', error);
            return null;
        }
    }
}

class ScaleModifier {
    constructor(scaleFactor) {
        this.scaleFactor = scaleFactor;
    }
    apply(obj, _context) {
        obj.scale.multiplyScalar(this.scaleFactor);
        return obj;
    }
}

class VisibilityModifier {
    constructor(visible) {
        this.visible = visible;
    }
    apply(obj, _context) {
        obj.visible = this.visible;
        return obj;
    }
}

class PositionOffsetModifier {
    constructor(offset) {
        this.offset = offset;
    }
    apply(obj, _context) {
        obj.position.add(new Vector3(this.offset.x, this.offset.y, this.offset.z));
        return obj;
    }
}

class RelativePositionModifier {
    constructor(targetNodeKey, offset) {
        this.targetNodeKey = targetNodeKey;
        this.offset = offset;
    }
    getDependencies() {
        return [this.targetNodeKey];
    }
    apply(obj, context) {
        const targetObj = context.findNodeByKey(this.targetNodeKey);
        if (!targetObj) {
            console.warn(`RelativePositionModifier: Target node "${this.targetNodeKey}" not found`);
            return obj;
        }
        const targetWorldPos = new Vector3();
        targetObj.getWorldPosition(targetWorldPos);
        obj.position.set(targetWorldPos.x + this.offset.x, targetWorldPos.y + this.offset.y, targetWorldPos.z + this.offset.z);
        return obj;
    }
}

class CopyTransformModifier {
    constructor(sourceNodeKey) {
        this.sourceNodeKey = sourceNodeKey;
    }
    getDependencies() {
        return [this.sourceNodeKey];
    }
    apply(obj, context) {
        const sourceObj = context.findNodeByKey(this.sourceNodeKey);
        if (!sourceObj) {
            console.warn(`CopyTransformModifier: Source node "${this.sourceNodeKey}" not found`);
            return obj;
        }
        obj.position.copy(sourceObj.position);
        obj.rotation.copy(sourceObj.rotation);
        obj.scale.copy(sourceObj.scale);
        return obj;
    }
}

class ConditionalModifier {
    constructor(condition, modifier) {
        this.condition = condition;
        this.modifier = modifier;
    }
    getDependencies() {
        return this.modifier.getDependencies ? this.modifier.getDependencies() : [];
    }
    apply(obj, context) {
        if (this.condition(context)) {
            return this.modifier.apply(obj, context);
        }
        return obj;
    }
}

class ChainModifier {
    constructor(modifiers) {
        this.modifiers = modifiers;
    }
    getDependencies() {
        const deps = new Set();
        for (const modifier of this.modifiers) {
            if (modifier.getDependencies) {
                modifier.getDependencies().forEach((dep) => deps.add(dep));
            }
        }
        return Array.from(deps);
    }
    apply(obj, context) {
        return this.modifiers.reduce((currentObj, modifier) => modifier.apply(currentObj, context), obj);
    }
}

export { AssetsManager, AuthSceneEvents, AuthSceneLogic, Ball, BallScene, BoundsDebugPlugin, BuilderPlatformInfo, CameraFocusController, ChainModifier, ComponentNode, CompositePlugin, ConditionalModifier, ConfiguratorRenderer, CopyTransformModifier, DEFAULT_NODE_SETTINGS, DynamicValueService, EnvironmentPreviewService, FitZoomPlugin, GLTFMaterialParser, GlbConverter, GridHelperPlugin, HighlightPlugin, HotspotsPlugin, JointPointsPlugin, LayoutDebugPlugin, MaterialDefaultsCache, MaterialPreviewRenderer, MaterialsManager, MeshCsgModifier, Model3dNode, ModelMeshManagerFactory, PipeRegistry, PlatformBuilder, PositionOffsetModifier, RelativePositionModifier, SCENE_ENVIRONMENT_KEY, ScaleModifier, SceneSpecificationBuilder, SceneSpecificationBuilderBase, ScopePlugin, SelectionPlugin, SlotEditPlugin, StandardViewNodes, TEXTURE_SCHEMA, View3dConfiguration, View3dFactory, View3dPlugin, ViewBuilder, ViewHelperPlugin, ViewNode, ViewNodeBuilder, VisibilityModifier, changeUserData, computeAABB, computeModelBounds, convertTextureValue, deepMerge, deviceDependentConfigurationTypeGuard, emptyPadding, getDeviceType, getUserData, hasUserData, matchObject, materialId, partId, renderComposite, renderSlotsInto, resolveNodeSettings, setUserData, slotGroupName, slotItemGroupName, viewId };
//# sourceMappingURL=index.mjs.map
